import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadset, faAnglesLeft, faAnglesRight, faEnvelope, faTriangleExclamation, faFlask, faGasPump, faFileLines, faFileInvoiceDollar, faMoneyBill, faChartBar, faClock, faBell, faPalette, faShield, faFlag, faPlug, faCode, faDatabase, faCircleCheck, faCircleXmark, faPlay, faBook, faBold, faItalic, faHeading, faListUl, faListOl, faMinus, faQuoteLeft, faGift } from '@fortawesome/free-solid-svg-icons';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Form, Button } from 'react-bootstrap';

import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';

import { ActivityIndicator } from '../components/custom/ActivityIndicator';

// Api
import { 
    fetch_notifications, 
    update_notifications,
    get_alcohol_test_config,
    update_alcohol_test_config,
    get_fuel_surcharge_rates,
    update_fuel_surcharge_rates,
    upload_document,
    list_documents,
    get_document_url,
    delete_document,
    get_document_qr_sheet,
    upload_logo,
    delete_logo,
    send_test_notification,
    get_admin_profiles,
    set_super_admin,
    get_permissions_users,
    update_user_permissions,
    get_features,
    update_feature,
    get_active_kpis,
    update_active_kpi,
    get_company_vars,
    update_company_vars,
    get_depot_locations,
    save_depot_location,
    deactivate_depot_location,
    update_backup_config,
    test_backup_connection,
    run_backup_now,
    get_backup_stats,
    get_backup_runs,
    restore_list_buckets,
    restore_browse_files,
    run_restore,
    get_drivers_handbook,
    update_drivers_handbook
} from '../api/dev';

import {
    getTrainingModules, createTrainingModule, updateTrainingModule, deleteTrainingModule,
    bumpModuleVersion, uploadTrainingVideo, uploadTrainingDocument,
    getModuleQuestions, createQuestion, updateQuestion, deleteQuestion,
    getTrainingRoles, createTrainingRole, deleteTrainingRole, setRoleRequirements,
    getUserTrainingRoles, setUserTrainingRoles,
    getTrainingCompliance, getUserTrainingHistory,
    getTrainingCategories, createTrainingCategory, deleteTrainingCategory,
    getAssignableUsers,
} from '../api/training';

import { fetch_all_profiles, validatePassword } from '../api/profiles';
import RequestPassword from './custom/RequestPassword';
import InvoiceSettingsPanel from './InvoiceSettingsPanel';
import { upload_payslip, list_payslips, list_payslip_periods, get_payslip_url, delete_payslip } from '../api/payslips';
import { bulkRegenerateQRCodes } from '../api/enhancedWarehouse';
import { getSessionUUID } from './lib/AuthToken';
import { ProgressTracker } from './custom/ProgressTracker';
import IntegrationsManager from './Integrations/IntegrationsManager';
import DriverBonusPanel from './DriverBonusPanel';

// import css
import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';


const DevPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { isDev, isManager, features, companyLogo, setCompanyLogo, profile, permissions } = useAuthProvider();

    // loading state
    const [isLoading, setIsLoading] = useState(false);    

    // State to manage active tab (defaults dynamically to an authorized tab)
    const getDefaultTab = () => {
        if (isDev || (permissions && permissions.perm_system_settings)) return 'notifications';
        if (isManager) return 'driver-bonus';
        if (permissions && permissions.perm_defects_edit) return 'defect-management';
        if (permissions && permissions.perm_alcohol_edit) return 'alcohol-testing';
        if (permissions && permissions.perm_timesheets_edit) return 'payslips';
        if (permissions && permissions.perm_invoice_edit) return 'invoice-settings';
        return 'notifications';
    };

    const [activeTab, setActiveTab] = useState(getDefaultTab());
    const [showLeftNav, setShowLeftNav] = useState(false);
    const normalizeVehicleType = (value) => String(value ?? '').trim().toUpperCase();
    
    // State to manage notification type selection
    const [selectedNotificationType, setSelectedNotificationType] = useState('route_notifications');

    // Email notification states
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    
    // Separate states for each notification type
    const [routeNotificationUsers, setRouteNotificationUsers] = useState([]);
    const [routeNotificationEmails, setRouteNotificationEmails] = useState([]);
    const [serviceNotificationUsers, setServiceNotificationUsers] = useState([]);
    const [serviceNotificationEmails, setServiceNotificationEmails] = useState([]);
    
    // Developer Tools states
    const [selectedQRTable, setSelectedQRTable] = useState('pallets');
    const [qrRegenerationStatus, setQRRegenerationStatus] = useState(null);
    const [qrProgress, setQRProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [isQRProcessing, setIsQRProcessing] = useState(false);
    const [warehouseNotificationUsers, setWarehouseNotificationUsers] = useState([]);
    const [warehouseNotificationEmails, setWarehouseNotificationEmails] = useState([]);
    const [timesheetNotificationUsers, setTimesheetNotificationUsers] = useState([]);
    const [timesheetNotificationEmails, setTimesheetNotificationEmails] = useState([]);
    const [rentalNotificationUsers, setRentalNotificationUsers] = useState([]);
    const [rentalNotificationEmails, setRentalNotificationEmails] = useState([]);
    const [backupNotificationUsers, setBackupNotificationUsers] = useState([]);
    const [backupNotificationEmails, setBackupNotificationEmails] = useState([]);
    
    // All notifications data from API
    const [allNotificationsData, setAllNotificationsData] = useState({});

    // Defect Management State
    const [defectTypes, setDefectTypes] = useState([]);
    const [defectTypesVehicles, setDefectTypesVehicles] = useState([]);
    const [selectedVehicleType, setSelectedVehicleType] = useState('');
    const [newDefectType, setNewDefectType] = useState('');
    const [newDefectDescription, setNewDefectDescription] = useState('');
    const [editingDefectType, setEditingDefectType] = useState(null);
    const [showAddDefectForm, setShowAddDefectForm] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingAssignments, setPendingAssignments] = useState([]);
    const [pendingRemovals, setPendingRemovals] = useState([]);
    const [pendingReorders, setPendingReorders] = useState([]);

    // Alcohol Testing Configuration State
    const [alcoholTestConfig, setAlcoholTestConfig] = useState({ pass_percentage: 70, enabled: true });
    const [newPassPercentage, setNewPassPercentage] = useState(70);
    const [alcoholTestEnabled, setAlcoholTestEnabled] = useState(true);
    const [showAlcoholConfig, setShowAlcoholConfig] = useState(false);

    // Fuel Surcharge Configuration State
    const [fuelSurchargeRates, setFuelSurchargeRates] = useState({ low_percentage: 4.6, medium_percentage: 6.0, high_percentage: 8.0 });
    const [newFuelRates, setNewFuelRates] = useState({ low: 4.6, medium: 6.0, high: 8.0 });
    const [showFuelConfig, setShowFuelConfig] = useState(false);

    // Test Push Notifications state
    const [testNotifProfileId, setTestNotifProfileId] = useState('');
    const [testNotifTitle, setTestNotifTitle] = useState('Test Notification');
    const [testNotifBody, setTestNotifBody] = useState('');
    const [isSendingNotif, setIsSendingNotif] = useState(false);
    const [testNotifResults, setTestNotifResults] = useState(null);

    // Privilege Management state
    const [adminProfiles, setAdminProfiles] = useState([]);
    const [isPrivilegesLoading, setIsPrivilegesLoading] = useState(false);

    // Granular Permissions states (is_dev only)
    const [permissionsUsers, setPermissionsUsers] = useState([]);
    const [originalPermissionsUsers, setOriginalPermissionsUsers] = useState([]);
    const [isPermissionsSaving, setIsPermissionsSaving] = useState(false);
    const [expandedPermissionUser, setExpandedPermissionUser] = useState(null);

    // Feature Flags state (local editable copy for the toggle UI)
    const [featureToggles, setFeatureToggles] = useState({});
    const [isFeaturesLoading, setIsFeaturesLoading] = useState(false);

    // Active KPI Visibility state
    const [activeKpisSettings, setActiveKpisSettings] = useState({});
    const [targetEdits, setTargetEdits] = useState({});
    const [isActiveKpisLoading, setIsActiveKpisLoading] = useState(false);

    // Company Variables state
    const [companyVars, setCompanyVars] = useState({ contract_weekly_hours: 48 });
    const [companyVarsInput, setCompanyVarsInput] = useState(48);
    const [isCompanyVarsLoading, setIsCompanyVarsLoading] = useState(false);
    const [depotLocations, setDepotLocations] = useState([]);
    const [depotLocationForm, setDepotLocationForm] = useState({
        id: null,
        name: '',
        lat: '',
        lng: '',
        radius: 150,
        active: true
    });
    const [isDepotLocationsLoading, setIsDepotLocationsLoading] = useState(false);

    // Backup config state
    const [backupHost, setBackupHost] = useState('');
    const [backupPort, setBackupPort] = useState(8765);
    const [backupEnabled, setBackupEnabled] = useState(false);
    const [backupApiKey, setBackupApiKey] = useState('');
    const [backupApiKeySet, setBackupApiKeySet] = useState(false);
    const [backupLastIncremental, setBackupLastIncremental] = useState(null);
    const [backupLastFull, setBackupLastFull] = useState(null);
    const [isBackupSaving, setIsBackupSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [isRunningBackup, setIsRunningBackup] = useState(false);
    const [backupRunResult, setBackupRunResult] = useState(null);
    const [backupStats, setBackupStats] = useState(null);
    const [backupRuns, setBackupRuns] = useState(null);
    const [backupRunsLoading, setBackupRunsLoading] = useState(false);
    const [expandedRunId, setExpandedRunId] = useState(null);

    // Restore state
    const [restorePassword, setRestorePassword] = useState('');
    const [restoreBuckets, setRestoreBuckets] = useState([]);         // available buckets from server
    const [restoreSelectedBucket, setRestoreSelectedBucket] = useState(null); // null = all
    const [restoreFiles, setRestoreFiles] = useState(null);           // file list for browsing
    const [restoreOverwrite, setRestoreOverwrite] = useState(false);
    const [isRestoreLoading, setIsRestoreLoading] = useState(false);
    const [restoreResult, setRestoreResult] = useState(null);
    const [restoreConfirmPending, setRestoreConfirmPending] = useState(false);

    // Drivers Handbook state
    const [handbookContent, setHandbookContent] = useState('');
    const [handbookDraft, setHandbookDraft] = useState('');
    const [handbookUpdatedAt, setHandbookUpdatedAt] = useState(null);
    const [isHandbookLoading, setIsHandbookLoading] = useState(false);
    const [isHandbookSaving, setIsHandbookSaving] = useState(false);
    const [handbookHasChanges, setHandbookHasChanges] = useState(false);

    // Active KPI definitions
    const ACTIVE_KPI_FLAGS = [
        { key: 'operational_risk',     label: 'Operational Risk Index',     description: 'Outstanding issues composite risk score' },
        { key: 'subcontracted_profit', label: 'Subcontracted Profit Margin', description: 'Month-to-date profit margin on subcontracted routes' },
        { key: 'weekly_revenue',       label: 'Weekly Revenue Trend',        description: 'Week-on-week revenue percentage change' },
        { key: 'collections_kpi',      label: 'Palletforce Collections',     description: 'Daily and weekly collection volume tracking' },
        { key: 'defect_resolution',    label: 'Defect Resolution Rate',      description: '28-day rolling defect completion rate' },
        { key: 'tacho_compliance',     label: 'Tacho Compliance',            description: 'Unsigned tachograph infringement count' },
        { key: 'driver_testing',       label: 'Driver Testing Compliance',   description: '28-day alcohol testing coverage rate' },
        { key: 'service_compliance',   label: 'Service Compliance',          description: 'Vehicle service schedule adherence' },
        { key: 'diesel_usage',         label: 'Diesel Usage',                description: 'Monthly diesel fuel consumption tracking' },
        { key: 'adblue_usage',         label: 'AdBlue Usage',                description: 'Monthly AdBlue consumption tracking' },
        { key: 'average_mpg',          label: 'Average MPG',                 description: 'Fleet average miles per gallon' },
        { key: 'adblue_diesel_ratio',  label: 'AdBlue/Diesel Ratio',         description: 'AdBlue to diesel consumption percentage' },
        { key: 'training_compliance',  label: 'Training Compliance',         description: 'Overall employee training completion rate' },
    ];

    const KPI_TARGET_FIELDS = [
        { key: 'target_operational_risk',          label: 'Operational Risk Index Target',     type: 'number', step: '1',    placeholder: 10,  suffix: '' },
        { key: 'target_subcontracted_profit',      label: 'Subcontracted Profit Target',       type: 'number', step: '0.1',  placeholder: 30,  suffix: '%' },
        { key: 'target_weekly_revenue',            label: 'Weekly Revenue WoW Target',         type: 'number', step: '0.1',  placeholder: 5,   suffix: '%' },
        { key: 'target_weekly_revenue_amount',     label: 'Weekly Revenue Plot Target',        type: 'number', step: '1000', placeholder: 50000, suffix: '£' },
        { key: 'target_collections',               label: 'Palletforce Weekly Collections',    type: 'number', step: '5',    placeholder: 350, suffix: ' pallets' },
        { key: 'target_defect_resolution',         label: 'Defect Resolution Target',          type: 'number', step: '0.1',  placeholder: 80,  suffix: '%' },
        { key: 'target_outstanding_defects',       label: 'Outstanding Defects Target',        type: 'number', step: '1',    placeholder: 2,   suffix: ' defects' },
        { key: 'target_tacho_compliance',          label: 'Tacho Compliance Unsigned Target',  type: 'number', step: '1',    placeholder: 0,   suffix: ' unsigned' },
        { key: 'target_driver_testing',            label: 'Driver Testing Compliance Target',  type: 'number', step: '0.1',  placeholder: 50,  suffix: '%' },
        { key: 'target_service_compliance',        label: 'Service Compliance Target',         type: 'number', step: '0.1',  placeholder: 100, suffix: '%' },
        { key: 'target_diesel_usage',              label: 'Diesel Usage Target (L/km)',        type: 'number', step: '0.01', placeholder: 0.40, suffix: ' L/km' },
        { key: 'target_adblue_diesel_ratio_min',   label: 'AdBlue/Diesel Ratio Min Target',    type: 'number', step: '0.1',  placeholder: 3,   suffix: '%' },
        { key: 'target_adblue_diesel_ratio_max',   label: 'AdBlue/Diesel Ratio Max Target',    type: 'number', step: '0.1',  placeholder: 5,   suffix: '%' },
        { key: 'target_training_compliance',       label: 'Training Compliance Target',        type: 'number', step: '1',    placeholder: 80,  suffix: '%' },
    ];

    // Feature definitions (key = DB column, label + description for display)
    const FEATURE_FLAGS = [
        { key: 'kpis',            label: 'KPI Dashboard',    description: 'KPI reporting and performance analytics' },
        { key: 'warehouse',       label: 'Warehouse',         description: 'Warehouse management and stock control' },
        { key: 'alcohol_testing', label: 'Alcohol Testing',   description: 'Alcohol compliance testing module' },
        { key: 'tacho',           label: 'Tachograph',        description: 'Tacho data analysis and infringement tracking' },
        { key: 'timesheets',      label: 'Timesheets',        description: 'Driver and staff timesheet management' },
        { key: 'holidays',        label: 'Holidays',          description: 'Holiday request and approval system' },
        { key: 'collections',     label: 'Collections',       description: 'Parcel collection management' },
        { key: 'purchase_orders', label: 'Purchase Orders',   description: 'Purchase order creation and tracking' },
        { key: 'ai',              label: 'AI Assistant',      description: 'AI chatbot and intelligent query features' },
        { key: 'fire_register',   label: 'Fire Register',      description: 'Site visitor sign-in and fire register management' },
        { key: 'messaging',        label: 'Messaging',           description: 'Internal messaging between staff' },
        { key: 'mobile',           label: 'Mobile App',          description: 'Mobile app access for drivers (checks, defects, clock in/out)' },
        { key: 'uptime_robot',     label: 'UptimeRobot',          description: 'UptimeRobot monitoring dashboard (requires UPTIMEROBOT_API_KEY)' },
        { key: 'service',          label: 'Service Schedule',     description: 'Vehicle service schedule management' },
        { key: 'routing',          label: 'Route Schedule',        description: 'Route planning and delivery schedule' },
        { key: 'invoicing',        label: 'Invoicing',             description: 'Invoice management and automated draft generation' },
        { key: 'palletforce',      label: 'Palletforce',           description: 'Palletforce pallet network integration' },
        { key: 'pallettrack',      label: 'PalletTrack',           description: 'PalletTrack pallet network integration' },
        { key: 'velocity',         label: 'Velocity',              description: 'Velocity vehicle tracking integration' },
        { key: 'fleetclear',       label: 'Fleetclear',            description: 'Fleetclear vehicle tracking integration' },
        { key: 'fleetview',        label: 'Fleet View',             description: 'Matrix Telematics vehicle tracking integration' },
        { key: 'training',         label: 'Training',              description: 'Employee training modules and compliance tracking' },
    ];

    // Payslips state
    const [payslips, setPayslips] = useState([]);
    const [payslipDriverId, setPayslipDriverId] = useState('');
    const [payslipUploadDriverSearch, setPayslipUploadDriverSearch] = useState('');
    const [payslipUploadDriverDropdownOpen, setPayslipUploadDriverDropdownOpen] = useState(false);
    const [payslipPasswordOpen, setPayslipPasswordOpen] = useState(false);
    const [payslipPendingAction, setPayslipPendingAction] = useState(null);
    const [payslipDisplayName, setPayslipDisplayName] = useState('');
    const [payslipPayDate, setPayslipPayDate] = useState('');
    const [payslipFile, setPayslipFile] = useState(null);
    const [isPayslipUploading, setIsPayslipUploading] = useState(false);
    const [isPayslipsLoading, setIsPayslipsLoading] = useState(false);
    const [isPayslipPeriodsLoading, setIsPayslipPeriodsLoading] = useState(false);
    const [payslipFilterDriver, setPayslipFilterDriver] = useState('');
    const [payslipDriverSearch, setPayslipDriverSearch] = useState('');
    const [payslipDriverDropdownOpen, setPayslipDriverDropdownOpen] = useState(false);
    // Folder navigation: years → months → files
    const [payslipNavLevel, setPayslipNavLevel] = useState('years'); // 'years' | 'months' | 'files'
    const [payslipNavYear, setPayslipNavYear] = useState(null);
    const [payslipNavMonth, setPayslipNavMonth] = useState(null);
    const [payslipYears, setPayslipYears] = useState([]);
    const [payslipMonths, setPayslipMonths] = useState([]);

    // SOPs & RAMS state
    const [documents, setDocuments] = useState([]);
    const [docFilterType, setDocFilterType] = useState('all');
    const [docUploadFile, setDocUploadFile] = useState(null);
    const [docUploadType, setDocUploadType] = useState('rams');
    const [docUploadName, setDocUploadName] = useState('');
    const [isDocUploading, setIsDocUploading] = useState(false);
    const [isDocLoading, setIsDocLoading] = useState(false);

    // Company logo state
    const [logoFile, setLogoFile] = useState(null);
    const [isLogoUploading, setIsLogoUploading] = useState(false);

    // Notification types configuration
    const notificationTypes = [
        {
            key: 'route_notifications',
            label: 'Route Notifications',
            description: 'Users who receive route-related email notifications',
            feature: 'routing'
        },
        {
            key: 'service_notifications',
            label: 'Service Notifications', 
            description: 'Users who receive service-related email notifications',
            feature: 'service'
        },
        {
            key: 'warehouse_reports',
            label: 'Warehouse Reports',
            description: 'Users who receive weekly warehouse report emails',
            feature: 'warehouse'
        },
        {
            key: 'timesheet_reports',
            label: 'Timesheet Reports',
            description: 'Users who receive weekly timesheet report emails',
            feature: 'timesheets'
        },
        {
            key: 'rental_notifications',
            label: 'Vehicle Rental Reports',
            description: 'Users who receive monthly vehicle rental report emails'
        },
        {
            key: 'backup_notifications',
            label: 'Backup Reports',
            description: 'Users who receive backup success/failure email notifications'
        }
        // Add more notification types here as needed
        // {
        //     key: 'defect_notifications',
        //     label: 'Defect Notifications',
        //     description: 'Users who receive defect-related email notifications'
        // }
    ].filter(type => !type.feature || features[type.feature] !== false);


    // Fetch all users ===========================================================================
    const fetchAllUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data, status } = await fetch_all_profiles();

            if (status === 200 || status === 201) {
                const users = data || [];
                
                // Split users into groups
                const adminUsers = users.filter(user => user.group === 'ADMIN');
                const driverUsers = users.filter(user => user.group === 'DRIVER');
                const serviceUsers = users.filter(user => user.group === 'SERVICE');
                
                // Sort each group by name
                const sortByName = (a, b) => {
                    const nameA = `${a.name} ${a.surname}`.toLowerCase();
                    const nameB = `${b.name} ${b.surname}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                };
                
                adminUsers.sort(sortByName);
                driverUsers.sort(sortByName);
                serviceUsers.sort(sortByName);
                
                // Combine in order: ADMIN, DRIVERS, SERVICE
                const sortedUsers = [...adminUsers, ...driverUsers, ...serviceUsers];
                
                setAllUsers(sortedUsers);

            } else {
                showAlert({
                    message: 'Failed to fetch users',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'Error fetching users',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        } finally {
            setIsLoading(false);
        }
    }, [showAlert]);


    // Fetch all notifications data ================================================================
    const fetchNotifications = useCallback(async () => {
        setIsLoading(true);
        try {

            // Fetch notifications from the API
            const { data, status } = await fetch_notifications();

            if (status === 200) {
                // Store all notifications data
                setAllNotificationsData(data);
                
                // Route notifications
                const routeEmails = data?.route_notifications || [];
                const routeUsers = allUsers.filter(user => 
                    routeEmails.includes(user.email)
                );
                setRouteNotificationUsers(routeUsers);
                setRouteNotificationEmails(routeEmails);
                
                // Service notifications
                const serviceEmails = data?.service_notifications || [];
                const serviceUsers = allUsers.filter(user => 
                    serviceEmails.includes(user.email)
                );
                setServiceNotificationUsers(serviceUsers);
                setServiceNotificationEmails(serviceEmails);
                
                // Warehouse notifications
                const warehouseEmails = data?.warehouse_reports || [];
                const warehouseUsers = allUsers.filter(user => 
                    warehouseEmails.includes(user.email)
                );
                setWarehouseNotificationUsers(warehouseUsers);
                setWarehouseNotificationEmails(warehouseEmails);
                
                // Timesheet notifications
                const timesheetEmails = data?.timesheet_reports || [];
                const timesheetUsers = allUsers.filter(user => 
                    timesheetEmails.includes(user.email)
                );
                setTimesheetNotificationUsers(timesheetUsers);
                setTimesheetNotificationEmails(timesheetEmails);
                
                // Rental notifications
                const rentalEmails = data?.rental_notifications || [];
                const rentalUsers = allUsers.filter(user => 
                    rentalEmails.includes(user.email)
                );
                setRentalNotificationUsers(rentalUsers);
                setRentalNotificationEmails(rentalEmails);

                // Backup notifications
                const backupEmails = data?.backup_notifications || [];
                const backupUsers = allUsers.filter(user =>
                    backupEmails.includes(user.email)
                );
                setBackupNotificationUsers(backupUsers);
                setBackupNotificationEmails(backupEmails);
                
            } else {
                showAlert({
                    message: 'Failed to fetch notifications data',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'Error fetching notifications data',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        } finally {
            setIsLoading(false);
        }
    }, [allUsers, showAlert]);

    // Helper functions to get current notification data based on selected type
    const getCurrentNotificationUsers = () => {
        switch (selectedNotificationType) {
            case 'route_notifications':
                return routeNotificationUsers;
            case 'service_notifications':
                return serviceNotificationUsers;
            case 'warehouse_reports':
                return warehouseNotificationUsers;
            case 'timesheet_reports':
                return timesheetNotificationUsers;
            case 'rental_notifications':
                return rentalNotificationUsers;
            case 'backup_notifications':
                return backupNotificationUsers;
            default:
                return [];
        }
    };

    const getCurrentNotificationEmails = () => {
        switch (selectedNotificationType) {
            case 'route_notifications':
                return routeNotificationEmails;
            case 'service_notifications':
                return serviceNotificationEmails;
            case 'warehouse_reports':
                return warehouseNotificationEmails;
            case 'timesheet_reports':
                return timesheetNotificationEmails;
            case 'rental_notifications':
                return rentalNotificationEmails;
            case 'backup_notifications':
                return backupNotificationEmails;
            default:
                return [];
        }
    };

    // Add user to notifications
    const handleAddUser = async () => {
        if (!selectedUser || !selectedUser.email) {
            showAlert({
                message: 'Please select a user to add',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'warning' }],
            });
            return;
        }

        const currentEmails = getCurrentNotificationEmails();
        
        setIsLoading(true);
        try {

            if (currentEmails.includes(selectedUser.email)) {
                const notificationType = notificationTypes.find(type => type.key === selectedNotificationType);
                showAlert({
                    message: `User already exists in ${notificationType?.label || selectedNotificationType}`,
                    type: 'warning',
                    buttons: [{ label: 'Close', type: 'warning' }],
                });
                setIsLoading(false);
                return;
            }

            // Add the new user email to the list
            const updatedEmails = [...currentEmails, selectedUser.email];

            const { status, message } = await update_notifications({
                column: selectedNotificationType,
                data: updatedEmails
            });

            if (status === 200 || status === 201) {
                showAlert({
                    message: message || 'User added successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'success' }],
                });
                setSelectedUser('');
                // Refresh all notifications data
                fetchNotifications();
            } else {
                showAlert({
                    message: message || 'Failed to add user',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'Error adding user',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Remove user from notifications
    const handleRemoveUser = async (userEmail) => {
        const currentEmails = getCurrentNotificationEmails();
        
        setIsLoading(true);
        try {
            // Remove the user email from the current list
            const updatedEmails = currentEmails.filter(email => email !== userEmail);
            
            const { status, message } = await update_notifications({
                column: selectedNotificationType,
                data: updatedEmails
            });

            if (status === 200) {
                showAlert({
                    message: message || 'User removed successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'success' }],
                });
                // Refresh all notifications data
                fetchNotifications();
            } else {
                showAlert({
                    message: message || 'Failed to remove user',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'danger' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'Error removing user',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Bulk QR regeneration function with progress tracking
    const handleBulkQRRegeneration = async () => {
        if (!selectedQRTable) {
            showAlert({
                message: 'Please select a table type.',
                type: 'error'
            });
            return;
        }

        // Show confirmation dialog before proceeding
        showAlert({
            message: `⚠️ WARNING: This will regenerate ALL QR codes for ${selectedQRTable}. This action cannot be undone`,
            type: 'warning',
            buttons: [
                {
                    label: 'Cancel',
                    type: 'secondary'
                },
                {
                    label: 'Yes',
                    type: 'danger',
                    onClick: () => proceedWithBulkRegeneration()
                }
            ]
        });
    };



    // Actual bulk regeneration function (called after confirmation)
    const proceedWithBulkRegeneration = async () => {
        setIsLoading(true);
        setIsQRProcessing(true);
        setQRProgress({ current: 0, total: 0, percentage: 0 });
        setQRRegenerationStatus({ 
            message: `Starting bulk QR regeneration for ${selectedQRTable}...`,
            type: 'info'
        });
        
        try {
            const result = await bulkRegenerateQRCodes(selectedQRTable, (progress) => {
                // Real-time progress callback
                setQRProgress({
                    current: progress.processed,
                    total: progress.total,
                    percentage: progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0
                });
                
                setQRRegenerationStatus({
                    message: `Processing ${selectedQRTable}: ${progress.processed}/${progress.total} items (${Math.round((progress.processed / progress.total) * 100)}%)`,
                    type: 'info'
                });
            });
            
            if (result.success) {
                setQRProgress({
                    current: result.processed,
                    total: result.total,
                    percentage: 100
                });
                
                setQRRegenerationStatus({
                    message: `✅ Complete! Processed: ${result.processed}/${result.total}, Success: ${result.success_count}, Errors: ${result.error_count}`,
                    type: result.error_count > 0 ? 'warning' : 'success'
                });
                
                // Show detailed error information if there were errors
                if (result.error_count > 0 && result.errors && result.errors.length > 0) {
                    const errorDetails = result.errors.slice(0, 3).join('\n• '); // Show first 3 errors with bullets
                    showAlert({
                        message: `${result.message}\n\n📊 Results:\n• Success: ${result.success_count}\n• Errors: ${result.error_count}\n\n❌ Sample errors:\n• ${errorDetails}${result.errors.length > 3 ? `\n...and ${result.errors.length - 3} more errors` : ''}`,
                        type: 'warning'
                    });
                } else {
                    showAlert({
                        message: `${result.message} - ${result.success_count} successful, ${result.error_count} errors`,
                        type: result.error_count > 0 ? 'warning' : 'success'
                    });
                }
            } else {
                setQRRegenerationStatus({
                    message: `❌ Error: ${result.error}`,
                    type: 'error'
                });
                
                showAlert({
                    message: result.error,
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Error in bulk QR regeneration:', error);
            setQRRegenerationStatus({
                message: `❌ Error: ${error.message}`,
                type: 'error'
            });
            
            showAlert({
                message: 'Failed to regenerate QR codes',
                type: 'error'
            });
        } finally {
            setIsLoading(false);
            setIsQRProcessing(false);
        }
    };

    // Fetch all users when the component mounts
    useEffect(() => {
        fetchAllUsers();
    }, []);

    // Fetch notifications after users are loaded
    useEffect(() => {
        if (activeTab === 'notifications' && allUsers.length > 0) {
            fetchNotifications();
        }
    }, [allUsers, activeTab, fetchNotifications]); // Removed selectedNotificationType since we fetch all data

    // Fetch defect data when defect management tab is active
    useEffect(() => {
        if (activeTab === 'defect-management') {
            fetchDefectTypes();
        }
    }, [activeTab]);

    // Fetch alcohol test config when alcohol testing tab is active
    useEffect(() => {
        if (activeTab === 'alcohol-testing') {
            fetchAlcoholTestConfig();
        }
        
        if (activeTab === 'fuel-surcharge') {
            fetchFuelSurchargeRates();
        }

        if (activeTab === 'sops-rams') {
            fetchDocuments();
        }

        if (activeTab === 'privilege-management') {
            fetchAdminProfiles();
            if (isDev) {
                fetchPermissionsUsers();
            }
        }

        if (activeTab === 'feature-flags') {
            fetchFeatures();
        }

        if (activeTab === 'kpi-settings') {
            fetchActiveKpisSettings();
        }

        if (activeTab === 'backup-config' || activeTab === 'company-vars') {
            fetchCompanyVars();
        }

        if (activeTab === 'company-vars') {
            fetchDepotLocations();
        }

        if (activeTab === 'payslips') {
            fetchPayslipYears();
        }

        if (activeTab === 'drivers-handbook') {
            fetchHandbook();
        }
    }, [activeTab]);

    // Fetch defect-vehicle assignments when vehicle type is selected
    useEffect(() => {
        if (selectedVehicleType) {
            fetchDefectTypesVehicles(selectedVehicleType);
            // Reset unsaved changes when switching vehicle types
            setHasUnsavedChanges(false);
            setPendingAssignments([]);
            setPendingRemovals([]);
            setPendingReorders([]);
        }
    }, [selectedVehicleType]);

    // Fetch defect types function
    const fetchDefectTypes = async () => {
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/defects/types', {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setDefectTypes(data.defect_types || []);
            }
        } catch (error) {
            console.error('Error fetching defect types:', error);
        }
    };

    // Fetch defect-vehicle assignments function
    const fetchDefectTypesVehicles = async (vehicleType) => {
        const normalizedType = normalizeVehicleType(vehicleType);
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch(`/defects/vehicle-types/${normalizedType}`, {
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setDefectTypesVehicles(data.defect_types_vehicles || []);
            }
        } catch (error) {
            console.error('Error fetching defect types vehicles:', error);
        }
    };

    // Assign defect to vehicle type
    const assignDefectToVehicle = async (defectTypeId, vehicleType) => {
        const normalizedType = normalizeVehicleType(vehicleType);
        try {
            const sessionToken = await getSessionUUID();
            const response = await fetch('/defects/assign-to-vehicle', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    defect_type_id: defectTypeId,
                    vehicle_type: normalizedType,
                    position: defectTypesVehicles.length + 1
                }),
            });
            
            if (response.ok) {
                fetchDefectTypesVehicles(vehicleType); // Refresh the list
            } else {
                console.error('Failed to assign defect to vehicle');
            }
        } catch (error) {
            console.error('Error assigning defect to vehicle:', error);
        }
    };

    // Remove defect from vehicle type
    const removeDefectFromVehicle = async (assignmentId, vehicleType) => {
        try {
            const response = await fetch(`/api/defects/remove-from-vehicle/${assignmentId}`, {
                method: 'DELETE',
            });
            
            if (response.ok) {
                fetchDefectTypesVehicles(vehicleType); // Refresh the list
            } else {
                console.error('Failed to remove defect from vehicle');
            }
        } catch (error) {
            console.error('Error removing defect from vehicle:', error);
        }
    };

    // Add defect to pending assignments (don't save immediately)
    const addDefectToPending = (defectTypeId, vehicleType) => {
        const defectType = defectTypes.find(dt => dt.id === defectTypeId);
        if (defectType) {
            const newAssignment = {
                id: 'temp_' + Date.now(), // Temporary ID
                defect_type_id: defectTypeId,
                vehicle_type: vehicleType,
                position: defectTypesVehicles.length + pendingAssignments.length + 1,
                defect_types: defectType,
                isPending: true
            };
            
            setPendingAssignments(prev => [...prev, newAssignment]);
            setHasUnsavedChanges(true);
        }
    };

    // Remove defect to pending removals (don't save immediately)
    const removeDefectToPending = (assignmentId) => {
        // Check if it's a pending assignment
        const isPendingAssignment = pendingAssignments.find(pa => pa.id === assignmentId);
        if (isPendingAssignment) {
            // Just remove from pending assignments
            setPendingAssignments(prev => prev.filter(pa => pa.id !== assignmentId));
        } else {
            // Add to pending removals
            setPendingRemovals(prev => [...prev, assignmentId]);
        }
        setHasUnsavedChanges(true);
    };

    // Save all changes
    const saveAllChanges = async () => {
        try {
            const sessionToken = await getSessionUUID();
            const errors = [];
            
            // Process removals first
            for (const assignmentId of pendingRemovals) {
                await fetch(`/defects/remove-from-vehicle/${assignmentId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
            }
            
            // Process new assignments
            for (const assignment of pendingAssignments) {
                const response = await fetch('/defects/assign-to-vehicle', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        defect_type_id: assignment.defect_type_id,
                        vehicle_type: assignment.vehicle_type,
                        position: assignment.position
                    }),
                });
                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    errors.push(body.error || `Failed to assign ${assignment.defect_types?.defect || 'defect'}`);
                }
            }
            
            // Process reorder changes (update positions)
            for (const reorder of pendingReorders) {
                await fetch(`/defects/update-position/${reorder.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`
                    },
                    body: JSON.stringify({
                        position: reorder.position
                    }),
                });
            }
            
            // Reset pending changes
            setPendingAssignments([]);
            setPendingRemovals([]);
            setPendingReorders([]);
            setHasUnsavedChanges(false);
            
            // Refresh the list
            fetchDefectTypesVehicles(selectedVehicleType);

            if (errors.length > 0) {
                showAlert({ message: errors.join(' | '), type: 'danger', buttons: [{ label: 'Close' }] });
            }
            
        } catch (error) {
            console.error('Error saving changes:', error);
            showAlert({ message: 'Error saving defect assignment changes', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    // Cancel all changes
    const cancelAllChanges = () => {
        setPendingAssignments([]);
        setPendingRemovals([]);
        setPendingReorders([]);
        setHasUnsavedChanges(false);
    };

    // Get combined assignments for display (existing + pending - removals)
    const getCombinedAssignments = () => {
        const existingAssignments = defectTypesVehicles.filter(dtv => !pendingRemovals.includes(dtv.id));
        return [...existingAssignments, ...pendingAssignments];
    };

    // Reorder assignments when drag and drop occurs
    const reorderAssignments = (fromIndex, toIndex) => {
        const combined = getCombinedAssignments();
        const reorderedItems = [...combined];
        const [movedItem] = reorderedItems.splice(fromIndex, 1);
        reorderedItems.splice(toIndex, 0, movedItem);
        
        // Update positions for all items
        const updatedItems = reorderedItems.map((item, index) => ({
            ...item,
            position: index + 1
        }));
        
        // Separate existing and pending assignments
        const updatedExisting = updatedItems.filter(item => !item.isPending && !pendingRemovals.includes(item.id));
        const updatedPending = updatedItems.filter(item => item.isPending);
        
        // Update the states
        setDefectTypesVehicles(updatedExisting);
        setPendingAssignments(updatedPending);
        
        // Track reordering changes
        setPendingReorders(prev => {
            const newReorders = [...prev];
            updatedExisting.forEach(item => {
                const existing = newReorders.find(r => r.id === item.id);
                if (existing) {
                    existing.position = item.position;
                } else {
                    newReorders.push({ id: item.id, position: item.position });
                }
            });
            return newReorders;
        });
        
        setHasUnsavedChanges(true);
    };

    // Alcohol Testing Configuration Functions
    const fetchAlcoholTestConfig = async () => {
        try {
            const response = await get_alcohol_test_config();
            if (response.status === 200 && response.data) {
                setAlcoholTestConfig(response.data);
                setNewPassPercentage(response.data.pass_percentage);
                setAlcoholTestEnabled(response.data.enabled);
            }
        } catch (error) {
            console.error('Error fetching alcohol test config:', error);
        }
    };

    const updateAlcoholTestConfiguration = async () => {
        setIsLoading(true);
        try {
            const response = await update_alcohol_test_config(newPassPercentage, alcoholTestEnabled);
            
            if (response.status === 200) {
                showAlert({
                    message: 'Alcohol test configuration updated successfully',
                    type: 'success',
                    buttons: [{ label: 'Close' }]
                });
                setAlcoholTestConfig({ pass_percentage: newPassPercentage, enabled: alcoholTestEnabled });
                setShowAlcoholConfig(false);
            } else {
                // Handle API error response
                const errorMessage = response.data?.error || response.data?.message || response.message || 'Failed to update configuration';
                showAlert({
                    message: errorMessage,
                    type: 'danger',
                    buttons: [{ label: 'Close' }]
                });
            }
        } catch (error) {
            console.error('Error updating alcohol test config:', error);
            showAlert({
                message: `Failed to update configuration: ${error.message || 'Unknown error'}`,
                type: 'danger',
                buttons: [{ label: 'Close' }]
            });
        } finally {
            setIsLoading(false);
        }
    };

    // =====================================================
    // Fuel Surcharge Configuration Functions
    // =====================================================

    const fetchFuelSurchargeRates = async () => {
        try {
            const response = await get_fuel_surcharge_rates();
            if (response.status === 200 && response.data) {
                setFuelSurchargeRates(response.data);
                setNewFuelRates({
                    low: response.data.low_percentage,
                    medium: response.data.medium_percentage,
                    high: response.data.high_percentage
                });
            }
        } catch (error) {
            console.error('Error fetching fuel surcharge rates:', error);
        }
    };

    const updateFuelSurchargeConfiguration = async () => {
        setIsLoading(true);
        try {
            const response = await update_fuel_surcharge_rates(
                newFuelRates.low,
                newFuelRates.medium,
                newFuelRates.high,
                'admin'
            );
            
            if (response.status === 200) {
                showAlert({
                    message: 'Fuel surcharge rates updated successfully',
                    type: 'success',
                    buttons: [{ label: 'Close' }]
                });
                setFuelSurchargeRates({
                    low_percentage: newFuelRates.low,
                    medium_percentage: newFuelRates.medium,
                    high_percentage: newFuelRates.high
                });
                setShowFuelConfig(false);
            } else {
                const errorMessage = response.data?.error || response.data?.message || response.message || 'Failed to update fuel surcharge rates';
                showAlert({
                    message: errorMessage,
                    type: 'danger',
                    buttons: [{ label: 'Close' }]
                });
            }
        } catch (error) {
            console.error('Error updating fuel surcharge rates:', error);
            showAlert({
                message: `Failed to update fuel surcharge rates: ${error.message || 'Unknown error'}`,
                type: 'danger',
                buttons: [{ label: 'Close' }]
            });
        } finally {
            setIsLoading(false);
        }
    };


    // =====================================================
    // Payslips handlers
    // =====================================================

    const MONTH_NAMES = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const fetchPayslipYears = useCallback(async () => {
        setIsPayslipPeriodsLoading(true);
        try {
            const { data, status } = await list_payslip_periods();
            if (status === 200) {
                setPayslipYears(data.years || []);
                setPayslipNavLevel('years');
                setPayslipNavYear(null);
                setPayslipNavMonth(null);
            } else {
                showAlert({ message: 'Failed to load payslip years', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        } catch (err) {
            showAlert({ message: 'Error loading payslip years', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        } finally {
            setIsPayslipPeriodsLoading(false);
        }
    }, [showAlert]);

    const fetchPayslipMonths = useCallback(async (year) => {
        setIsPayslipPeriodsLoading(true);
        try {
            const { data, status } = await list_payslip_periods(year);
            if (status === 200) {
                setPayslipMonths(data.months || []);
                setPayslipNavYear(year);
                setPayslipNavLevel('months');
                setPayslipNavMonth(null);
            } else {
                showAlert({ message: 'Failed to load payslip months', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        } catch (err) {
            showAlert({ message: 'Error loading payslip months', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        } finally {
            setIsPayslipPeriodsLoading(false);
        }
    }, [showAlert]);

    const fetchPayslips = useCallback(async (year, month, profileIdFilter) => {
        setIsPayslipsLoading(true);
        try {
            const filter = profileIdFilter !== undefined ? profileIdFilter : payslipFilterDriver;
            const { data, status } = await list_payslips(filter, year, month);
            if (status === 200) {
                setPayslips(data.payslips || []);
                setPayslipNavYear(year);
                setPayslipNavMonth(month);
                setPayslipNavLevel('files');
            } else {
                showAlert({ message: data?.error || 'Failed to load payslips', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        } catch (err) {
            showAlert({ message: 'Error loading payslips', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        } finally {
            setIsPayslipsLoading(false);
        }
    }, [payslipFilterDriver, showAlert]);

    const handleUploadPayslip = async () => {
        if (!payslipDriverId) {
            showAlert({ message: 'Please select a driver', type: 'warning', buttons: [{ label: 'Close', type: 'secondary' }] });
            return;
        }
        if (!payslipDisplayName.trim()) {
            showAlert({ message: 'Please enter a display name', type: 'warning', buttons: [{ label: 'Close', type: 'secondary' }] });
            return;
        }
        if (!payslipPayDate) {
            showAlert({ message: 'Please select a pay date', type: 'warning', buttons: [{ label: 'Close', type: 'secondary' }] });
            return;
        }
        if (!payslipFile) {
            showAlert({ message: 'Please select a PDF file', type: 'warning', buttons: [{ label: 'Close', type: 'secondary' }] });
            return;
        }

        setIsPayslipUploading(true);
        try {
            const { data, status } = await upload_payslip(payslipFile, payslipDriverId, payslipDisplayName, payslipPayDate);
            if (status === 201) {
                showAlert({ message: 'Payslip uploaded successfully', type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
                setPayslipDriverId('');
                setPayslipUploadDriverSearch('');
                setPayslipDisplayName('');
                setPayslipPayDate('');
                setPayslipFile(null);
                fetchPayslipYears();
            } else {
                showAlert({ message: data?.error || 'Upload failed', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        } catch (err) {
            showAlert({ message: 'Error uploading payslip', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        } finally {
            setIsPayslipUploading(false);
        }
    };

    const handleViewPayslip = (payslipId, displayName) => {
        setPayslipPendingAction(() => async () => {
            try {
                const { data, status } = await get_payslip_url(payslipId);
                if (status === 200 && data?.url) {
                    window.open(data.url, '_blank', 'noopener,noreferrer');
                } else {
                    showAlert({ message: data?.error || 'Failed to generate download link', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
                }
            } catch (err) {
                showAlert({ message: 'Error opening payslip', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        });
        setPayslipPasswordOpen(true);
    };

    const handleDeletePayslip = (payslipId) => {
        setPayslipPendingAction(() => async () => {
            showAlert({
                message: 'Are you sure you want to permanently delete this payslip?',
                type: 'warning',
                buttons: [
                    { label: 'Cancel', type: 'secondary' },
                    {
                        label: 'Delete',
                        type: 'danger',
                        onClick: async () => {
                            try {
                                const { data, status } = await delete_payslip(payslipId);
                                if (status === 200) {
                                    showAlert({ message: 'Payslip deleted', type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
                                    fetchPayslips(payslipNavYear, payslipNavMonth, payslipFilterDriver);
                                } else {
                                    showAlert({ message: data?.error || 'Failed to delete payslip', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
                                }
                            } catch (err) {
                                showAlert({ message: 'Error deleting payslip', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
                            }
                        }
                    }
                ],
            });
        });
        setPayslipPasswordOpen(true);
    };

    const handlePayslipPasswordSubmit = async (password) => {
        try {
            const { status } = await validatePassword(password, profile?.email);
            if (status === 200) {
                if (payslipPendingAction) payslipPendingAction();
            } else {
                showAlert({ message: 'Incorrect password.', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
            }
        } catch (err) {
            showAlert({ message: 'Error verifying password.', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        } finally {
            setPayslipPasswordOpen(false);
            setPayslipPendingAction(null);
        }
    };

    // =====================================================
    // SOPs & RAMS handlers
    // =====================================================

    const fetchDocuments = async () => {
        setIsDocLoading(true);
        try {
            const response = await list_documents(docFilterType === 'all' ? null : docFilterType);
            if (response.status === 200) {
                setDocuments(response.data?.documents || []);
            } else {
                showAlert({ message: 'Failed to load documents', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading documents', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsDocLoading(false);
        }
    };

    const handleDocUpload = async () => {
        if (!docUploadFile) {
            showAlert({ message: 'Please select a PDF file to upload', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }

        // Duplicate check against already-fetched documents
        const intendedName = (docUploadName.trim() || docUploadFile.name.replace(/\.pdf$/i, '')).toLowerCase();
        const duplicate = documents.find(d => {
            const strippedName = d.name
                .replace(/\.pdf$/i, '')
                .toLowerCase();
            return strippedName === intendedName;
        });
        if (duplicate) {
            showAlert({
                message: `A document named "${docUploadName.trim() || docUploadFile.name}" already exists. Delete the existing one first or use a different name.`,
                type: 'warning',
                buttons: [{ label: 'OK' }]
            });
            return;
        }

        setIsDocUploading(true);
        try {
            const response = await upload_document(docUploadFile, docUploadType, docUploadName);
            if (response.status === 200) {
                showAlert({ message: 'Document uploaded successfully', type: 'success', buttons: [{ label: 'Close' }] });
                setDocUploadFile(null);
                setDocUploadName('');
                fetchDocuments();
            } else {
                showAlert({ message: response.data?.error || 'Upload failed', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error uploading document', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsDocUploading(false);
        }
    };

    const handleDocDownload = async (filePath, fileName) => {
        try {
            const response = await get_document_url(filePath);
            if (response.status === 200 && response.data?.signed_url) {
                // Fetch as blob so the download attribute works cross-origin
                const blob = await fetch(response.data.signed_url).then(r => r.blob());
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objectUrl);
            } else {
                showAlert({ message: 'Failed to generate download link', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error downloading document', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    // =====================================================
    // Privilege Management handlers
    // =====================================================

    const fetchAdminProfiles = async () => {
        setIsPrivilegesLoading(true);
        try {
            const response = await get_admin_profiles();
            if (response.status === 200) {
                setAdminProfiles(response.data?.profiles || []);
            } else {
                showAlert({ message: 'Failed to load admin profiles', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading admin profiles', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsPrivilegesLoading(false);
        }
    };

    const handleToggleSuperAdmin = async (profileId, currentValue) => {
        const newValue = !currentValue;
        const action = newValue ? 'grant' : 'revoke';
        showAlert({
            message: `Are you sure you want to ${action} super admin privileges for this user?`,
            type: 'warning',
            buttons: [
                { label: 'Cancel', type: 'secondary' },
                {
                    label: 'Confirm',
                    type: newValue ? 'success' : 'danger',
                    onClick: async () => {
                        try {
                            const response = await set_super_admin(profileId, newValue);
                            if (response.status === 200) {
                                showAlert({ message: response.message || 'Updated successfully', type: 'success', buttons: [{ label: 'Close' }] });
                                fetchAdminProfiles();
                            } else {
                                showAlert({ message: response.data?.error || 'Failed to update', type: 'danger', buttons: [{ label: 'Close' }] });
                            }
                        } catch (error) {
                            showAlert({ message: 'Error updating privilege', type: 'danger', buttons: [{ label: 'Close' }] });
                        }
                    }
                }
            ]
        });
    };

    // Granular Permissions handlers (is_dev only)
    // =====================================================

    const fetchPermissionsUsers = async () => {
        try {
            const response = await get_permissions_users();
            if (response.status === 200) {
                const users = response.data?.users || [];
                setPermissionsUsers(JSON.parse(JSON.stringify(users))); // Deep copy
                setOriginalPermissionsUsers(JSON.parse(JSON.stringify(users)));
            } else {
                showAlert({ message: 'Failed to load granular permissions users', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading granular permissions users', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleTogglePermission = (userId, permissionKey) => {
        setPermissionsUsers((prev) => 
            prev.map((user) => {
                if (user.id === userId) {
                    return {
                        ...user,
                        [permissionKey]: !user[permissionKey]
                    };
                }
                return user;
            })
        );
    };

    const handleToggleAllPermissionsForUser = (userId, value) => {
        setPermissionsUsers((prev) => 
            prev.map((user) => {
                if (user.id === userId) {
                    const updatedUser = { ...user };
                    Object.keys(updatedUser).forEach((key) => {
                        if (key.startsWith('perm_')) {
                            updatedUser[key] = value;
                        }
                    });
                    return updatedUser;
                }
                return user;
            })
        );
    };

    const handleSavePermissions = async () => {
        // Calculate the diff to send to the backend
        const updates = [];
        
        permissionsUsers.forEach((user) => {
            const origUser = originalPermissionsUsers.find((orig) => orig.id === user.id);
            if (!origUser) return;
            
            const userPermissions = {};
            let hasChanges = false;
            
            Object.keys(user).forEach((key) => {
                if (key.startsWith('perm_')) {
                    if (user[key] !== origUser[key]) {
                        userPermissions[key] = user[key];
                        hasChanges = true;
                    }
                }
            });
            
            if (hasChanges) {
                updates.push({
                    user_id: user.id,
                    permissions: userPermissions
                });
            }
        });
        
        if (updates.length === 0) {
            showAlert({ message: 'No changes to save.', type: 'info', buttons: [{ label: 'Close' }] });
            return;
        }
        
        setIsPermissionsSaving(true);
        try {
            const response = await update_user_permissions(updates);
            if (response.status === 200 || response.status === 201) {
                showAlert({ message: 'User permissions successfully updated!', type: 'success', buttons: [{ label: 'Close' }] });
                fetchPermissionsUsers();
            } else {
                showAlert({ message: response.message || 'Failed to save permission updates', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error saving user permission updates', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsPermissionsSaving(false);
        }
    };

    const renderUserPermissionsRow = (user) => {
        const isExpanded = expandedPermissionUser === user.id;
        
        const permissionGroups = [
            { label: "User Accounts", viewKey: "perm_users_view", editKey: "perm_users_edit" },
            { label: "Vehicles Page", viewKey: "perm_vehicles_view", editKey: "perm_vehicles_edit" },
            { label: "Services / PMI Calendar", viewKey: "perm_services_view", editKey: "perm_services_edit" },
            { label: "Walk-Around Defects", viewKey: "perm_defects_view", editKey: "perm_defects_edit" },
            { label: "Tachograph Compliance", viewKey: "perm_tacho_view", editKey: "perm_tacho_edit" },
            { label: "Warehouse (WMS)", viewKey: "perm_warehouse_view", editKey: "perm_warehouse_edit" },
            { label: "Purchase Orders", viewKey: "perm_purchase_orders_view", editKey: "perm_purchase_orders_edit" },
            { label: "Timesheets Module", viewKey: "perm_timesheets_view", editKey: "perm_timesheets_edit" },
            { label: "Holiday Management", viewKey: "perm_holidays_view", editKey: "perm_holidays_edit" },
            { label: "Routing & Dispatch", viewKey: "perm_routing_view", editKey: "perm_routing_edit" },
            { label: "Notice Board", viewKey: "perm_notice_board_view", editKey: "perm_notice_board_edit" },
            { label: "Parcel Collections", viewKey: "perm_collections_view", editKey: "perm_collections_edit" },
            { label: "Accidents & Incidents", viewKey: "perm_incidents_view", editKey: "perm_incidents_edit" },
            { label: "Palletforce Integration", viewKey: "perm_palletforce_view", editKey: "perm_palletforce_edit" },
            { label: "Alcohol Compliance Tests", viewKey: "perm_alcohol_view", editKey: "perm_alcohol_edit" },
            { label: "Customers Page", viewKey: "perm_customers_view", editKey: "perm_customers_edit" },
            { label: "Sub-Contractors", viewKey: "perm_subcontractors_view", editKey: "perm_subcontractors_edit" },
            { label: "Training Module", viewKey: "perm_training_view", editKey: "perm_training_edit" },
            { label: "Invoicing", viewKey: "perm_invoice_view", editKey: "perm_invoice_edit" },
        ];

        return (
            <div 
                key={user.id} 
                style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    marginBottom: '12px', 
                    overflow: 'hidden',
                    backgroundColor: theme === 'dark' ? '#262626' : '#fff'
                }}
            >
                {/* Accordion Header */}
                <div 
                    onClick={() => setExpandedPermissionUser(isExpanded ? null : user.id)}
                    style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '16px 20px', 
                        cursor: 'pointer',
                        backgroundColor: isExpanded ? (theme === 'dark' ? '#333' : '#f1f8ff') : (theme === 'dark' ? '#222' : '#fafafa'),
                        borderBottom: isExpanded ? '1px solid #ddd' : 'none',
                        transition: 'background-color 0.2s'
                    }}
                >
                    <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: '600', fontSize: '15px' }}>{user.name} {user.surname}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{user.email}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                            {isExpanded ? 'Click to collapse' : 'Click to manage permissions'}
                        </span>
                        <FontAwesomeIcon icon={faMinus} style={{ width: 14, transform: isExpanded ? 'none' : 'rotate(90deg)', transition: 'transform 0.2s', opacity: 0.5 }} />
                    </div>
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                    <div style={{ padding: '24px', backgroundColor: theme === 'dark' ? '#1c1c1c' : '#fff' }}>
                        
                        {/* Quick toggle options */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px dashed #eee', paddingBottom: '16px' }}>
                            <Button variant="outline-success" size="sm" onClick={() => handleToggleAllPermissionsForUser(user.id, true)}>
                                Toggle All ON
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleToggleAllPermissionsForUser(user.id, false)}>
                                Toggle All OFF
                            </Button>
                        </div>

                        {/* Permissions Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                            {permissionGroups.map(group => (
                                <div 
                                    key={group.label} 
                                    style={{ 
                                        padding: '12px 16px', 
                                        borderRadius: '8px', 
                                        border: '1px solid #eee', 
                                        backgroundColor: theme === 'dark' ? '#222' : '#fafafa' 
                                    }}
                                >
                                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: theme === 'dark' ? '#fff' : '#333', textAlign: 'left' }}>{group.label}</div>
                                    <div style={{ display: 'flex', gap: '24px' }}>
                                        <Form.Check 
                                            type="switch"
                                            id={`${user.id}-${group.viewKey}`}
                                            label="View"
                                            checked={user[group.viewKey] || false}
                                            onChange={() => handleTogglePermission(user.id, group.viewKey)}
                                            style={{ fontSize: '13px', fontWeight: '500' }}
                                        />
                                        <Form.Check 
                                            type="switch"
                                            id={`${user.id}-${group.editKey}`}
                                            label="Edit"
                                            checked={user[group.editKey] || false}
                                            onChange={() => handleTogglePermission(user.id, group.editKey)}
                                            style={{ fontSize: '13px', fontWeight: '500' }}
                                        />
                                    </div>
                                </div>
                            ))}
                            
                            {/* Special Single-Toggles */}
                            <div 
                                style={{ 
                                    padding: '12px 16px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #eee', 
                                    backgroundColor: theme === 'dark' ? '#222' : '#fafafa' 
                                }}
                            >
                                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: theme === 'dark' ? '#fff' : '#333', textAlign: 'left' }}>Warehouse Map</div>
                                <Form.Check 
                                    type="switch"
                                    id={`${user.id}-perm_warehousemap_edit`}
                                    label="Edit Map Design"
                                    checked={user.perm_warehousemap_edit || false}
                                    onChange={() => handleTogglePermission(user.id, 'perm_warehousemap_edit')}
                                    style={{ fontSize: '13px', fontWeight: '500' }}
                                />
                            </div>

                            <div 
                                style={{ 
                                    padding: '12px 16px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #eee', 
                                    backgroundColor: theme === 'dark' ? '#222' : '#fafafa' 
                                }}
                            >
                                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: theme === 'dark' ? '#fff' : '#333', textAlign: 'left' }}>System Settings</div>
                                <Form.Check 
                                    type="switch"
                                    id={`${user.id}-perm_system_settings`}
                                    label="Access Settings"
                                    checked={user.perm_system_settings || false}
                                    onChange={() => handleTogglePermission(user.id, 'perm_system_settings')}
                                    style={{ fontSize: '13px', fontWeight: '500' }}
                                />
                            </div>

                            <div 
                                style={{ 
                                    padding: '12px 16px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #eee', 
                                    backgroundColor: theme === 'dark' ? '#222' : '#fafafa' 
                                }}
                            >
                                <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: theme === 'dark' ? '#fff' : '#333', textAlign: 'left' }}>AI Assistant Chatbot</div>
                                <Form.Check 
                                    type="switch"
                                    id={`${user.id}-perm_use_ai`}
                                    label="Access AI"
                                    checked={user.perm_use_ai || false}
                                    onChange={() => handleTogglePermission(user.id, 'perm_use_ai')}
                                    style={{ fontSize: '13px', fontWeight: '500' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Active KPI settings handlers
    // =====================================================

    const fetchCompanyVars = async () => {
        setIsCompanyVarsLoading(true);
        try {
            const response = await get_company_vars();
            if (response.status === 200 && response.data) {
                setCompanyVars(response.data);
                setCompanyVarsInput(response.data.contract_weekly_hours);
                setBackupHost(response.data.backup_host || '');
                setBackupPort(response.data.backup_port || 8765);
                setBackupEnabled(response.data.backup_enabled || false);
                setBackupApiKey('');
                setBackupApiKeySet(response.data.backup_api_key_set || false);
                setBackupLastIncremental(response.data.last_incremental_backup || null);
                setBackupLastFull(response.data.last_full_backup || null);
            } else {
                showAlert({ message: 'Failed to load company settings', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading company settings', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsCompanyVarsLoading(false);
        }
    };

    const handleUpdateCompanyVars = async () => {
        const hours = parseInt(companyVarsInput, 10);
        if (isNaN(hours) || hours < 0 || hours > 60) {
            showAlert({ message: 'Contract hours must be a whole number between 0 and 60', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        setIsCompanyVarsLoading(true);
        try {
            const response = await update_company_vars({ contract_weekly_hours: hours });
            if (response.status === 200) {
                setCompanyVars(prev => ({ ...prev, contract_weekly_hours: hours }));
                showAlert({ message: 'Contract hours updated successfully', type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                showAlert({ message: response.data?.error || 'Failed to update', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error updating company settings', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsCompanyVarsLoading(false);
        }
    };

    const emptyDepotLocationForm = {
        id: null,
        name: '',
        lat: '',
        lng: '',
        radius: 150,
        active: true
    };

    const fetchDepotLocations = async () => {
        setIsDepotLocationsLoading(true);
        try {
            const response = await get_depot_locations();
            if (response.status === 200 && Array.isArray(response.data)) {
                setDepotLocations(response.data);
            } else {
                showAlert({ message: response.data?.error || 'Failed to load clocking geofences', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading clocking geofences', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsDepotLocationsLoading(false);
        }
    };

    const handleDepotLocationChange = (field, value) => {
        setDepotLocationForm(prev => ({ ...prev, [field]: value }));
    };

    const handleEditDepotLocation = (location) => {
        setDepotLocationForm({
            id: location.id,
            name: location.name,
            lat: String(location.lat),
            lng: String(location.lng),
            radius: location.radius,
            active: location.active !== false
        });
    };

    const handleSaveDepotLocation = async () => {
        const lat = Number(depotLocationForm.lat);
        const lng = Number(depotLocationForm.lng);
        const radius = Number(depotLocationForm.radius);
        if (!depotLocationForm.name.trim()) {
            showAlert({ message: 'Area name is required', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
            showAlert({ message: 'Enter a valid latitude and longitude', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        if (!Number.isInteger(radius) || radius < 1 || radius > 10000) {
            showAlert({ message: 'Radius must be a whole number between 1 and 10000 metres', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }

        setIsDepotLocationsLoading(true);
        try {
            const response = await save_depot_location({
                ...depotLocationForm,
                name: depotLocationForm.name.trim(),
                lat,
                lng,
                radius
            });
            if (response.status === 200) {
                setDepotLocationForm(emptyDepotLocationForm);
                await fetchDepotLocations();
                showAlert({ message: response.data.message, type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                showAlert({ message: response.data?.error || 'Failed to save geofence', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error saving clocking geofence', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsDepotLocationsLoading(false);
        }
    };

    const handleDeactivateDepotLocation = async (location) => {
        setIsDepotLocationsLoading(true);
        try {
            const response = await deactivate_depot_location(location.id);
            if (response.status === 200) {
                if (depotLocationForm.id === location.id) {
                    setDepotLocationForm(emptyDepotLocationForm);
                }
                await fetchDepotLocations();
                showAlert({ message: response.data.message, type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                showAlert({ message: response.data?.error || 'Failed to deactivate geofence', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error deactivating clocking geofence', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsDepotLocationsLoading(false);
        }
    };

    const fetchActiveKpisSettings = async () => {
        setIsActiveKpisLoading(true);
        try {
            const response = await get_active_kpis();
            if (response.status === 200 && response.data) {
                setActiveKpisSettings(response.data);
                setTargetEdits(response.data);
            } else {
                showAlert({ message: 'Failed to load KPI settings', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading KPI settings', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsActiveKpisLoading(false);
        }
    };

    const handleUpdateTarget = async (targetKey, newValue) => {
        setActiveKpisSettings(prev => ({ ...prev, [targetKey]: newValue }));
        setTargetEdits(prev => ({ ...prev, [targetKey]: newValue }));
        try {
            const response = await update_active_kpi(targetKey, newValue);
            if (response.status === 200) {
                showAlert({ message: response.message || 'KPI target parameter updated', type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                fetchActiveKpisSettings();
                showAlert({ message: response.data?.error || 'Failed to update target', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            fetchActiveKpisSettings();
            showAlert({ message: 'Error updating target parameter', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleAdjustValue = (key, stepStr, direction) => {
        const step = parseFloat(stepStr) || 1;
        const currentVal = targetEdits[key] !== undefined && targetEdits[key] !== '' ? parseFloat(targetEdits[key]) : parseFloat(activeKpisSettings[key]) || 0;
        let newVal = direction === 'up' ? currentVal + step : currentVal - step;
        
        if (stepStr.toString().includes('.')) {
            const decimalPlaces = stepStr.toString().split('.')[1].length;
            newVal = parseFloat(newVal.toFixed(decimalPlaces));
        } else {
            newVal = Math.round(newVal);
        }
        
        setTargetEdits(prev => ({ ...prev, [key]: newVal }));
    };

    const handleToggleActiveKpi = async (kpiKey, currentValue) => {
        const newValue = !currentValue;
        setActiveKpisSettings(prev => ({ ...prev, [kpiKey]: newValue }));
        try {
            const response = await update_active_kpi(kpiKey, newValue);
            if (response.status === 200) {
                showAlert({ message: response.message || 'KPI visibility updated', type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                setActiveKpisSettings(prev => ({ ...prev, [kpiKey]: currentValue }));
                showAlert({ message: response.data?.error || 'Failed to update KPI', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            setActiveKpisSettings(prev => ({ ...prev, [kpiKey]: currentValue }));
            showAlert({ message: 'Error updating KPI visibility', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    // Feature Flags handlers
    // =====================================================

    const fetchFeatures = async () => {
        setIsFeaturesLoading(true);
        try {
            const response = await get_features();
            if (response.status === 200 && response.data) {
                setFeatureToggles(response.data);
            } else {
                showAlert({ message: 'Failed to load feature flags', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading feature flags', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsFeaturesLoading(false);
        }
    };

    const handleToggleFeature = async (featureKey, currentValue) => {
        const newValue = !currentValue;
        setFeatureToggles(prev => ({ ...prev, [featureKey]: newValue }));
        try {
            const response = await update_feature(featureKey, newValue);
            if (response.status === 200) {
                showAlert({ message: response.message || 'Feature updated', type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                setFeatureToggles(prev => ({ ...prev, [featureKey]: currentValue }));
                showAlert({ message: response.data?.error || 'Failed to update feature', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            setFeatureToggles(prev => ({ ...prev, [featureKey]: currentValue }));
            showAlert({ message: 'Error updating feature', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleDocDelete = async (filePath, fileName) => {
        showAlert({
            message: `Are you sure you want to delete "${fileName}"? This cannot be undone.`,
            type: 'danger',
            buttons: [
                { label: 'Cancel', type: 'secondary' },
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: async () => {
                        try {
                            const response = await delete_document(filePath);
                            if (response.status === 200) {
                                showAlert({ message: 'Document deleted', type: 'success', buttons: [{ label: 'Close' }] });
                                fetchDocuments();
                            } else {
                                showAlert({ message: response.data?.error || 'Delete failed', type: 'danger', buttons: [{ label: 'Close' }] });
                            }
                        } catch (error) {
                            showAlert({ message: 'Error deleting document', type: 'danger', buttons: [{ label: 'Close' }] });
                        }
                    }
                }
            ]
        });
    };

    const handleDocQrSheet = async (filePath, displayName, docType) => {
        try {
            const response = await get_document_qr_sheet(filePath, displayName, docType);
            if (response.status === 200 && response.data) {
                const objectUrl = URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = response.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objectUrl);
            } else {
                showAlert({ message: response.error || 'Failed to generate QR sheet', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error generating QR sheet', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleDocCopyLink = (publicUrl) => {
        navigator.clipboard.writeText(publicUrl).then(() => {
            showAlert({ message: 'Shareable link copied to clipboard!', type: 'success', buttons: [{ label: 'OK' }] });
        }).catch(() => {
            showAlert({ message: 'Failed to copy link. Please copy it manually:\n' + publicUrl, type: 'danger', buttons: [{ label: 'OK' }] });
        });
    };

    // =====================================================
    // Drivers Handbook handlers
    // =====================================================

    const fetchHandbook = async () => {
        setIsHandbookLoading(true);
        try {
            const response = await get_drivers_handbook();
            if (response.status === 200) {
                const content = response.data?.content || '';
                setHandbookContent(content);
                setHandbookDraft(content);
                setHandbookUpdatedAt(response.data?.updated_at || null);
                setHandbookHasChanges(false);
            } else {
                showAlert({ message: 'Failed to load handbook', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error loading handbook', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsHandbookLoading(false);
        }
    };

    const handleSaveHandbook = async () => {
        setIsHandbookSaving(true);
        try {
            const response = await update_drivers_handbook(handbookDraft);
            if (response.status === 200) {
                setHandbookContent(handbookDraft);
                setHandbookUpdatedAt(response.data?.updated_at || null);
                setHandbookHasChanges(false);
                showAlert({ message: 'Handbook saved successfully', type: 'success', buttons: [{ label: 'Close' }] });
            } else {
                showAlert({ message: response.data?.error || 'Failed to save handbook', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } catch (error) {
            showAlert({ message: 'Error saving handbook', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setIsHandbookSaving(false);
        }
    };

    const handleHandbookDraftChange = (value) => {
        setHandbookDraft(value);
        setHandbookHasChanges(value !== handbookContent);
    };

    const insertHandbookMarkdown = (textareaRef, before, after = '') => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const selected = handbookDraft.substring(start, end);
        const newValue =
            handbookDraft.substring(0, start) +
            before + selected + after +
            handbookDraft.substring(end);
        handleHandbookDraftChange(newValue);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + before.length, start + before.length + selected.length);
        }, 0);
    };


    // Check for dev/super-admin access
    if (!isDev && !isManager) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red' }}>
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    const hasAnyDevPermission = isDev || isManager || (permissions && (
        permissions.perm_system_settings ||
        permissions.perm_defects_edit ||
        permissions.perm_alcohol_edit ||
        permissions.perm_timesheets_edit ||
        permissions.perm_invoice_edit
    ));

    if (!hasAnyDevPermission) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red', padding: '0 20px' }}>
                <h3>Access Restricted</h3>
                <p style={{ color: '#666', marginTop: '10px', fontSize: '18px', fontWeight: 'bold' }}>
                    You do not have permissions for this page.
                </p>
                <p style={{ color: '#888', fontSize: '14px' }}>
                    Please contact your system developer to request access to specific modules.
                </p>
            </div>
        );
    }
    
    return (
        <div id={theme} className="container-fluid closed-drivers-container">

            <Row>
                {/* Left Navigation Panel */}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>

                        {/* email */}
                        {(isDev || permissions.perm_system_settings) && (<li title="Email Notifications" className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'notifications' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('notifications') : null}><FontAwesomeIcon icon={faEnvelope} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Email Notifications</span></li>)}

                        {/* defect management */}
                        {features.mobile !== false && (isDev || permissions.perm_defects_edit) && (<li title="Defect Management" className={`nav-item ${activeTab === 'defect-management' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'defect-management' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('defect-management') : null}><FontAwesomeIcon icon={faTriangleExclamation} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Defect Management</span></li>)}

                        {/* alcohol testing */}
                        {features.alcohol_testing !== false && (isDev || permissions.perm_alcohol_edit) && (<li title="Alcohol Testing Config" className={`nav-item ${activeTab === 'alcohol-testing' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'alcohol-testing' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('alcohol-testing') : null}><FontAwesomeIcon icon={faFlask} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Alcohol Testing Config</span></li>)}

                        {/* fuel surcharge */}
                        {features.routing !== false && (isDev || permissions.perm_system_settings) && (<li title="Fuel Surcharge Config" className={`nav-item ${activeTab === 'fuel-surcharge' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'fuel-surcharge' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('fuel-surcharge') : null}><FontAwesomeIcon icon={faGasPump} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Fuel Surcharge Config</span></li>)}
                        
                        {/* SOPs & RAMS */}
                        {(isDev || permissions.perm_defects_edit) && (<li title="SOPs &amp; RAMS" className={`nav-item ${activeTab === 'sops-rams' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'sops-rams' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('sops-rams') : null}><FontAwesomeIcon icon={faFileLines} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">SOPs &amp; RAMS</span></li>)}
                        
                        {/* Payslips */}
                        {(isDev || permissions.perm_timesheets_edit) && (<li title="Payslips" className={`nav-item ${activeTab === 'payslips' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'payslips' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('payslips') : null}><FontAwesomeIcon icon={faMoneyBill} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Payslips</span></li>)}
                        
                        {/* KPI Settings */}
                        {features.kpis !== false && (isDev || permissions.perm_system_settings) && (<li title="KPI Settings" className={`nav-item ${activeTab === 'kpi-settings' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'kpi-settings' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('kpi-settings') : null}><FontAwesomeIcon icon={faChartBar} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">KPI Settings</span></li>)}
                        
                        {/* Timesheet Settings */}
                        {features.timesheets !== false && (isDev || permissions.perm_system_settings) && (<li title="Timesheet Settings" className={`nav-item ${activeTab === 'company-vars' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'company-vars' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('company-vars') : null}><FontAwesomeIcon icon={faClock} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Timesheet Settings</span></li>)}

                        {(isDev || permissions.perm_invoice_edit) && (<li title="Invoice Settings" className={`nav-item ${activeTab === 'invoice-settings' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'invoice-settings' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('invoice-settings') : null}><FontAwesomeIcon icon={faFileInvoiceDollar} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Invoice Settings</span></li>)}
                        {(isDev || isManager) && (<li title="Driver Bonus" className={`nav-item ${activeTab === 'driver-bonus' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'driver-bonus' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('driver-bonus') : null}><FontAwesomeIcon icon={faGift} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Driver Bonus</span></li>)}
                        
                        {/* dev only */}
                        {isDev && (<li title="Test Push Notifications" className={`nav-item ${activeTab === 'test-notifications' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'test-notifications' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('test-notifications') : null}><FontAwesomeIcon icon={faBell} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Test Push Notifications</span></li>)}
                        {isDev && (<li title="Company Branding" className={`nav-item ${activeTab === 'branding' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'branding' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('branding') : null}><FontAwesomeIcon icon={faPalette} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Company Branding</span></li>)}
                        {isDev && (<li title="Privilege Management" className={`nav-item ${activeTab === 'privilege-management' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'privilege-management' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('privilege-management') : null}><FontAwesomeIcon icon={faShield} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Privilege Management</span></li>)}
                        {isDev && (<li title="Feature Flags" className={`nav-item ${activeTab === 'feature-flags' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'feature-flags' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('feature-flags') : null}><FontAwesomeIcon icon={faFlag} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Feature Flags</span></li>)}
                        {isDev && (<li title="Integrations" className={`nav-item ${activeTab === 'integrations' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'integrations' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('integrations') : null}><FontAwesomeIcon icon={faPlug} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Integrations</span></li>)}
                        
                        {/* backup config */}
                        {(isDev || permissions.perm_system_settings) && (<li title="Backup Config" className={`nav-item ${activeTab === 'backup-config' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'backup-config' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('backup-config') : null}><FontAwesomeIcon icon={faDatabase} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Backup Config</span></li>)}
                        
                        {/* Developer Tools */}
                        {isDev && (<li title="Developer Tools" className={`nav-item ${activeTab === 'developer-tools' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'developer-tools' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('developer-tools') : null}><FontAwesomeIcon icon={faCode} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Developer Tools</span></li>)}
                        
                        {/* Drivers Handbook */}
                        {(isDev || permissions.perm_defects_edit) && (<li title="Drivers Handbook" className={`nav-item ${activeTab === 'drivers-handbook' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'drivers-handbook' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('drivers-handbook') : null}><FontAwesomeIcon icon={faBook} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Drivers Handbook</span></li>)}

                        {/* Training */}
                        {features.training !== false && (isDev || permissions.perm_training_view) && (<li title="Training" className={`nav-item ${activeTab === 'training' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'training' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => setActiveTab('training') : null}><FontAwesomeIcon icon={faCircleCheck} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Training</span></li>)}
                        
                        {/* Remote Assistance */}
                        {isDev && (<li title="Remote Assistance" className="nav-item" style={{ cursor: 'pointer', background: 'transparent' }} onClick={() => window.open('https://217.154.37.183', '_blank')}><FontAwesomeIcon icon={faHeadset} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Remote Assistance</span></li>)}
                    </ul>
                </Col>

                {/* Right Content Panel */}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>
                    {/* Show progress tracker when processing QR codes */}
                    {isQRProcessing && (
                        <ProgressTracker
                            title="QR Code Regeneration"
                            progress={{
                                current: qrProgress.current,
                                total: qrProgress.total,
                                percentage: qrProgress.percentage
                            }}
                            status={{
                                message: qrRegenerationStatus?.message || 'Processing...',
                                type: qrRegenerationStatus?.type || 'info'
                            }}
                            isProcessing={isQRProcessing}
                            showProgress={true}
                        />
                    )}

                    {/* Show loading indicator only for non-QR processing operations */}
                    {isLoading && !isQRProcessing ? (
                        <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                            <ActivityIndicator />
                        </div>
                    ) : (
                        <>
                            {/* Email Notifications Configuration */}
                            <div className="accounts-container">
                                {activeTab === 'notifications' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Configure Email Notifications</h3>
                                        </div>

                                        {/* Notification Type Selector */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Select Notification Type:</strong></h5>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                {notificationTypes.map((type) => (
                                                    <Button
                                                        key={type.key}
                                                        variant={selectedNotificationType === type.key ? 'primary' : 'outline-primary'}
                                                        onClick={() => {
                                                            setSelectedNotificationType(type.key);
                                                            setSelectedUser(''); // Reset selected user when changing type
                                                        }}
                                                        disabled={isLoading}
                                                        style={{ minWidth: '160px' }}
                                                    >
                                                        {type.label}
                                                    </Button>
                                                ))}
                                            </div>
                                            <p style={{ 
                                                marginTop: '10px', 
                                                marginBottom: '0', 
                                                fontStyle: 'italic', 
                                                color: theme === 'dark' ? '#bbb' : '#666',
                                                fontSize: '0.9em'
                                            }}>
                                                {notificationTypes.find(type => type.key === selectedNotificationType)?.description}
                                            </p>
                                        </div>

                                        {/* Current Notification Type Configuration */}
                                        <div style={{ margin: '20px 0' }}>
                                            <Form.Group style={{ marginBottom: '20px' }}>
                                                <Form.Label>
                                                    <strong>Add User to {notificationTypes.find(type => type.key === selectedNotificationType)?.label}:</strong>
                                                </Form.Label>
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <Form.Control
                                                        as="select"
                                                        value={selectedUser ? selectedUser.email : ''}
                                                        onChange={(e) => {
                                                            const selectedEmail = e.target.value;
                                                            const user = allUsers.find(u => u.email === selectedEmail);
                                                            setSelectedUser(user || '');
                                                        }}
                                                        style={{ flex: 1 }}
                                                    >
                                                        <option value="">Select a user...</option>
                                                        {allUsers.map((user) => (
                                                            <option key={user.email} value={user.email}>
                                                                {user.name} {user.surname} [{user.email}]
                                                            </option>
                                                        ))}
                                                    </Form.Control>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleAddUser}
                                                        disabled={isLoading || !selectedUser}
                                                    >
                                                        Add User
                                                    </Button>
                                                </div>
                                            </Form.Group>

                                            <div style={{ marginTop: '30px' }}>
                                                <h5>
                                                    <strong>Current Users Receiving {notificationTypes.find(type => type.key === selectedNotificationType)?.label}:</strong>
                                                </h5>
                                                {getCurrentNotificationUsers().length > 0 ? (
                                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                                        {getCurrentNotificationUsers().map((user) => (
                                                            <li
                                                                key={user.email}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    padding: '10px',
                                                                    margin: '5px 0',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '5px',
                                                                    backgroundColor: theme === 'dark' ? '#333' : '#f9f9f9'
                                                                }}
                                                            >
                                                                <span>
                                                                    <strong>{user.name} {user.surname}</strong> [{user.email}]
                                                                </span>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveUser(user.email)}
                                                                    disabled={isLoading}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p style={{ fontStyle: 'italic', color: '#666' }}>
                                                        No users currently configured for {notificationTypes.find(type => type.key === selectedNotificationType)?.label.toLowerCase()}.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Defect Management Section */}
                                {activeTab === 'defect-management' && (isDev || permissions.perm_defects_edit) && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Defect Management</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Manage defect types and vehicle assignments
                                            </p>
                                        </div>

                                        {/* Vehicle Type Selector */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Select Vehicle Type:</strong></h5>
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                {['UNIT', 'RIGID', 'TRAILER', 'MHE'].map((vehicleType) => (
                                                    <Button
                                                        key={vehicleType}
                                                        variant={selectedVehicleType === vehicleType ? 'primary' : 'outline-primary'}
                                                        onClick={() => setSelectedVehicleType(normalizeVehicleType(vehicleType))}
                                                        disabled={isLoading}
                                                        style={{ minWidth: '80px' }}
                                                    >
                                                        {vehicleType}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Defect Types Management - Side by side layout */}
                                        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', minWidth: '400px' }}>
                                            {/* All Defect Types - Left Side */}
                                            <div style={{ flex: 1, padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                    <h5 style={{ margin: 0 }}><strong>All Defect Types:</strong></h5>
                                                    <Button
                                                        variant="success"
                                                        onClick={() => setShowAddDefectForm(true)}
                                                        disabled={isLoading}
                                                        size="sm"
                                                    >
                                                        + Add
                                                    </Button>
                                                </div>

                                                {/* Add New Defect Form */}
                                                {showAddDefectForm && (
                                                    <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #007bff', borderRadius: '5px', backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f0f8ff' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                            <input
                                                                type="text"
                                                                placeholder="Enter defect type name..."
                                                                value={newDefectType}
                                                                onChange={(e) => setNewDefectType(e.target.value)}
                                                                style={{ 
                                                                    padding: '8px', 
                                                                    border: '1px solid #ddd', 
                                                                    borderRadius: '4px',
                                                                    backgroundColor: theme === 'dark' ? '#333' : '#fff',
                                                                    color: theme === 'dark' ? '#fff' : '#000'
                                                                }}
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder="Enter description (optional)..."
                                                                value={newDefectDescription}
                                                                onChange={(e) => setNewDefectDescription(e.target.value)}
                                                                style={{ 
                                                                    padding: '8px', 
                                                                    border: '1px solid #ddd', 
                                                                    borderRadius: '4px',
                                                                    backgroundColor: theme === 'dark' ? '#333' : '#fff',
                                                                    color: theme === 'dark' ? '#fff' : '#000'
                                                                }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={async () => {
                                                                        if (!newDefectType.trim()) return;
                                                                        
                                                                        try {
                                                                            const sessionToken = await getSessionUUID();
                                                                            const response = await fetch('/defects/types', {
                                                                                method: 'POST',
                                                                                headers: {
                                                                                    'Content-Type': 'application/json',
                                                                                    'Authorization': `Bearer ${sessionToken}`
                                                                                },
                                                                                body: JSON.stringify({
                                                                                    defect: newDefectType.trim(),
                                                                                    description: newDefectDescription.trim() || null
                                                                                }),
                                                                            });
                                                                            
                                                                            if (response.ok) {
                                                                                setNewDefectType('');
                                                                                setNewDefectDescription('');
                                                                                setShowAddDefectForm(false);
                                                                                fetchDefectTypes(); // Refresh the list
                                                                            } else {
                                                                                console.error('Failed to add defect type');
                                                                            }
                                                                        } catch (error) {
                                                                            console.error('Error adding defect type:', error);
                                                                        }
                                                                    }}
                                                                    disabled={!newDefectType.trim() || isLoading}
                                                                    size="sm"
                                                                >
                                                                    Add
                                                                </Button>
                                                                <Button
                                                                    variant="secondary"
                                                                    onClick={() => {
                                                                        setNewDefectType('');
                                                                        setNewDefectDescription('');
                                                                        setShowAddDefectForm(false);
                                                                    }}
                                                                    size="sm"
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Defect Types List */}
                                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                    {defectTypes.length > 0 ? defectTypes.map((defectType) => (
                                                        <div key={defectType.id} style={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'center',
                                                            padding: '10px',
                                                            margin: '5px 0',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '5px',
                                                            backgroundColor: theme === 'dark' ? '#333' : '#fff'
                                                        }}>
                                                            <span style={{ fontWeight: '500' }}>{defectType.defect}</span>
                                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => setEditingDefectType(defectType)}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        showAlert({
                                                                            message: `Are you sure you want to delete "${defectType.defect}"?`,
                                                                            type: 'warning',
                                                                            buttons: [
                                                                                {
                                                                                    label: 'Delete',
                                                                                    type: 'danger',
                                                                                    onClick: async () => {
                                                                                        try {
                                                                                            const sessionToken = await getSessionUUID();
                                                                                            const response = await fetch(`/defects/types/${defectType.id}`, {
                                                                                                method: 'DELETE',
                                                                                                headers: {
                                                                                                    'Authorization': `Bearer ${sessionToken}`
                                                                                                }
                                                                                            });
                                                                                            
                                                                                            if (response.ok) {
                                                                                                fetchDefectTypes(); // Refresh the list
                                                                                                showAlert({
                                                                                                    message: 'Defect type deleted successfully',
                                                                                                    type: 'success',
                                                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                                                });
                                                                                            } else {
                                                                                                showAlert({
                                                                                                    message: 'Failed to delete defect type',
                                                                                                    type: 'danger',
                                                                                                    buttons: [{ label: 'Close', type: 'primary' }]
                                                                                                });
                                                                                            }
                                                                                        } catch (error) {
                                                                                            showAlert({
                                                                                                message: 'An error occurred while deleting the defect type',
                                                                                                type: 'danger',
                                                                                                buttons: [{ label: 'Close', type: 'primary' }]
                                                                                            });
                                                                                        }
                                                                                    }
                                                                                },
                                                                                {
                                                                                    label: 'Cancel',
                                                                                    type: 'secondary'
                                                                                }
                                                                            ]
                                                                        });
                                                                    }}>
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )) : (
                                                        <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                                                            No defect types found. Add one to get started.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Add Defects to Vehicle - Right Side */}
                                            {selectedVehicleType && (
                                                <div style={{ flex: 1, padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                                    <h5 style={{ marginBottom: '15px' }}><strong>Add Defects to {selectedVehicleType}:</strong></h5>
                                                    <p style={{ color: '#666', fontSize: '0.9em', marginBottom: '15px' }}>
                                                        Select defects to assign to {selectedVehicleType} vehicles:
                                                    </p>
                                                    
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                                                        {defectTypes
                                                            .filter(defectType => {
                                                                const combinedAssignments = getCombinedAssignments();
                                                                return !combinedAssignments.some(dtv => dtv.defect_type_id === defectType.id);
                                                            })
                                                            .map(defectType => (
                                                                <Button
                                                                    key={defectType.id}
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    onClick={() => addDefectToPending(defectType.id, selectedVehicleType)}
                                                                >
                                                                    + {defectType.defect}
                                                                </Button>
                                                            ))
                                                        }
                                                        {defectTypes.filter(defectType => {
                                                            const combinedAssignments = getCombinedAssignments();
                                                            return !combinedAssignments.some(dtv => dtv.defect_type_id === defectType.id);
                                                        }).length === 0 && (
                                                            <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>
                                                                All defect types are already assigned to {selectedVehicleType}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Vehicle Defect Assignments */}
                                        {selectedVehicleType && (
                                            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                    <h5 style={{ margin: 0 }}><strong>Defect Checks for {selectedVehicleType} (Drag to Reorder):</strong></h5>
                                                    {hasUnsavedChanges && (
                                                        <div style={{ display: 'flex', gap: '10px' }}>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={saveAllChanges}
                                                                disabled={isLoading}
                                                            >
                                                                💾 Save Changes
                                                            </Button>
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={cancelAllChanges}
                                                                disabled={isLoading}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                                <p style={{ color: '#666', fontSize: '0.9em', marginBottom: '15px' }}>
                                                    This order determines how drivers see defect checks during vehicle inspection.
                                                    {hasUnsavedChanges && (
                                                        <span style={{ color: '#ff6b35', fontWeight: 'bold' }}> • Unsaved changes</span>
                                                    )}
                                                </p>
                                                    
                                                    {getCombinedAssignments().length > 0 ? (
                                                        <div style={{ minHeight: '200px' }}>
                                                            {getCombinedAssignments().map((assignment, index) => {
                                                                const isPending = assignment.isPending;
                                                                const isMarkedForRemoval = pendingRemovals.includes(assignment.id);
                                                                
                                                                return (
                                                                    <div 
                                                                        key={assignment.id} 
                                                                        style={{ 
                                                                            display: 'flex', 
                                                                            justifyContent: 'space-between', 
                                                                            alignItems: 'center',
                                                                            padding: '12px',
                                                                            margin: '5px 0',
                                                                            border: `1px solid ${isPending ? '#28a745' : isMarkedForRemoval ? '#dc3545' : '#ddd'}`,
                                                                            borderRadius: '5px',
                                                                            backgroundColor: isPending ? (theme === 'dark' ? '#1a4d2e' : '#e8f5e8') : 
                                                                                              isMarkedForRemoval ? (theme === 'dark' ? '#4d1a1a' : '#fde8e8') : 
                                                                                              (theme === 'dark' ? '#333' : '#fff'),
                                                                            cursor: 'grab',
                                                                            opacity: isMarkedForRemoval ? 0.5 : 1
                                                                        }}
                                                                        draggable={!isPending && !isMarkedForRemoval}
                                                                        onDragStart={(e) => {
                                                                            e.dataTransfer.setData('text/plain', index.toString());
                                                                        }}
                                                                        onDragOver={(e) => {
                                                                            e.preventDefault();
                                                                        }}
                                                                        onDrop={(e) => {
                                                                            e.preventDefault();
                                                                            const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                                                            if (draggedIndex !== index) {
                                                                                reorderAssignments(draggedIndex, index);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <span style={{ marginRight: '10px', color: '#666' }}>#{assignment.position}</span>
                                                                            <span style={{ fontWeight: '500' }}>
                                                                                {assignment.defect_types ? assignment.defect_types.defect : 'Unknown Defect'}
                                                                            </span>
                                                                            {isPending && (
                                                                                <span style={{ marginLeft: '10px', color: '#28a745', fontSize: '0.8em', fontWeight: 'bold' }}>
                                                                                    NEW
                                                                                </span>
                                                                            )}
                                                                            {isMarkedForRemoval && (
                                                                                <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '0.8em', fontWeight: 'bold' }}>
                                                                                    REMOVING
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => removeDefectToPending(assignment.id)}
                                                                            disabled={isMarkedForRemoval}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div style={{ 
                                                            minHeight: '200px', 
                                                            border: '2px dashed #ddd', 
                                                            borderRadius: '8px', 
                                                            padding: '40px',
                                                            textAlign: 'center',
                                                            backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f9f9f9'
                                                        }}>
                                                            <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>
                                                                No defects assigned to {selectedVehicleType} yet.
                                                                <br />
                                                                Use the buttons above to assign defect types.
                                                            </p>
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Alcohol Testing Configuration Section */}
                                {activeTab === 'alcohol-testing' && (isDev || permissions.perm_alcohol_edit) && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Alcohol Testing Configuration</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Configure pass/fail ratios and system settings for random alcohol testing
                                            </p>
                                        </div>

                                        {/* Current Status Display */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '2px solid #007bff', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#1a1a2e' : '#f0f8ff', textAlign: 'center' }}>
                                            <h4 style={{ margin: 0, marginBottom: '10px', color: '#007bff' }}>Current Configuration</h4>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
                                                <div>
                                                    <strong>Pass Rate:</strong> 
                                                    <span style={{ fontSize: '24px', color: '#28a745', marginLeft: '8px' }}>
                                                        {alcoholTestConfig.pass_percentage}%
                                                    </span>
                                                </div>
                                                <div>
                                                    <strong>System Status:</strong>
                                                    <span style={{ 
                                                        fontSize: '18px', 
                                                        color: alcoholTestConfig.enabled ? '#28a745' : '#dc3545',
                                                        marginLeft: '8px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {alcoholTestConfig.enabled ? '✅ ENABLED' : '❌ DISABLED'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Configuration Panel */}
                                        <div style={{ padding: '30px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                                                <h5 style={{ margin: 0, fontSize: '20px' }}><strong>Update Configuration</strong></h5>
                                                {!showAlcoholConfig && (
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => setShowAlcoholConfig(true)}
                                                        disabled={isLoading}
                                                        style={{ padding: '8px 16px', fontSize: '16px', marginLeft: '20px' }}
                                                    >
                                                        🔧 Configure Settings
                                                    </Button>
                                                )}
                                            </div>

                                            {showAlcoholConfig && (
                                                <div style={{ padding: '20px', border: '2px solid #007bff', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff' }}>
                                                    <div style={{ marginBottom: '30px' }}>
                                                        <label style={{ display: 'block', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>
                                                            🎯 Pass Percentage: {newPassPercentage}%
                                                        </label>
                                                        <div style={{ marginBottom: '15px' }}>
                                                            <input
                                                                type="range"
                                                                min="0"
                                                                max="100"
                                                                value={newPassPercentage}
                                                                onChange={(e) => setNewPassPercentage(parseInt(e.target.value))}
                                                                style={{ width: '100%', height: '8px', marginBottom: '10px' }}
                                                                disabled={isLoading}
                                                            />
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#666' }}>
                                                                <span>0% (Always Fail)</span>
                                                                <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                                                                    {newPassPercentage}% chance of PASS
                                                                </span>
                                                                <span>100% (Always Pass)</span>
                                                            </div>
                                                        </div>
                                                        <div style={{ padding: '15px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa', borderRadius: '5px', marginTop: '15px' }}>
                                                            <p style={{ margin: 0, fontSize: '14px' }}>
                                                                <strong>📊 What this means:</strong><br/>
                                                                • {newPassPercentage}% of tests will show GREEN (pass)<br/>
                                                                • {100 - newPassPercentage}% of tests will show RED (fail)<br/>
                                                                • Adjust this slider to set the desired pass rate for random alcohol tests
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginBottom: '30px' }}>
                                                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 'bold' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={alcoholTestEnabled}
                                                                onChange={(e) => setAlcoholTestEnabled(e.target.checked)}
                                                                style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                                                                disabled={isLoading}
                                                            />
                                                            🔄 Enable Alcohol Testing System
                                                        </label>
                                                        <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', marginLeft: '32px' }}>
                                                            When disabled, users will see an error message when trying to generate tests
                                                        </p>
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
                                                        <Button
                                                            variant="success"
                                                            onClick={updateAlcoholTestConfiguration}
                                                            disabled={isLoading}
                                                            style={{ padding: '12px 30px', fontSize: '16px' }}
                                                        >
                                                            {isLoading ? (
                                                                <>🔄 Updating...</>
                                                            ) : (
                                                                <>💾 Save Configuration</>
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setNewPassPercentage(alcoholTestConfig.pass_percentage);
                                                                setAlcoholTestEnabled(alcoholTestConfig.enabled);
                                                                setShowAlcoholConfig(false);
                                                            }}
                                                            disabled={isLoading}
                                                            style={{ padding: '12px 30px', fontSize: '16px' }}
                                                        >
                                                            ❌ Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Fuel Surcharge Configuration Section */}
                                {activeTab === 'fuel-surcharge' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>⛽ Fuel Surcharge Configuration</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Configure percentage rates for low, medium, and high fuel surcharges
                                            </p>
                                        </div>

                                        {/* Current Status Display */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '2px solid #ff6b35', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a1a1a' : '#fff8f5', textAlign: 'center' }}>
                                            <h4 style={{ margin: 0, marginBottom: '20px', color: '#ff6b35' }}>Current Fuel Surcharge Rates</h4>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
                                                <div style={{ padding: '10px', border: '1px solid #28a745', borderRadius: '8px', backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
                                                    <strong>Low Surcharge:</strong> 
                                                    <span style={{ fontSize: '24px', color: '#28a745', marginLeft: '8px' }}>
                                                        {fuelSurchargeRates.low_percentage}%
                                                    </span>
                                                </div>
                                                <div style={{ padding: '10px', border: '1px solid #ffc107', borderRadius: '8px', backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                                                    <strong>Medium Surcharge:</strong>
                                                    <span style={{ 
                                                        fontSize: '24px', 
                                                        color: '#ffc107',
                                                        marginLeft: '8px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {fuelSurchargeRates.medium_percentage}%
                                                    </span>
                                                </div>
                                                <div style={{ padding: '10px', border: '1px solid #dc3545', borderRadius: '8px', backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                                                    <strong>High Surcharge:</strong>
                                                    <span style={{ 
                                                        fontSize: '24px', 
                                                        color: '#dc3545',
                                                        marginLeft: '8px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {fuelSurchargeRates.high_percentage}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Configuration Panel */}
                                        <div style={{ padding: '30px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                                                <h5 style={{ margin: 0, fontSize: '20px' }}><strong>Update Fuel Surcharge Rates</strong></h5>
                                                {!showFuelConfig && (
                                                    <Button
                                                        variant="warning"
                                                        onClick={() => setShowFuelConfig(true)}
                                                        disabled={isLoading}
                                                        style={{ padding: '8px 16px', fontSize: '16px', marginLeft: '20px' }}
                                                    >
                                                        🔧 Configure Rates
                                                    </Button>
                                                )}
                                            </div>

                                            {showFuelConfig && (
                                                <div style={{ padding: '20px', border: '2px solid #ff6b35', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px', marginBottom: '30px' }}>
                                                        
                                                        {/* Low Surcharge */}
                                                        <div style={{ padding: '20px', border: '2px solid #28a745', borderRadius: '12px', backgroundColor: 'rgba(40, 167, 69, 0.05)' }}>
                                                            <label style={{ display: 'block', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                                                                🟢 Low Surcharge: {newFuelRates.low}%
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="50"
                                                                step="0.1"
                                                                value={newFuelRates.low}
                                                                onChange={(e) => setNewFuelRates(prev => ({ ...prev, low: parseFloat(e.target.value) || 0 }))}
                                                                style={{ 
                                                                    width: '100%', 
                                                                    padding: '12px', 
                                                                    fontSize: '16px', 
                                                                    border: '2px solid #28a745', 
                                                                    borderRadius: '6px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
                                                                    color: theme === 'dark' ? '#fff' : '#333'
                                                                }}
                                                                disabled={isLoading}
                                                            />
                                                            <p style={{ fontSize: '12px', color: '#28a745', marginTop: '8px' }}>
                                                                Recommended: 3% - 6%
                                                            </p>
                                                        </div>

                                                        {/* Medium Surcharge */}
                                                        <div style={{ padding: '20px', border: '2px solid #ffc107', borderRadius: '12px', backgroundColor: 'rgba(255, 193, 7, 0.05)' }}>
                                                            <label style={{ display: 'block', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>
                                                                🟡 Medium Surcharge: {newFuelRates.medium}%
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="50"
                                                                step="0.1"
                                                                value={newFuelRates.medium}
                                                                onChange={(e) => setNewFuelRates(prev => ({ ...prev, medium: parseFloat(e.target.value) || 0 }))}
                                                                style={{ 
                                                                    width: '100%', 
                                                                    padding: '12px', 
                                                                    fontSize: '16px', 
                                                                    border: '2px solid #ffc107', 
                                                                    borderRadius: '6px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
                                                                    color: theme === 'dark' ? '#fff' : '#333'
                                                                }}
                                                                disabled={isLoading}
                                                            />
                                                            <p style={{ fontSize: '12px', color: '#e0a800', marginTop: '8px' }}>
                                                                Recommended: 5% - 8%
                                                            </p>
                                                        </div>

                                                        {/* High Surcharge */}
                                                        <div style={{ padding: '20px', border: '2px solid #dc3545', borderRadius: '12px', backgroundColor: 'rgba(220, 53, 69, 0.05)' }}>
                                                            <label style={{ display: 'block', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                                                                🔴 High Surcharge: {newFuelRates.high}%
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="50"
                                                                step="0.1"
                                                                value={newFuelRates.high}
                                                                onChange={(e) => setNewFuelRates(prev => ({ ...prev, high: parseFloat(e.target.value) || 0 }))}
                                                                style={{ 
                                                                    width: '100%', 
                                                                    padding: '12px', 
                                                                    fontSize: '16px', 
                                                                    border: '2px solid #dc3545', 
                                                                    borderRadius: '6px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
                                                                    color: theme === 'dark' ? '#fff' : '#333'
                                                                }}
                                                                disabled={isLoading}
                                                            />
                                                            <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '8px' }}>
                                                                Recommended: 7% - 12%
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div style={{ padding: '15px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
                                                        <p style={{ margin: 0, fontSize: '14px' }}>
                                                            <strong>📊 How Fuel Surcharges Work:</strong><br/>
                                                            • Customers with <strong style={{ color: '#28a745' }}>Low surcharge</strong> will have costs increased by {newFuelRates.low}%<br/>
                                                            • Customers with <strong style={{ color: '#ffc107' }}>Medium surcharge</strong> will have costs increased by {newFuelRates.medium}%<br/>
                                                            • Customers with <strong style={{ color: '#dc3545' }}>High surcharge</strong> will have costs increased by {newFuelRates.high}%<br/>
                                                            • Customers with <strong>No surcharge</strong> pay standard rates
                                                        </p>
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
                                                        <Button
                                                            variant="success"
                                                            onClick={updateFuelSurchargeConfiguration}
                                                            disabled={isLoading}
                                                            style={{ padding: '12px 30px', fontSize: '16px' }}
                                                        >
                                                            {isLoading ? (
                                                                <>🔄 Updating...</>
                                                            ) : (
                                                                <>💾 Save Fuel Rates</>
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setNewFuelRates({
                                                                    low: fuelSurchargeRates.low_percentage,
                                                                    medium: fuelSurchargeRates.medium_percentage,
                                                                    high: fuelSurchargeRates.high_percentage
                                                                });
                                                                setShowFuelConfig(false);
                                                            }}
                                                            disabled={isLoading}
                                                            style={{ padding: '12px 30px', fontSize: '16px' }}
                                                        >
                                                            ❌ Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Company Branding Section */}
                                {isDev && activeTab === 'branding' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Company Branding</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Upload a custom logo to replace the default on all pages. Leave blank to use the default bundled logo.
                                            </p>
                                        </div>

                                        {/* Current logo preview */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa', textAlign: 'center' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Current Logo</strong></h5>
                                            {companyLogo ? (
                                                <>
                                                    <img src={companyLogo} alt="Company logo" style={{ maxHeight: '80px', maxWidth: '300px', objectFit: 'contain', marginBottom: '15px', borderRadius: '6px', background: '#fff', padding: '8px' }} />
                                                    <br />
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        disabled={isLogoUploading}
                                                        onClick={async () => {
                                                            setIsLogoUploading(true);
                                                            try {
                                                                const result = await delete_logo();
                                                                if (result.status === 200) {
                                                                    setCompanyLogo(null);
                                                                    showAlert({ message: 'Logo removed. Default logo restored.', type: 'success', buttons: [{ label: 'OK', type: 'primary' }] });
                                                                } else {
                                                                    showAlert({ message: result.message || 'Failed to remove logo', type: 'danger', buttons: [{ label: 'OK', type: 'primary' }] });
                                                                }
                                                            } catch {
                                                                showAlert({ message: 'Something went wrong', type: 'danger', buttons: [{ label: 'OK', type: 'primary' }] });
                                                            } finally {
                                                                setIsLogoUploading(false);
                                                            }
                                                        }}
                                                    >
                                                        Remove Custom Logo
                                                    </Button>
                                                </>
                                            ) : (
                                                <p style={{ color: '#888', fontStyle: 'italic' }}>Using default bundled logo</p>
                                            )}
                                        </div>

                                        {/* Upload new logo */}
                                        <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Upload New Logo</strong></h5>
                                            <p style={{ color: '#888', fontSize: '13px' }}>PNG, JPG, WebP or SVG — max 2MB. Recommended size: 260×60px.</p>
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                                <div>
                                                    <Form.Label><strong>Image File</strong></Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                        onChange={(e) => setLogoFile(e.target.files[0] || null)}
                                                    />
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    disabled={!logoFile || isLogoUploading}
                                                    onClick={async () => {
                                                        if (!logoFile) return;
                                                        setIsLogoUploading(true);
                                                        try {
                                                            const result = await upload_logo(logoFile);
                                                            if (result.status === 200) {
                                                                setCompanyLogo(result.data.logo_url);
                                                                setLogoFile(null);
                                                                showAlert({ message: 'Logo updated successfully!', type: 'success', buttons: [{ label: 'OK', type: 'primary' }] });
                                                            } else {
                                                                showAlert({ message: result.message || 'Upload failed', type: 'danger', buttons: [{ label: 'OK', type: 'primary' }] });
                                                            }
                                                        } catch {
                                                            showAlert({ message: 'Something went wrong', type: 'danger', buttons: [{ label: 'OK', type: 'primary' }] });
                                                        } finally {
                                                            setIsLogoUploading(false);
                                                        }
                                                    }}
                                                >
                                                    {isLogoUploading ? 'Uploading...' : 'Upload Logo'}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* SOPs & RAMS Section */}
                                {activeTab === 'sops-rams' && (isDev || permissions.perm_notice_board_view) && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>SOPs &amp; RAMS</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Upload and manage Standard Operating Procedures and Risk Assessment &amp; Method Statements
                                            </p>
                                        </div>

                                        {/* Upload Panel */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Upload Document</strong></h5>
                                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                <div>
                                                    <Form.Label><strong>Type</strong></Form.Label>
                                                    <Form.Select
                                                        value={docUploadType}
                                                        onChange={e => setDocUploadType(e.target.value)}
                                                        style={{ width: '120px' }}
                                                    >
                                                        <option value="rams">RAMS</option>
                                                        <option value="sop">SOP</option>
                                                    </Form.Select>
                                                </div>
                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <Form.Label><strong>Display Name (optional)</strong></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g. Manual Handling RAMS"
                                                        value={docUploadName}
                                                        onChange={e => setDocUploadName(e.target.value)}
                                                    />
                                                </div>
                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <Form.Label><strong>PDF File</strong></Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={e => setDocUploadFile(e.target.files[0] || null)}
                                                    />
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    onClick={handleDocUpload}
                                                    disabled={isDocUploading || !docUploadFile}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {isDocUploading ? 'Uploading...' : 'Upload'}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Filter + Document List */}
                                        <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                                                <h5 style={{ margin: 0 }}><strong>Documents</strong></h5>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <Form.Select
                                                        value={docFilterType}
                                                        onChange={e => { setDocFilterType(e.target.value); }}
                                                        style={{ width: '130px' }}
                                                    >
                                                        <option value="all">All</option>
                                                        <option value="rams">RAMS</option>
                                                        <option value="sop">SOP</option>
                                                    </Form.Select>
                                                    <Button variant="outline-secondary" size="sm" onClick={fetchDocuments} disabled={isDocLoading}>
                                                        {isDocLoading ? 'Loading...' : 'Refresh'}
                                                    </Button>
                                                </div>
                                            </div>

                                            {isDocLoading ? (
                                                <div style={{ textAlign: 'center', padding: '30px' }}>
                                                    <ActivityIndicator />
                                                </div>
                                            ) : documents.filter(d => docFilterType === 'all' || d.doc_type.toLowerCase() === docFilterType).length === 0 ? (
                                                <p style={{ textAlign: 'center', color: '#999', padding: '30px 0' }}>No documents found.</p>
                                            ) : (
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                                                            <th style={{ padding: '8px 12px' }}>Name</th>
                                                            <th style={{ padding: '8px 12px', width: '80px' }}>Type</th>
                                                            <th style={{ padding: '8px 12px', width: '160px' }}>Uploaded</th>
                                                            <th style={{ padding: '8px 12px', width: '140px', textAlign: 'right' }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {documents
                                                            .filter(d => docFilterType === 'all' || d.doc_type.toLowerCase() === docFilterType)
                                                            .map((doc, idx) => {
                                                                const displayName = doc.name.replace(/\.pdf$/i, '');
                                                                const uploadedAt = doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB') : '—';
                                                                return (
                                                                    <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                                        <td style={{ padding: '10px 12px' }}>{displayName}</td>
                                                                        <td style={{ padding: '10px 12px' }}>
                                                                            <span style={{
                                                                                padding: '2px 8px',
                                                                                borderRadius: '4px',
                                                                                fontSize: '12px',
                                                                                fontWeight: 'bold',
                                                                                background: doc.doc_type === 'RAMS' ? '#fff3cd' : '#d1ecf1',
                                                                                color: doc.doc_type === 'RAMS' ? '#856404' : '#0c5460'
                                                                            }}>
                                                                                {doc.doc_type}
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>{uploadedAt}</td>
                                                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                                                                                <Button
                                                                                    variant="outline-primary"
                                                                                    size="sm"
                                                                                    onClick={() => handleDocDownload(doc.file_path, displayName)}
                                                                                >
                                                                                    Download
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-success"
                                                                                    size="sm"
                                                                                    title="Copy shareable link — paste into a notice board post"
                                                                                    onClick={() => handleDocCopyLink(doc.public_url)}
                                                                                >
                                                                                    Copy Link
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    title="Download A4 QR code reference sheet"
                                                                                    onClick={() => handleDocQrSheet(doc.file_path, displayName, doc.doc_type)}
                                                                                >
                                                                                    QR
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => handleDocDelete(doc.file_path, displayName)}
                                                                                >
                                                                                    Delete
                                                                                </Button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Test Push Notifications Section */}
                                {activeTab === 'test-notifications' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Test Push Notifications</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>Send a test push notification to all registered devices for a user</p>
                                        </div>

                                        <div style={{ maxWidth: '500px', margin: '0 auto', padding: '24px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <Form>
                                                <Form.Group style={{ marginBottom: '16px' }}>
                                                    <Form.Label><strong>Select User</strong></Form.Label>
                                                    <Form.Select
                                                        value={testNotifProfileId}
                                                        onChange={e => setTestNotifProfileId(e.target.value)}
                                                    >
                                                        <option value=''>-- Choose a user --</option>
                                                        {allUsers.map(user => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.name} {user.surname} ({user.group})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group style={{ marginBottom: '16px' }}>
                                                    <Form.Label><strong>Title</strong></Form.Label>
                                                    <Form.Control
                                                        type='text'
                                                        value={testNotifTitle}
                                                        onChange={e => setTestNotifTitle(e.target.value)}
                                                        placeholder='Notification title'
                                                    />
                                                </Form.Group>

                                                <Form.Group style={{ marginBottom: '24px' }}>
                                                    <Form.Label><strong>Message</strong></Form.Label>
                                                    <Form.Control
                                                        as='textarea'
                                                        rows={3}
                                                        value={testNotifBody}
                                                        onChange={e => setTestNotifBody(e.target.value)}
                                                        placeholder='Notification message body'
                                                    />
                                                </Form.Group>

                                                <Button
                                                    variant='primary'
                                                    disabled={isSendingNotif || !testNotifProfileId || !testNotifBody.trim()}
                                                    onClick={async () => {
                                                        setIsSendingNotif(true);
                                                        setTestNotifResults(null);
                                                        try {
                                                            const { status, data } = await send_test_notification(
                                                                testNotifProfileId,
                                                                testNotifTitle,
                                                                testNotifBody
                                                            );
                                                            if (status === 200) {
                                                                setTestNotifResults(data);
                                                                setTestNotifBody('');
                                                            } else {
                                                                showAlert({
                                                                    message: data?.error || 'Failed to send notification',
                                                                    type: 'danger',
                                                                    buttons: [{ label: 'Close', type: 'danger' }],
                                                                });
                                                            }
                                                        } catch (err) {
                                                            showAlert({
                                                                message: 'An error occurred',
                                                                type: 'danger',
                                                                buttons: [{ label: 'Close', type: 'danger' }],
                                                            });
                                                        } finally {
                                                            setIsSendingNotif(false);
                                                        }
                                                    }}
                                                >
                                                    {isSendingNotif ? 'Sending...' : 'Send Notification'}
                                                </Button>
                                            </Form>

                                            {testNotifResults && (
                                                <div style={{ marginTop: '20px' }}>
                                                    <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{testNotifResults.message}</p>
                                                    {testNotifResults.details?.map((d, i) => (
                                                        <div key={i} style={{
                                                            padding: '8px 12px',
                                                            marginBottom: '6px',
                                                            borderRadius: '4px',
                                                            fontSize: '13px',
                                                            backgroundColor: d.status === 'sent'
                                                                ? (theme === 'dark' ? '#1a3a1a' : '#d4edda')
                                                                : (theme === 'dark' ? '#3a1a1a' : '#f8d7da'),
                                                            color: d.status === 'sent' ? '#155724' : '#721c24',
                                                        }}>
                                                            <strong>{d.status === 'sent' ? '✓' : '✗'} {d.device}</strong>
                                                            {d.status === 'sent'
                                                                ? <div style={{ fontSize: '11px', opacity: 0.7 }}>ID: {d.message_id}</div>
                                                                : <div style={{ fontSize: '11px' }}>{d.error}</div>
                                                            }
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Developer Tools Section */}
                                {/* Privilege Management Section */}
                                {activeTab === 'privilege-management' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                            <h3>Privilege & Capability Management</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Manage Super Admin privilege elevations and configure granular capability switches for all administrators
                                            </p>
                                        </div>

                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
                                            gap: '40px', 
                                            maxWidth: '1200px', 
                                            margin: '0 auto',
                                            padding: '0 15px'
                                        }}>
                                            {/* Left Column: Privilege Elevations */}
                                            <div>
                                                <h4 style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px', fontWeight: '600' }}>
                                                    🔑 Super Admin Elevations
                                                </h4>
                                                
                                                <div style={{ padding: '16px', border: '1px solid #d9534f', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a1a1a' : '#fff8f8', marginBottom: '20px' }}>
                                                    <p style={{ margin: 0, fontSize: '13px', color: theme === 'dark' ? '#f8a0a0' : '#721c24' }}>
                                                        <strong>Warning:</strong> Super admin users have elevated privileges within the application. Only grant this to trusted administrators.
                                                    </p>
                                                </div>

                                                {isPrivilegesLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                                        <ActivityIndicator />
                                                    </div>
                                                ) : adminProfiles.length === 0 ? (
                                                    <p style={{ textAlign: 'center', color: '#999', padding: '30px 0' }}>No admin profiles found.</p>
                                                ) : (
                                                    <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                                            <h5 style={{ margin: 0, fontSize: '15px' }}><strong>Admin Users ({adminProfiles.length})</strong></h5>
                                                            <Button variant='outline-secondary' size='sm' onClick={fetchAdminProfiles} disabled={isPrivilegesLoading}>
                                                                Refresh
                                                            </Button>
                                                        </div>
                                                        {adminProfiles.map(profile => (
                                                            <div
                                                                key={profile.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    padding: '14px 16px',
                                                                    marginBottom: '8px',
                                                                    border: `1px solid ${profile.is_super_admin ? '#28a745' : '#ddd'}`,
                                                                    borderRadius: '6px',
                                                                    backgroundColor: profile.is_super_admin
                                                                        ? (theme === 'dark' ? '#1a3a1a' : '#f0fff0')
                                                                        : (theme === 'dark' ? '#333' : '#fff')
                                                                }}
                                                            >
                                                                <div>
                                                                    <div style={{ fontWeight: '600', fontSize: '15px' }}>
                                                                        {profile.name} {profile.surname}
                                                                        {profile.is_dev && (
                                                                            <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '11px', fontWeight: 'bold', borderRadius: '4px', backgroundColor: '#6f42c1', color: '#fff' }}>DEV</span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>{profile.email}</div>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                    <span style={{
                                                                        padding: '3px 10px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '12px',
                                                                        fontWeight: 'bold',
                                                                        backgroundColor: profile.is_super_admin ? '#28a745' : '#6c757d',
                                                                        color: '#fff'
                                                                    }}>
                                                                        {profile.is_super_admin ? 'Super Admin' : 'Standard'}
                                                                    </span>
                                                                    <Button
                                                                        variant={profile.is_super_admin ? 'outline-danger' : 'outline-success'}
                                                                        size='sm'
                                                                        onClick={() => handleToggleSuperAdmin(profile.id, profile.is_super_admin)}
                                                                    >
                                                                        {profile.is_super_admin ? 'Revoke' : 'Elevate'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right Column: Granular Capability Permissions */}
                                            {isDev && (
                                                <div>
                                                    <h4 style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px', fontWeight: '600' }}>
                                                        🛡️ Granular Capability Permissions
                                                    </h4>

                                                    {permissionsUsers.length === 0 ? (
                                                        <p style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>No permissions users loaded.</p>
                                                    ) : (
                                                        <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff' }}>
                                                            
                                                            {/* Batch actions banner */}
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', padding: '12px', borderRadius: '6px', backgroundColor: theme === 'dark' ? '#333' : '#f8f9fa', borderLeft: '4px solid #0588f3' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '500', color: theme === 'dark' ? '#fff' : '#000' }}>Review and edit capability switches, then batch-save below.</span>
                                                                <Button 
                                                                    variant='primary' 
                                                                    size='sm' 
                                                                    onClick={handleSavePermissions} 
                                                                    disabled={isPermissionsSaving}
                                                                    style={{ backgroundColor: '#0588f3', borderColor: '#0588f3', fontWeight: '600', alignSelf: 'flex-start' }}
                                                                >
                                                                    {isPermissionsSaving ? 'Saving Changes...' : 'Push Permissions Changes'}
                                                                </Button>
                                                            </div>

                                                            {/* Group 1: Super Admins */}
                                                            <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '6px', marginBottom: '12px', fontSize: '14px', fontWeight: '700', color: '#28a745', textAlign: 'left' }}>Super Administrators</h5>
                                                            {permissionsUsers.filter(u => u.is_super_admin && !u.is_dev).length === 0 ? (
                                                                <p style={{ color: '#888', fontStyle: 'italic', fontSize: '13px', marginBottom: '20px', textAlign: 'left' }}>No active super administrators.</p>
                                                            ) : (
                                                                permissionsUsers.filter(u => u.is_super_admin && !u.is_dev).map(user => renderUserPermissionsRow(user))
                                                            )}

                                                            {/* Group 2: Standard Admins */}
                                                            <h5 style={{ borderBottom: '1px solid #eee', paddingBottom: '6px', marginTop: '24px', marginBottom: '12px', fontSize: '14px', fontWeight: '700', color: '#0588f3', textAlign: 'left' }}>Standard Administrators</h5>
                                                            {permissionsUsers.filter(u => !u.is_super_admin && !u.is_dev).length === 0 ? (
                                                                <p style={{ color: '#888', fontStyle: 'italic', fontSize: '13px', textAlign: 'left' }}>No active standard administrators.</p>
                                                            ) : (
                                                                permissionsUsers.filter(u => !u.is_super_admin && !u.is_dev).map(user => renderUserPermissionsRow(user))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {activeTab === 'kpi-settings' && (isDev || permissions.perm_system_settings) && (
                                    <>
                                        <style>{`
                                            /* Hide spin-buttons for Chrome, Safari, Edge, Opera */
                                            .no-spinners::-webkit-outer-spin-button,
                                            .no-spinners::-webkit-inner-spin-button {
                                                -webkit-appearance: none;
                                                margin: 0;
                                            }
                                            /* Hide spin-buttons for Firefox */
                                            .no-spinners {
                                                -moz-appearance: textfield;
                                            }
                                        `}</style>
                                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                            <h3>KPI Dashboard Settings</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Configure visibility and performance target thresholds for the Business Intelligence Dashboard
                                            </p>
                                        </div>

                                        {isActiveKpisLoading ? (
                                            <ActivityIndicator />
                                        ) : (
                                            <div style={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
                                                gap: '40px', 
                                                maxWidth: '1200px', 
                                                margin: '0 auto',
                                                padding: '0 15px'
                                            }}>
                                                {/* Left Column: Visibility Toggles */}
                                                <div>
                                                    <h4 style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px', fontWeight: '600' }}>
                                                        👁️ Card Visibility Toggles
                                                    </h4>
                                                    {ACTIVE_KPI_FLAGS.map(({ key, label, description }) => (
                                                        <div
                                                            key={key}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                padding: '16px 20px',
                                                                marginBottom: '10px',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '8px',
                                                                backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                                            }}
                                                        >
                                                            <div>
                                                                <div style={{ fontWeight: '600' }}>{label}</div>
                                                                <div style={{ fontSize: '0.85rem', color: '#888' }}>{description}</div>
                                                            </div>
                                                            <Form.Check
                                                                type="switch"
                                                                id={`kpi-${key}`}
                                                                checked={activeKpisSettings[key] !== false}
                                                                onChange={() => handleToggleActiveKpi(key, activeKpisSettings[key] !== false)}
                                                                style={{ transform: 'scale(1.3)', transformOrigin: 'right center' }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Right Column: KPI Target Parameters */}
                                                <div>
                                                    <h4 style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px', fontWeight: '600' }}>
                                                        🎯 Performance Targets
                                                    </h4>
                                                    {KPI_TARGET_FIELDS.map(({ key, label, type, step, placeholder, suffix }) => {
                                                        const savedValue = activeKpisSettings[key] !== undefined ? activeKpisSettings[key] : placeholder;
                                                        const editedValue = targetEdits[key] !== undefined ? targetEdits[key] : savedValue;
                                                        const isChanged = editedValue !== savedValue;

                                                        return (
                                                            <div
                                                                key={key}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    padding: '12px 20px',
                                                                    marginBottom: '10px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                                                }}
                                                            >
                                                                <div style={{ flex: 1, marginRight: '15px' }}>
                                                                    <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{label}</div>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                                        <Form.Control
                                                                            type={type}
                                                                            step={step}
                                                                            value={editedValue}
                                                                            className="no-spinners"
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                setTargetEdits(prev => ({ ...prev, [key]: val === '' ? '' : (type === 'number' ? parseFloat(val) : val) }));
                                                                            }}
                                                                            style={{ width: '120px', paddingLeft: '12px', paddingRight: suffix ? '35px' : '12px', textAlign: 'left', background: theme === 'dark' ? '#333' : '#fff', color: theme === 'dark' ? '#fff' : '#000', border: '1px solid ' + (theme === 'dark' ? '#555' : '#ccc') }}
                                                                        />
                                                                        {suffix && (
                                                                            <span style={{ position: 'absolute', right: '10px', color: '#888', pointerEvents: 'none', fontSize: '0.85rem' }}>
                                                                                {suffix}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Custom Increment/Decrement Arrows outside the box */}
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '4px' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleAdjustValue(key, step, 'up')}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: theme === 'dark' ? '#007bff' : '#0056b3',
                                                                                fontSize: '9px',
                                                                                lineHeight: 1,
                                                                                padding: '2px 4px',
                                                                                cursor: 'pointer',
                                                                                borderRadius: '3px',
                                                                                backgroundColor: theme === 'dark' ? '#333' : '#e2e8f0'
                                                                            }}
                                                                        >
                                                                            ▲
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleAdjustValue(key, step, 'down')}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: theme === 'dark' ? '#007bff' : '#0056b3',
                                                                                fontSize: '9px',
                                                                                lineHeight: 1,
                                                                                padding: '2px 4px',
                                                                                cursor: 'pointer',
                                                                                borderRadius: '3px',
                                                                                backgroundColor: theme === 'dark' ? '#333' : '#e2e8f0'
                                                                            }}
                                                                        >
                                                                            ▼
                                                                        </button>
                                                                    </div>

                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        disabled={!isChanged || editedValue === ''}
                                                                        onClick={() => handleUpdateTarget(key, editedValue)}
                                                                        style={{ minWidth: '60px' }}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'company-vars' && (isDev || permissions.perm_timesheets_edit) && features.timesheets !== false && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Timesheet Settings</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Configure holiday hours and the permitted clock-in/out areas
                                            </p>
                                        </div>

                                        {isCompanyVarsLoading ? (
                                            <div style={{ textAlign: 'center', padding: '40px' }}><ActivityIndicator /></div>
                                        ) : (
                                            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                                            <div style={{ padding: '24px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa', marginBottom: '24px' }}>
                                                <h4>Holiday Hours</h4>
                                                <div style={{ marginBottom: '24px', padding: '16px', border: '2px solid #007bff', borderRadius: '8px', textAlign: 'center', backgroundColor: theme === 'dark' ? '#1a1a2e' : '#f0f8ff' }}>
                                                    <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>Current Contract Weekly Hours</div>
                                                    <div style={{ fontSize: '40px', fontWeight: 'bold', color: '#007bff' }}>{companyVars.contract_weekly_hours}</div>
                                                    <div style={{ fontSize: '12px', color: '#888' }}>hours per week</div>
                                                </div>

                                                <Form.Group style={{ marginBottom: '20px' }}>
                                                    <Form.Label><strong>Contract Weekly Hours</strong></Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        max={60}
                                                        value={companyVarsInput}
                                                        onChange={e => setCompanyVarsInput(e.target.value)}
                                                    />
                                                    <Form.Text className="text-muted">Whole number between 0 and 60.</Form.Text>
                                                </Form.Group>

                                                <Button
                                                    variant="primary"
                                                    onClick={handleUpdateCompanyVars}
                                                    disabled={isCompanyVarsLoading}
                                                >
                                                    {isCompanyVarsLoading ? 'Saving...' : 'Save Changes'}
                                                </Button>
                                            </div>
                                            <div style={{ padding: '24px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                                <h4>Clock-in/out Geofences</h4>
                                                <p className="text-muted">Staff may clock in or out inside any active area. Coordinates mark the centre and the radius is measured in metres.</p>

                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group style={{ marginBottom: '14px' }}>
                                                            <Form.Label>Area name</Form.Label>
                                                            <Form.Control value={depotLocationForm.name} onChange={e => handleDepotLocationChange('name', e.target.value)} placeholder="Main yard" />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group style={{ marginBottom: '14px' }}>
                                                            <Form.Label>Latitude</Form.Label>
                                                            <Form.Control type="number" step="any" value={depotLocationForm.lat} onChange={e => handleDepotLocationChange('lat', e.target.value)} placeholder="51.5074" />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Group style={{ marginBottom: '14px' }}>
                                                            <Form.Label>Longitude</Form.Label>
                                                            <Form.Control type="number" step="any" value={depotLocationForm.lng} onChange={e => handleDepotLocationChange('lng', e.target.value)} placeholder="-0.1278" />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col md={4}>
                                                        <Form.Group style={{ marginBottom: '14px' }}>
                                                            <Form.Label>Radius (metres)</Form.Label>
                                                            <Form.Control type="number" min={1} max={10000} step={1} value={depotLocationForm.radius} onChange={e => handleDepotLocationChange('radius', e.target.value)} />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Check
                                                            type="switch"
                                                            label="Active"
                                                            checked={depotLocationForm.active}
                                                            onChange={e => handleDepotLocationChange('active', e.target.checked)}
                                                            style={{ marginBottom: '20px' }}
                                                        />
                                                    </Col>
                                                    <Col md={5} style={{ marginBottom: '14px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                        {depotLocationForm.id && (
                                                            <Button variant="secondary" onClick={() => setDepotLocationForm(emptyDepotLocationForm)} disabled={isDepotLocationsLoading}>Cancel</Button>
                                                        )}
                                                        <Button variant="primary" onClick={handleSaveDepotLocation} disabled={isDepotLocationsLoading}>
                                                            {depotLocationForm.id ? 'Update Area' : 'Add Area'}
                                                        </Button>
                                                    </Col>
                                                </Row>

                                                <hr />
                                                {isDepotLocationsLoading && depotLocations.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: '20px' }}><ActivityIndicator /></div>
                                                ) : depotLocations.length === 0 ? (
                                                    <p className="text-muted">No geofence areas are configured.</p>
                                                ) : (
                                                    depotLocations.map(location => (
                                                        <div key={location.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`, opacity: location.active === false ? 0.6 : 1 }}>
                                                            <div>
                                                                <strong>{location.name}</strong> {location.active === false && <span className="text-muted">(inactive)</span>}
                                                                <div className="text-muted" style={{ fontSize: '13px' }}>{location.lat}, {location.lng} · {location.radius} m radius</div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                                                <Button size="sm" variant="outline-primary" onClick={() => handleEditDepotLocation(location)} disabled={isDepotLocationsLoading}>Edit</Button>
                                                                {location.active !== false && (
                                                                    <Button size="sm" variant="outline-danger" onClick={() => handleDeactivateDepotLocation(location)} disabled={isDepotLocationsLoading}>Deactivate</Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'invoice-settings' && (isDev || permissions.perm_invoice_edit) && (
                                    <InvoiceSettingsPanel showAlert={showAlert} />
                                )}

                                {/* ============================================================ */}
                                {/* Backup Config Tab (dev only)                                 */}
                                {/* ============================================================ */}
                                {activeTab === 'backup-config' && (isDev || isManager) && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Backup Configuration</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Configure nightly incremental (Mon–Sat 1am) and weekly full backup (Sun 1am) to your local backup server.
                                            </p>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

                                            {/* ── LEFT COLUMN: config / run ─────────────────────── */}
                                            <div>

                                            {/* Status panel */}
                                            <div style={{ marginBottom: '24px', padding: '16px', border: `2px solid ${backupEnabled ? '#28a745' : '#6c757d'}`, borderRadius: '8px', backgroundColor: theme === 'dark' ? '#1a1a2e' : '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: backupEnabled ? '#28a745' : '#6c757d' }}>{backupEnabled ? 'Backups Enabled' : 'Backups Disabled'}</div>
                                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                                        Last incremental: {backupLastIncremental ? new Date(backupLastIncremental).toLocaleString('en-GB') : 'Never'}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#888' }}>
                                                        Last full backup: {backupLastFull ? new Date(backupLastFull).toLocaleString('en-GB') : 'Never'}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    {['Yes', 'No'].map(opt => {
                                                        const val = opt === 'Yes';
                                                        return (
                                                            <button key={opt} onClick={() => isDev && setBackupEnabled(val)} disabled={!isDev} style={{ padding: '6px 14px', fontSize: '13px', borderRadius: '4px', border: 'none', cursor: isDev ? 'pointer' : 'not-allowed', opacity: isDev ? 1 : 0.5, backgroundColor: backupEnabled === val ? (val ? '#28a745' : '#dc3545') : (theme === 'dark' ? '#444' : '#e9ecef'), color: backupEnabled === val ? '#fff' : (theme === 'dark' ? '#ccc' : '#333'), fontWeight: backupEnabled === val ? 'bold' : 'normal' }}>
                                                                {opt}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Config form */}
                                            <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff', marginBottom: '20px' }}>
                                                <h6 style={{ marginBottom: '16px', fontWeight: 'bold' }}>Server Details</h6>

                                                <Form.Group style={{ marginBottom: '14px' }}>
                                                    <Form.Label><strong>Host / IP Address</strong></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g. 192.168.1.50"
                                                        value={backupHost}
                                                        onChange={e => setBackupHost(e.target.value)}
                                                        disabled={!isDev}
                                                    />
                                                    <Form.Text className="text-muted">IP address or hostname of your local backup server.</Form.Text>
                                                </Form.Group>

                                                <Form.Group style={{ marginBottom: '14px' }}>
                                                    <Form.Label><strong>Port</strong></Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min={1} max={65535}
                                                        value={backupPort}
                                                        onChange={e => setBackupPort(e.target.value)}
                                                        disabled={!isDev}
                                                    />
                                                    <Form.Text className="text-muted">Default: 8765</Form.Text>
                                                </Form.Group>

                                                <Form.Group style={{ marginBottom: '20px' }}>
                                                    <Form.Label><strong>API Key</strong></Form.Label>
                                                    {backupApiKeySet && backupApiKey === '' && (
                                                        <div style={{ marginBottom: '6px', fontSize: '13px', color: '#28a745' }}>✓ Key saved — enter a new key below to replace it</div>
                                                    )}
                                                    <Form.Control
                                                        type="password"
                                                        placeholder={backupApiKeySet ? 'Leave blank to keep existing key' : 'Enter API key'}
                                                        value={backupApiKey}
                                                        onChange={e => setBackupApiKey(e.target.value)}
                                                        autoComplete="new-password"
                                                        disabled={!isDev}
                                                    />
                                                    <Form.Text className="text-muted">Must match the <code>--key</code> argument on your backup server.</Form.Text>
                                                </Form.Group>

                                                <Button
                                                    variant="primary"
                                                    onClick={async () => {
                                                        setIsBackupSaving(true);
                                                        const r = await update_backup_config({
                                                            backup_host: backupHost,
                                                            backup_port: parseInt(backupPort, 10),
                                                            backup_enabled: backupEnabled,
                                                            backup_api_key: backupApiKey,
                                                        });
                                                        setIsBackupSaving(false);
                                                        if (r.status === 200) {
                                                            showAlert({ message: 'Backup configuration saved.', type: 'success', buttons: [{ label: 'Close', type: 'success' }] });
                                                            if (backupApiKey) setBackupApiKeySet(true);
                                                            setBackupApiKey('');
                                                        } else {
                                                            showAlert({ message: r.data?.error || 'Failed to save', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
                                                        }
                                                    }}
                                                    disabled={isBackupSaving || !isDev}
                                                >
                                                    {isBackupSaving ? 'Saving...' : 'Save Configuration'}
                                                </Button>
                                            </div>

                                            {/* Test connection */}
                                            <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff', marginBottom: '20px' }}>
                                                <h6 style={{ marginBottom: '12px', fontWeight: 'bold' }}>Test Connection</h6>
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={async () => {
                                                            setIsTesting(true);
                                                            setTestResult(null);
                                                            // Only send the key if the user has typed a new one; otherwise the backend uses the stored key
                                                            const r = await test_backup_connection(backupHost, parseInt(backupPort, 10), backupApiKey || null);
                                                            setTestResult(r);
                                                            setIsTesting(false);
                                                        }}
                                                        disabled={isTesting || !backupHost || (!backupApiKeySet && !backupApiKey)}
                                                    >
                                                        {isTesting ? 'Testing...' : 'Test Connection'}
                                                    </Button>
                                                    {testResult && (
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: testResult.success ? '#28a745' : '#dc3545', fontSize: '14px' }}>
                                                            <FontAwesomeIcon icon={testResult.success ? faCircleCheck : faCircleXmark} style={{ width: 16 }} />
                                                            {testResult.message}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Manual run */}
                                            <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff', marginBottom: '20px' }}>
                                                <h6 style={{ marginBottom: '12px', fontWeight: 'bold' }}>Run Backup Now</h6>
                                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={async () => {
                                                            setIsRunningBackup(true);
                                                            setBackupRunResult(null);
                                                            const r = await run_backup_now('incremental');
                                                            setBackupRunResult({ success: r.status === 202, message: r.data?.message || r.data?.error, mode: 'incremental' });
                                                            setIsRunningBackup(false);
                                                        }}
                                                        disabled={isRunningBackup || !isDev}
                                                    >
                                                        <FontAwesomeIcon icon={faPlay} style={{ width: 14, marginRight: '6px' }} />
                                                        {isRunningBackup ? 'Starting...' : 'Run Incremental'}
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        onClick={async () => {
                                                            setIsRunningBackup(true);
                                                            setBackupRunResult(null);
                                                            const r = await run_backup_now('full');
                                                            setBackupRunResult({ success: r.status === 202, message: r.data?.message || r.data?.error, mode: 'full' });
                                                            setIsRunningBackup(false);
                                                        }}
                                                        disabled={isRunningBackup || !isDev}
                                                    >
                                                        <FontAwesomeIcon icon={faPlay} style={{ width: 14, marginRight: '6px' }} />
                                                        {isRunningBackup ? 'Starting...' : 'Run Full Backup'}
                                                    </Button>
                                                </div>
                                                {backupRunResult && (
                                                    <div style={{ fontSize: '13px', padding: '10px', borderRadius: '6px', backgroundColor: backupRunResult.success ? (theme === 'dark' ? '#1a3a1a' : '#d4edda') : (theme === 'dark' ? '#3a1a1a' : '#f8d7da'), color: backupRunResult.success ? '#155724' : '#721c24' }}>
                                                        {backupRunResult.success
                                                            ? `✓ ${backupRunResult.message}`
                                                            : `✗ ${backupRunResult.message || 'Backup failed'}`
                                                        }
                                                    </div>
                                                )}
                                            </div>

                                            </div>{/* end left column */}

                                            {/* ── RIGHT COLUMN: restore + stats ─────────────────── */}
                                            <div>

                                            {/* ── Restore from backup ────────────────────────────── */}
                                            <div style={{ padding: '20px', border: '2px solid #dc3545', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a1a1a' : '#fff5f5', marginBottom: '20px', opacity: isDev ? 1 : 0.5, pointerEvents: isDev ? 'auto' : 'none' }}>
                                                <h6 style={{ marginBottom: '4px', fontWeight: 'bold', color: '#dc3545', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ width: 20, marginRight: '6px' }} />
                                                    Restore from Backup
                                                </h6>
                                                <p style={{ fontSize: '12px', color: theme === 'dark' ? '#ff9999' : '#721c24', marginBottom: '16px' }}>
                                                    <strong>DEV ONLY.</strong> Pushes files from the backup server back into Supabase storage.
                                                    Default mode skips files that already exist — enable "Overwrite" only if you intend to replace live data.
                                                </p>

                                                {/* Password */}
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '14px', flexWrap: 'wrap' }}>
                                                    <div style={{ flex: 1, minWidth: '240px' }}>
                                                        <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Restore Password</Form.Label>
                                                        <Form.Control
                                                            type="password"
                                                            placeholder="BACKUP_RESTORE_PASSWORD"
                                                            value={restorePassword}
                                                            onChange={e => { setRestorePassword(e.target.value); setRestoreBuckets([]); setRestoreFiles(null); setRestoreResult(null); }}
                                                            size="sm"
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        disabled={!restorePassword || isRestoreLoading}
                                                        onClick={async () => {
                                                            setIsRestoreLoading(true);
                                                            setRestoreFiles(null);
                                                            setRestoreResult(null);
                                                            const r = await restore_list_buckets(restorePassword);
                                                            if (r.status === 200) {
                                                                setRestoreBuckets(r.data.buckets || []);
                                                            } else {
                                                                showAlert({ message: r.data?.error || 'Wrong password or backup server unreachable', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
                                                            }
                                                            setIsRestoreLoading(false);
                                                        }}
                                                    >
                                                        {isRestoreLoading ? 'Loading...' : 'Unlock & List Buckets'}
                                                    </Button>
                                                </div>

                                                {/* Bucket list + browse */}
                                                {restoreBuckets.length > 0 && (
                                                    <div style={{ marginBottom: '14px' }}>
                                                        <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Target</Form.Label>
                                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                                            <Button
                                                                size="sm"
                                                                variant={restoreSelectedBucket === null ? 'danger' : 'outline-secondary'}
                                                                onClick={() => { setRestoreSelectedBucket(null); setRestoreFiles(null); }}
                                                            >
                                                                All Buckets ({restoreBuckets.reduce((s, b) => s + b.file_count, 0).toLocaleString()} files)
                                                            </Button>
                                                            {restoreBuckets.map(b => (
                                                                <Button
                                                                    key={b.name}
                                                                    size="sm"
                                                                    variant={restoreSelectedBucket === b.name ? 'danger' : 'outline-secondary'}
                                                                    onClick={() => { setRestoreSelectedBucket(b.name); setRestoreFiles(null); }}
                                                                >
                                                                    {b.name} ({b.file_count.toLocaleString()})
                                                                </Button>
                                                            ))}
                                                        </div>

                                                        {/* Browse files in selected bucket */}
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            disabled={isRestoreLoading}
                                                            onClick={async () => {
                                                                setIsRestoreLoading(true);
                                                                const r = await restore_browse_files(restorePassword, restoreSelectedBucket);
                                                                if (r.status === 200) setRestoreFiles(r.data);
                                                                else showAlert({ message: r.data?.error || 'Could not list files', type: 'warning', buttons: [{ label: 'Close', type: 'warning' }] });
                                                                setIsRestoreLoading(false);
                                                            }}
                                                        >
                                                            {isRestoreLoading ? 'Loading...' : `Browse files${restoreSelectedBucket ? ` in ${restoreSelectedBucket}` : ' (all)'}`}
                                                        </Button>

                                                        {restoreFiles && (
                                                            <div style={{ marginTop: '10px', maxHeight: '180px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', padding: '8px', fontSize: '11px', fontFamily: 'monospace', backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f8f8' }}>
                                                                <div style={{ marginBottom: '4px', fontWeight: 'bold', fontSize: '12px', fontFamily: 'inherit' }}>{restoreFiles.total?.toLocaleString()} files</div>
                                                                {restoreFiles.files?.slice(0, 200).map((f, i) => (
                                                                    <div key={i} style={{ color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                        <span style={{ color: theme === 'dark' ? '#aaa' : '#333' }}>{f.bucket}/</span>{f.path}
                                                                        <span style={{ color: '#aaa', marginLeft: '6px' }}>{(f.size_bytes / 1024).toFixed(1)}KB</span>
                                                                    </div>
                                                                ))}
                                                                {restoreFiles.total > 200 && <div style={{ color: '#aaa', marginTop: '4px' }}>… and {(restoreFiles.total - 200).toLocaleString()} more</div>}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Overwrite toggle + run */}
                                                {restoreBuckets.length > 0 && (
                                                    <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', paddingTop: '10px', borderTop: '1px solid #f5c6cb' }}>
                                                        <Form.Check
                                                            type="switch"
                                                            id="restore-overwrite"
                                                            label={<span style={{ fontSize: '13px', color: restoreOverwrite ? '#dc3545' : 'inherit' }}><strong>Overwrite</strong> existing files</span>}
                                                            checked={restoreOverwrite}
                                                            onChange={e => setRestoreOverwrite(e.target.checked)}
                                                        />
                                                        {!restoreConfirmPending ? (
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                disabled={isRestoreLoading}
                                                                onClick={() => setRestoreConfirmPending(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faTriangleExclamation} style={{ width: 16, marginRight: '6px' }} />
                                                                {restoreSelectedBucket ? `Restore "${restoreSelectedBucket}"` : 'Restore ALL Buckets'}
                                                            </Button>
                                                        ) : (
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 'bold' }}>Are you sure?</span>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    disabled={isRestoreLoading}
                                                                    onClick={async () => {
                                                                        setRestoreConfirmPending(false);
                                                                        setIsRestoreLoading(true);
                                                                        setRestoreResult(null);
                                                                        const buckets = restoreSelectedBucket ? [restoreSelectedBucket] : null;
                                                                        const r = await run_restore(restorePassword, buckets, restoreOverwrite);
                                                                        setRestoreResult({ success: r.status === 202, message: r.data?.message || r.data?.error });
                                                                        setIsRestoreLoading(false);
                                                                    }}
                                                                >
                                                                    Yes, Restore
                                                                </Button>
                                                                <Button variant="outline-secondary" size="sm" onClick={() => setRestoreConfirmPending(false)}>Cancel</Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {restoreResult && (
                                                    <div style={{ marginTop: '12px', fontSize: '13px', padding: '10px', borderRadius: '6px', backgroundColor: restoreResult.success ? (theme === 'dark' ? '#1a3a1a' : '#d4edda') : (theme === 'dark' ? '#3a1a1a' : '#f8d7da'), color: restoreResult.success ? '#155724' : '#721c24' }}>
                                                        {restoreResult.success ? `✓ ${restoreResult.message}` : `✗ ${restoreResult.message || 'Restore failed'}`}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Backup server stats */}
                                            <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <h6 style={{ margin: 0, fontWeight: 'bold' }}>Backup Server Storage</h6>
                                                    <Button variant="outline-secondary" size="sm" onClick={async () => {
                                                        const r = await get_backup_stats();
                                                        if (r.status === 200) setBackupStats(r.data);
                                                        else showAlert({ message: r.data?.error || 'Could not fetch stats', type: 'warning', buttons: [{ label: 'Close', type: 'warning' }] });
                                                    }}>Refresh Stats</Button>
                                                </div>
                                                {backupStats ? (
                                                    <>
                                                        <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                                            <strong>{backupStats.total_files?.toLocaleString()}</strong> files &nbsp;·&nbsp; <strong>{backupStats.total_size_gb} GB</strong> total
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                            {Object.entries(backupStats.buckets || {}).map(([bucket, info]) => (
                                                                <div key={bucket} style={{ padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '12px' }}>
                                                                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{bucket}</div>
                                                                    <div style={{ color: '#666' }}>{info.files?.toLocaleString()} files · {info.size_mb} MB</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>Click "Refresh Stats" to see what's stored on the backup server.</p>
                                                )}
                                            </div>

                                            {/* Backup run logs */}
                                            <div style={{ marginTop: '16px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <h6 style={{ margin: 0, fontWeight: 'bold' }}>Backup Logs (Last 7 Days)</h6>
                                                    <Button variant="outline-secondary" size="sm" disabled={backupRunsLoading} onClick={async () => {
                                                        setBackupRunsLoading(true);
                                                        const r = await get_backup_runs(7);
                                                        setBackupRunsLoading(false);
                                                        if (r.status === 200) setBackupRuns(r.data);
                                                        else showAlert({ message: r.data?.error || 'Could not fetch logs', type: 'warning', buttons: [{ label: 'Close', type: 'warning' }] });
                                                    }}>{backupRunsLoading ? 'Loading…' : 'Refresh Logs'}</Button>
                                                </div>
                                                {backupRuns === null ? (
                                                    <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>Click "Refresh Logs" to load recent backup history.</p>
                                                ) : backupRuns.length === 0 ? (
                                                    <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>No backup runs in the last 7 days.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                        {backupRuns.map(run => {
                                                            const isExpanded = expandedRunId === run.id;
                                                            const statusColor = run.status === 'success' ? '#28a745' : run.status === 'partial' ? '#fd7e14' : run.status === 'running' ? '#007bff' : '#dc3545';
                                                            const statusBg = theme === 'dark'
                                                                ? (run.status === 'success' ? '#1a3a1a' : run.status === 'partial' ? '#3a2a10' : run.status === 'running' ? '#0d1f3a' : '#3a1a1a')
                                                                : (run.status === 'success' ? '#f0fff4' : run.status === 'partial' ? '#fff8f0' : run.status === 'running' ? '#f0f4ff' : '#fff0f0');
                                                            const startedAt = new Date(run.started_at);
                                                            const completedAt = run.completed_at ? new Date(run.completed_at) : null;
                                                            const durationSec = completedAt ? Math.round((completedAt - startedAt) / 1000) : null;
                                                            const durationStr = durationSec !== null ? (durationSec >= 60 ? `${Math.floor(durationSec / 60)}m ${durationSec % 60}s` : `${durationSec}s`) : '—';
                                                            return (
                                                                <div key={run.id} style={{ border: `1px solid ${statusColor}40`, borderRadius: '6px', overflow: 'hidden' }}>
                                                                    <div
                                                                        onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                                                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: statusBg, cursor: 'pointer', userSelect: 'none' }}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                                                                            <span style={{ fontWeight: 'bold', color: statusColor, textTransform: 'uppercase', fontSize: '11px', minWidth: '60px' }}>{run.status}</span>
                                                                            <span style={{ fontWeight: '600' }}>{run.mode}</span>
                                                                            <span style={{ color: '#888' }}>{startedAt.toLocaleDateString()} {startedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                        </div>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#888' }}>
                                                                            {run.total_files != null && <span>↑ {run.success_count}/{run.total_files} files</span>}
                                                                            {run.fail_count > 0 && <span style={{ color: '#dc3545' }}>✗ {run.fail_count} failed</span>}
                                                                            <span>{durationStr}</span>
                                                                            <span style={{ fontSize: '10px' }}>{isExpanded ? '▲' : '▼'}</span>
                                                                        </div>
                                                                    </div>
                                                                    {isExpanded && (
                                                                        <div style={{ padding: '12px 14px', fontSize: '12px', borderTop: `1px solid ${statusColor}30` }}>
                                                                            {run.error_message && (
                                                                                <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: theme === 'dark' ? '#3a1a1a' : '#fff0f0', borderRadius: '4px', color: '#dc3545' }}>
                                                                                    <strong>Error:</strong> {run.error_message}
                                                                                </div>
                                                                            )}
                                                                            {run.bucket_summary && Object.keys(run.bucket_summary).length > 0 ? (
                                                                                <>
                                                                                    <div style={{ fontWeight: '600', marginBottom: '6px', color: '#888' }}>Bucket Summary</div>
                                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                                                                        {Object.entries(run.bucket_summary).map(([bucket, counts]) => (
                                                                                            <div key={bucket} style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: theme === 'dark' ? '#1e1e1e' : '#f8f9fa' }}>
                                                                                                <div style={{ fontWeight: '600', marginBottom: '2px' }}>{bucket}</div>
                                                                                                <div style={{ color: '#28a745' }}>✓ {counts.success} uploaded</div>
                                                                                                {counts.fail > 0 && <div style={{ color: '#dc3545' }}>✗ {counts.fail} failed</div>}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <span style={{ color: '#888' }}>No bucket detail available.</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            </div>{/* end right column */}

                                        </div>{/* end 2-col grid */}
                                    </>
                                )}

                                {activeTab === 'feature-flags' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Feature Flags</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Control which features are enabled for the company
                                            </p>
                                        </div>

                                        {isFeaturesLoading ? (
                                            <ActivityIndicator />
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                {FEATURE_FLAGS.map(({ key, label, description }) => (
                                                    <div
                                                        key={key}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '16px 20px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '8px',
                                                            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                                        }}
                                                    >
                                                        <div>
                                                            <div style={{ fontWeight: '600' }}>{label}</div>
                                                            <div style={{ fontSize: '0.85rem', color: '#888' }}>{description}</div>
                                                        </div>
                                                        <Form.Check
                                                            type="switch"
                                                            id={`feature-${key}`}
                                                            checked={featureToggles[key] === true}
                                                            onChange={() => handleToggleFeature(key, featureToggles[key])}
                                                            style={{ transform: 'scale(1.3)', transformOrigin: 'right center', marginLeft: '12px' }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'integrations' && isDev && (
                                    <IntegrationsManager theme={theme} showAlert={showAlert} />
                                )}

                                {activeTab === 'driver-bonus' && (isDev || isManager) && (
                                    <DriverBonusPanel theme={theme} />
                                )}

                                {activeTab === 'developer-tools' && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Developer Tools</h3>
                                            <p style={{ color: '#666', fontStyle: 'italic' }}>
                                                Administrative functions for system maintenance
                                            </p>
                                        </div>

                                        {/* Bulk QR Code Regeneration */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px', color: '#d9534f' }}>
                                                <strong>⚠️ Bulk QR Code Regeneration</strong>
                                            </h5>
                                            <p style={{ marginBottom: '20px', color: '#666' }}>
                                                This will regenerate QR codes for all items in the selected table. Use with caution as this affects all existing QR codes.
                                            </p>
                                            
                                            <div style={{ marginBottom: '20px' }}>
                                                <Form.Label><strong>Select Table Type:</strong></Form.Label>
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                    <Button
                                                        variant={selectedQRTable === 'pallets' ? 'primary' : 'outline-primary'}
                                                        onClick={() => setSelectedQRTable('pallets')}
                                                        disabled={isLoading}
                                                    >
                                                        Pallets
                                                    </Button>
                                                    <Button
                                                        variant={selectedQRTable === 'products' ? 'primary' : 'outline-primary'}
                                                        onClick={() => setSelectedQRTable('products')}
                                                        disabled={isLoading}
                                                    >
                                                        Products
                                                    </Button>
                                                </div>
                                            </div>



                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Button
                                                    variant="danger"
                                                    onClick={handleBulkQRRegeneration}
                                                    disabled={isLoading || !selectedQRTable}
                                                >
                                                    {isQRProcessing ? (
                                                        <>
                                                            <span style={{ marginRight: '8px' }}>🔄</span>
                                                            Processing {qrProgress.current}/{qrProgress.total}...
                                                        </>
                                                    ) : isLoading ? (
                                                        'Starting...'
                                                    ) : (
                                                        `Regenerate All ${selectedQRTable} QR Codes`
                                                    )}
                                                </Button>
                                                <small style={{ color: '#666', fontStyle: 'italic' }}>
                                                    Selected: {selectedQRTable}
                                                </small>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* ============================================================ */}
                                {/* Payslips Tab                                                  */}
                                {/* ============================================================ */}
                                {activeTab === 'payslips' && (isDev || permissions.perm_timesheets_view) && (
                                    <>
                                        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                            <h3>Payslips</h3>
                                        </div>

                                        {/* Upload new payslip */}
                                        <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa' }}>
                                            <h5 style={{ marginBottom: '15px' }}><strong>Upload New Payslip</strong></h5>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group style={{ marginBottom: '12px' }}>
                                                        <Form.Label><strong>Driver</strong></Form.Label>
                                                        <div style={{ position: 'relative' }}>
                                                            <input
                                                                type="text"
                                                                placeholder="Search driver..."
                                                                autoComplete="off"
                                                                value={payslipUploadDriverSearch}
                                                                onChange={e => {
                                                                    setPayslipUploadDriverSearch(e.target.value);
                                                                    setPayslipDriverId('');
                                                                    setPayslipUploadDriverDropdownOpen(true);
                                                                }}
                                                                onFocus={() => setPayslipUploadDriverDropdownOpen(true)}
                                                                onBlur={() => setTimeout(() => setPayslipUploadDriverDropdownOpen(false), 150)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 12px',
                                                                    fontSize: '14px',
                                                                    border: `1px solid ${payslipDriverId ? '#0588f3' : (theme === 'dark' ? '#555' : '#ced4da')}`,
                                                                    borderRadius: '4px',
                                                                    outline: 'none',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
                                                                    color: theme === 'dark' ? '#e0e0e0' : '#212529',
                                                                }}
                                                            />
                                                            {payslipUploadDriverDropdownOpen && (
                                                                <ul style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    maxHeight: 220,
                                                                    overflowY: 'auto',
                                                                    margin: 0,
                                                                    padding: 0,
                                                                    listStyle: 'none',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: 4,
                                                                    backgroundColor: theme === 'dark' ? '#1e2535' : '#fff',
                                                                    zIndex: 999,
                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                                }}>
                                                                    {(() => {
                                                                        const filtered = allUsers
                                                                            .filter(u => u.status === 'active' && (u.group === 'ADMIN' || u.group === 'DRIVER'))
                                                                            .filter(u => payslipUploadDriverSearch
                                                                                ? `${u.name} ${u.surname}`.toLowerCase().includes(payslipUploadDriverSearch.toLowerCase())
                                                                                : true
                                                                            );
                                                                        if (filtered.length === 0) {
                                                                            return <li style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>No matches</li>;
                                                                        }
                                                                        return filtered.map(u => (
                                                                            <li
                                                                                key={u.id}
                                                                                onMouseDown={() => {
                                                                                    setPayslipDriverId(u.id);
                                                                                    setPayslipUploadDriverSearch(`${u.name} ${u.surname}`);
                                                                                    setPayslipUploadDriverDropdownOpen(false);
                                                                                }}
                                                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(5,136,243,0.1)'}
                                                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = u.id === payslipDriverId ? 'rgba(5,136,243,0.15)' : ''}
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: 13,
                                                                                    backgroundColor: u.id === payslipDriverId ? 'rgba(5,136,243,0.15)' : undefined,
                                                                                }}
                                                                            >
                                                                                {u.name} {u.surname} <span style={{ color: '#888', fontSize: 11 }}>[{u.group}]</span>
                                                                            </li>
                                                                        ));
                                                                    })()}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group style={{ marginBottom: '12px' }}>
                                                        <Form.Label><strong>Pay Date</strong></Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={payslipPayDate}
                                                            onChange={e => setPayslipPayDate(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group style={{ marginBottom: '12px' }}>
                                                        <Form.Label><strong>Display Name</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="e.g. Week 22 Payslip"
                                                            value={payslipDisplayName}
                                                            onChange={e => setPayslipDisplayName(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group style={{ marginBottom: '12px' }}>
                                                        <Form.Label><strong>PDF File</strong></Form.Label>
                                                        <Form.Control
                                                            type="file"
                                                            accept=".pdf"
                                                            onChange={e => setPayslipFile(e.target.files[0] || null)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Button
                                                variant="primary"
                                                onClick={handleUploadPayslip}
                                                disabled={isPayslipUploading}
                                                style={{ marginTop: '8px' }}
                                            >
                                                {isPayslipUploading ? 'Uploading...' : 'Upload Payslip'}
                                            </Button>
                                        </div>

                                        {/* Folder navigation */}
                                        <div>
                                            {/* Breadcrumb */}
                                            {payslipNavLevel !== 'years' && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', fontSize: '14px' }}>
                                                    <span
                                                        style={{ cursor: 'pointer', color: '#1997eb' }}
                                                        onClick={fetchPayslipYears}
                                                    >
                                                        📁 All Years
                                                    </span>
                                                    {payslipNavLevel === 'months' && (
                                                        <span style={{ color: '#888' }}> / {payslipNavYear}</span>
                                                    )}
                                                    {payslipNavLevel === 'files' && (
                                                        <>
                                                            <span style={{ color: '#888' }}> / </span>
                                                            <span
                                                                style={{ cursor: 'pointer', color: '#1997eb' }}
                                                                onClick={() => { setPayslipNavLevel('months'); setPayslipNavMonth(null); }}
                                                            >
                                                                {payslipNavYear}
                                                            </span>
                                                            <span style={{ color: '#888' }}> / {MONTH_NAMES[(payslipNavMonth || 1) - 1]}</span>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {/* Year folders */}
                                            {payslipNavLevel === 'years' && (
                                                isPayslipPeriodsLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '40px' }}><ActivityIndicator /></div>
                                                ) : payslipYears.length === 0 ? (
                                                    <p style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: '30px' }}>No payslips have been uploaded yet.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                        {payslipYears.map(year => (
                                                            <div
                                                                key={year}
                                                                onClick={() => fetchPayslipMonths(year)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    padding: '20px 28px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                                                    textAlign: 'center',
                                                                    minWidth: '100px',
                                                                    transition: 'background 0.15s',
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#e9ecef'}
                                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f8f9fa'}
                                                            >
                                                                <div style={{ fontSize: '32px', marginBottom: '6px' }}>📁</div>
                                                                <div style={{ fontWeight: 600 }}>{year}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                            )}

                                            {/* Month folders */}
                                            {payslipNavLevel === 'months' && (
                                                isPayslipPeriodsLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '40px' }}><ActivityIndicator /></div>
                                                ) : payslipMonths.length === 0 ? (
                                                    <p style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: '30px' }}>No payslips found for {payslipNavYear}.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                        {payslipMonths.map(month => (
                                                            <div
                                                                key={month}
                                                                onClick={() => fetchPayslips(payslipNavYear, month, payslipFilterDriver)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    padding: '20px 28px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                                                    textAlign: 'center',
                                                                    minWidth: '100px',
                                                                    transition: 'background 0.15s',
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3a3a3a' : '#e9ecef'}
                                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f8f9fa'}
                                                            >
                                                                <div style={{ fontSize: '32px', marginBottom: '6px' }}>📁</div>
                                                                <div style={{ fontWeight: 600 }}>{MONTH_NAMES[month - 1]}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                            )}

                                            {/* Files (payslips table) */}
                                            {payslipNavLevel === 'files' && (
                                                <>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                                        <strong style={{ whiteSpace: 'nowrap' }}>Filter by driver:</strong>
                                                        <div style={{ position: 'relative', maxWidth: '300px', width: '100%' }}>
                                                            <input
                                                                type="text"
                                                                placeholder="Search driver..."
                                                                autoComplete="off"
                                                                value={payslipDriverSearch}
                                                                onChange={e => {
                                                                    setPayslipDriverSearch(e.target.value);
                                                                    setPayslipFilterDriver('');
                                                                    setPayslipDriverDropdownOpen(true);
                                                                }}
                                                                onFocus={() => setPayslipDriverDropdownOpen(true)}
                                                                onBlur={() => setTimeout(() => setPayslipDriverDropdownOpen(false), 150)}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '6px 12px',
                                                                    fontSize: '14px',
                                                                    border: `1px solid ${payslipFilterDriver ? '#0588f3' : (theme === 'dark' ? '#555' : '#ced4da')}`,
                                                                    borderRadius: '4px',
                                                                    outline: 'none',
                                                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#fff',
                                                                    color: theme === 'dark' ? '#e0e0e0' : '#212529',
                                                                }}
                                                            />
                                                            {payslipDriverDropdownOpen && (
                                                                <ul style={{
                                                                    position: 'absolute',
                                                                    top: '100%',
                                                                    left: 0,
                                                                    right: 0,
                                                                    maxHeight: 220,
                                                                    overflowY: 'auto',
                                                                    margin: 0,
                                                                    padding: 0,
                                                                    listStyle: 'none',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: 4,
                                                                    backgroundColor: theme === 'dark' ? '#1e2535' : '#fff',
                                                                    zIndex: 999,
                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                                }}>
                                                                    {(() => {
                                                                        const filtered = allUsers
                                                                            .filter(u => u.status === 'active' && (u.group === 'ADMIN' || u.group === 'DRIVER'))
                                                                            .filter(u => payslipDriverSearch
                                                                                ? `${u.name} ${u.surname}`.toLowerCase().includes(payslipDriverSearch.toLowerCase())
                                                                                : true
                                                                            );
                                                                        if (filtered.length === 0) {
                                                                            return <li style={{ padding: '8px 12px', color: '#999', fontSize: 13 }}>No matches</li>;
                                                                        }
                                                                        return (
                                                                            <>
                                                                                {!payslipDriverSearch && (
                                                                                    <li
                                                                                        onMouseDown={() => {
                                                                                            setPayslipFilterDriver('');
                                                                                            setPayslipDriverSearch('');
                                                                                            setPayslipDriverDropdownOpen(false);
                                                                                            fetchPayslips(payslipNavYear, payslipNavMonth, '');
                                                                                        }}
                                                                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(5,136,243,0.1)'}
                                                                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                                                                                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: theme === 'dark' ? '#aaa' : '#555', fontStyle: 'italic' }}
                                                                                    >
                                                                                        All Drivers
                                                                                    </li>
                                                                                )}
                                                                                {filtered.map(u => (
                                                                                    <li
                                                                                        key={u.id}
                                                                                        onMouseDown={() => {
                                                                                            setPayslipFilterDriver(u.id);
                                                                                            setPayslipDriverSearch(`${u.name} ${u.surname}`);
                                                                                            setPayslipDriverDropdownOpen(false);
                                                                                            fetchPayslips(payslipNavYear, payslipNavMonth, u.id);
                                                                                        }}
                                                                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(5,136,243,0.1)'}
                                                                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = u.id === payslipFilterDriver ? 'rgba(5,136,243,0.15)' : ''}
                                                                                        style={{
                                                                                            padding: '8px 12px',
                                                                                            cursor: 'pointer',
                                                                                            fontSize: 13,
                                                                                            backgroundColor: u.id === payslipFilterDriver ? 'rgba(5,136,243,0.15)' : undefined,
                                                                                        }}
                                                                                    >
                                                                                        {u.name} {u.surname}
                                                                                    </li>
                                                                                ))}
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </ul>
                                                            )}
                                                        </div>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => fetchPayslips(payslipNavYear, payslipNavMonth, payslipFilterDriver)}
                                                            disabled={isPayslipsLoading}
                                                        >
                                                            Refresh
                                                        </Button>
                                                    </div>

                                                    {isPayslipsLoading ? (
                                                        <div style={{ textAlign: 'center', padding: '30px' }}>
                                                            <ActivityIndicator />
                                                        </div>
                                                    ) : (
                                                        <div style={{ width: '100%', overflowX: 'auto' }}>
                                                            <table style={{
                                                                width: '100%',
                                                                borderCollapse: 'collapse',
                                                                tableLayout: 'fixed',
                                                                color: theme === 'dark' ? '#e0e0e0' : '#212529',
                                                            }}>
                                                                <colgroup>
                                                                    <col style={{ width: '22%' }} />
                                                                    <col style={{ width: '14%' }} />
                                                                    <col style={{ width: '34%' }} />
                                                                    <col style={{ width: '14%' }} />
                                                                    <col style={{ width: '16%' }} />
                                                                </colgroup>
                                                                <thead>
                                                                    <tr style={{ borderBottom: `2px solid ${theme === 'dark' ? '#444' : '#dee2e6'}`, backgroundColor: theme === 'dark' ? '#1e1e1e' : '#f1f3f5' }}>
                                                                        <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'left' }}>Driver</th>
                                                                        <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'left' }}>Pay Date</th>
                                                                        <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'left' }}>Display Name</th>
                                                                        <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'left' }}>Uploaded</th>
                                                                        <th style={{ padding: '10px 12px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {payslips.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan={5} style={{ textAlign: 'center', fontStyle: 'italic', color: theme === 'dark' ? '#888' : '#999', padding: '30px' }}>
                                                                                No payslips found
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        payslips.map((p, idx) => (
                                                                            <tr key={p.id} style={{
                                                                                borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#dee2e6'}`,
                                                                                backgroundColor: idx % 2 === 0
                                                                                    ? (theme === 'dark' ? '#1a1a1a' : '#ffffff')
                                                                                    : (theme === 'dark' ? '#222222' : '#f8f9fa'),
                                                                            }}>
                                                                                <td style={{ padding: '10px 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                    {p.profiles ? `${p.profiles.name} ${p.profiles.surname}` : '—'}
                                                                                </td>
                                                                                <td style={{ padding: '10px 12px', fontSize: '13px', color: theme === 'dark' ? '#aaa' : '#555' }}>
                                                                                    {p.pay_date ? new Date(p.pay_date + 'T00:00:00').toLocaleDateString('en-GB') : '—'}
                                                                                </td>
                                                                                <td style={{ padding: '10px 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                    {p.display_name}
                                                                                </td>
                                                                                <td style={{ padding: '10px 12px', fontSize: '13px', color: theme === 'dark' ? '#aaa' : '#555' }}>
                                                                                    {new Date(p.created_at).toLocaleDateString('en-GB')}
                                                                                </td>
                                                                                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                                    {(isDev || p.profile_id === profile.id) && (
                                                                                        <Button
                                                                                            variant="outline-primary"
                                                                                            size="sm"
                                                                                            style={{ marginRight: '6px' }}
                                                                                            onClick={() => handleViewPayslip(p.id, p.display_name)}
                                                                                        >
                                                                                            View
                                                                                        </Button>
                                                                                    )}
                                                                                    <Button
                                                                                        variant="outline-danger"
                                                                                        size="sm"
                                                                                        onClick={() => handleDeletePayslip(p.id)}
                                                                                    >
                                                                                        Delete
                                                                                    </Button>
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* ============================================================ */}
                                {/* Drivers Handbook Tab                                          */}
                                {/* ============================================================ */}
                                {activeTab === 'drivers-handbook' && (isDev || permissions.perm_notice_board_edit) && (
                                    <div style={{ width: '75%', margin: '0 auto' }}>
                                        <DriversHandbookTab
                                            theme={theme}
                                            isDev={isDev}
                                            isManager={isManager}
                                            handbookDraft={handbookDraft}
                                            handbookUpdatedAt={handbookUpdatedAt}
                                            handbookHasChanges={handbookHasChanges}
                                            isHandbookLoading={isHandbookLoading}
                                            isHandbookSaving={isHandbookSaving}
                                            onDraftChange={handleHandbookDraftChange}
                                            onSave={handleSaveHandbook}
                                            onInsertMarkdown={insertHandbookMarkdown}
                                        />
                                    </div>
                                )}

                                {/* Training Tab */}
                                {activeTab === 'training' && features.training !== false && (isDev || permissions.perm_training_view) && (
                                    <TrainingTab
                                        theme={theme}
                                        showAlert={showAlert}
                                        isDev={isDev}
                                        canEdit={!!(isDev || permissions.perm_training_edit)}
                                    />
                                )}

                            </div>

                        </>
                    )}
                </Col>

            </Row>

            {payslipPasswordOpen && (
                <RequestPassword
                    theme={theme}
                    isOpen={payslipPasswordOpen}
                    setIsOpen={setPayslipPasswordOpen}
                    returnData={handlePayslipPasswordSubmit}
                />
            )}
        </div>
    );
};

// ===========================================================================
// Markdown renderer — lightweight, no external deps
// ===========================================================================
const _escapeHtml = (str) =>
    str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

const _processInline = (text) =>
    text
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_\n]+)__/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
        .replace(/_([^_\n]+)_/g, '<em>$1</em>')
        .replace(/`([^`\n]+)`/g, '<code style="background:#f0f0f0;color:#c7254e;padding:1px 5px;border-radius:3px;font-size:11px;">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#1997eb;text-decoration:underline;">$1</a>');

const parseMarkdown = (md) => {
    if (!md) return '';
    const lines = md.split('\n');
    let html = '';
    let inCodeBlock = false;
    let codeContent = '';
    let inList = false;
    let listType = '';

    const closeList = () => {
        if (inList) { html += `</${listType}>`;  inList = false; listType = ''; }
    };

    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];

        // Code blocks
        if (raw.startsWith('```')) {
            if (!inCodeBlock) {
                closeList();
                inCodeBlock = true;
                codeContent = '';
            } else {
                inCodeBlock = false;
                html += `<pre style="background:#1e1e1e;color:#d4d4d4;padding:10px 12px;border-radius:6px;overflow-x:auto;font-size:11px;margin:8px 0;line-height:1.5;"><code>${_escapeHtml(codeContent)}</code></pre>`;
            }
            continue;
        }
        if (inCodeBlock) { codeContent += (codeContent ? '\n' : '') + raw; continue; }

        const line = _escapeHtml(raw);

        // List items
        const ulMatch = raw.match(/^[-*] (.+)$/);
        const olMatch = raw.match(/^\d+\. (.+)$/);
        if (ulMatch) {
            if (!inList || listType !== 'ul') { closeList(); html += '<ul style="padding-left:20px;margin:4px 0;">'; inList = true; listType = 'ul'; }
            html += `<li style="margin:2px 0;font-size:13px;line-height:1.5;">${_processInline(_escapeHtml(ulMatch[1]))}</li>`;
            continue;
        }
        if (olMatch) {
            if (!inList || listType !== 'ol') { closeList(); html += '<ol style="padding-left:20px;margin:4px 0;">'; inList = true; listType = 'ol'; }
            html += `<li style="margin:2px 0;font-size:13px;line-height:1.5;">${_processInline(_escapeHtml(olMatch[1]))}</li>`;
            continue;
        }
        closeList();

        // Headers
        if (raw.startsWith('#### ')) { html += `<h4 style="font-size:13px;font-weight:700;margin:10px 0 3px;color:#333;">${_processInline(line.slice(5))}</h4>`; }
        else if (raw.startsWith('### ')) { html += `<h3 style="font-size:14px;font-weight:700;margin:12px 0 4px;color:#333;">${_processInline(line.slice(4))}</h3>`; }
        else if (raw.startsWith('## '))  { html += `<h2 style="font-size:16px;font-weight:700;margin:14px 0 5px;color:#222;">${_processInline(line.slice(3))}</h2>`; }
        else if (raw.startsWith('# '))   { html += `<h1 style="font-size:18px;font-weight:700;margin:16px 0 6px;padding-bottom:5px;border-bottom:2px solid #1997eb;color:#1997eb;">${_processInline(line.slice(2))}</h1>`; }
        else if (raw.trim() === '---')   { html += '<hr style="border:none;border-top:1px solid #ddd;margin:10px 0;"/>'; }
        else if (raw.startsWith('> '))   { html += `<blockquote style="border-left:3px solid #1997eb;margin:6px 0;padding:5px 10px;background:#f0f7ff;color:#444;font-style:italic;font-size:12px;">${_processInline(_escapeHtml(raw.slice(2)))}</blockquote>`; }
        else if (raw.trim() === '')      { html += '<div style="height:6px;"></div>'; }
        else                             { html += `<p style="margin:3px 0;font-size:13px;line-height:1.5;">${_processInline(line)}</p>`; }
    }
    closeList();
    return html;
};

// ===========================================================================
// Drivers Handbook Tab Component
// ===========================================================================
const DriversHandbookTab = ({
    theme, handbookDraft, handbookUpdatedAt,
    handbookHasChanges, isHandbookLoading, isHandbookSaving,
    onDraftChange, onSave, onInsertMarkdown
}) => {
    const textareaRef = useRef(null);
    const isDark = theme === 'dark';

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        const el = textareaRef.current;
        if (!el) return;
        const pos = el.selectionStart;
        const value = handbookDraft;
        const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
        const currentLine = value.substring(lineStart, pos);
        const bulletMatch = currentLine.match(/^([-*]) /);
        const orderedMatch = currentLine.match(/^(\d+)\. /);
        if (bulletMatch) {
            e.preventDefault();
            if (currentLine === `${bulletMatch[1]} `) {
                // empty list item — exit list
                const newVal = value.substring(0, lineStart) + '\n' + value.substring(pos);
                onDraftChange(newVal);
                setTimeout(() => el.setSelectionRange(lineStart + 1, lineStart + 1), 0);
            } else {
                const insert = `\n${bulletMatch[1]} `;
                const newVal = value.substring(0, pos) + insert + value.substring(pos);
                onDraftChange(newVal);
                setTimeout(() => el.setSelectionRange(pos + insert.length, pos + insert.length), 0);
            }
            return;
        }
        if (orderedMatch) {
            e.preventDefault();
            const num = parseInt(orderedMatch[1], 10);
            const prefix = `${orderedMatch[1]}. `;
            if (currentLine === prefix) {
                // empty list item — exit list
                const newVal = value.substring(0, lineStart) + '\n' + value.substring(pos);
                onDraftChange(newVal);
                setTimeout(() => el.setSelectionRange(lineStart + 1, lineStart + 1), 0);
            } else {
                const insert = `\n${num + 1}. `;
                const newVal = value.substring(0, pos) + insert + value.substring(pos);
                onDraftChange(newVal);
                setTimeout(() => el.setSelectionRange(pos + insert.length, pos + insert.length), 0);
            }
            return;
        }
    };

    const toolbarBtns = [
        { title: 'Heading 1',     icon: 'H1',  before: '# ',   after: '' },
        { title: 'Heading 2',     icon: 'H2',  before: '## ',  after: '' },
        { title: 'Heading 3',     icon: 'H3',  before: '### ', after: '' },
        { title: 'Bold',          icon: 'B',   before: '**',   after: '**' },
        { title: 'Italic',        icon: 'I',   before: '*',    after: '*' },
        { title: 'Divider',       icon: '—',   before: '\n---\n', after: '' },
        { title: 'Bullet list',   icon: '•—',  before: '- ',   after: '' },
        { title: 'Numbered list', icon: '1.',  before: '1. ',  after: '' },
        { title: 'Blockquote',    icon: '❝',   before: '> ',   after: '' },
        { title: 'Inline code',   icon: '</>',  before: '`',    after: '`' },
        { title: 'Code block',    icon: '```',  before: '```\n', after: '\n```' },
    ];

    const formattedDate = handbookUpdatedAt
        ? new Date(handbookUpdatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : null;

    const phoneBg = '#f2f2f7';
    const phoneText = '#1c1c1e';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)', minHeight: '400px' }}>
            {/* Header */}
            <div style={{ marginBottom: '10px', padding: '0 0 0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                <div>
                    <h3 style={{ marginBottom: '2px' }}>Drivers Handbook</h3>
                    <p style={{ color: '#888', fontSize: '12px', margin: 0 }}>
                        Write in Markdown — the phone preview updates live.
                        {formattedDate && <span style={{ marginLeft: '12px', color: '#aaa' }}>Last saved: {formattedDate}</span>}
                    </p>
                </div>
            </div>

            {isHandbookLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <div className="spinner-border text-primary" role="status" />
                    <p style={{ marginTop: '12px', color: '#666' }}>Loading handbook…</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Toolbar */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '4px',
                        padding: '7px 20px',
                        background: isDark ? '#2a2a2a' : '#f8f9fa',
                        borderTop: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
                        borderBottom: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
                        flexShrink: 0,
                    }}>
                        {toolbarBtns.map((btn) => (
                            <button
                                key={btn.title}
                                title={btn.title}
                                onClick={() => onInsertMarkdown(textareaRef, btn.before, btn.after)}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    border: `1px solid ${isDark ? '#555' : '#ccc'}`,
                                    borderRadius: '4px',
                                    background: isDark ? '#3a3a3a' : '#fff',
                                    color: isDark ? '#eee' : '#333',
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                    lineHeight: '1.4',
                                }}
                            >
                                {btn.icon}
                            </button>
                        ))}
                        <div style={{ flex: 1 }} />
                        {handbookHasChanges && (
                            <span style={{ fontSize: '12px', color: '#e67e22', alignSelf: 'center', marginRight: '8px' }}>
                                ● Unsaved changes
                            </span>
                        )}
                        <button
                            onClick={onSave}
                            disabled={isHandbookSaving || !handbookHasChanges}
                            style={{
                                padding: '4px 18px',
                                fontSize: '13px',
                                fontWeight: '600',
                                border: 'none',
                                borderRadius: '4px',
                                background: handbookHasChanges ? '#1997eb' : '#aaa',
                                color: '#fff',
                                cursor: handbookHasChanges ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {isHandbookSaving ? 'Saving…' : 'Save Handbook'}
                        </button>
                    </div>

                    {/* Editor + Preview */}
                    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                        {/* Markdown Editor */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <textarea
                                ref={textareaRef}
                                value={handbookDraft}
                                onChange={(e) => onDraftChange(e.target.value)}
                                spellCheck={true}
                                onKeyDown={handleKeyDown}
                                style={{
                                    flex: 1,
                                    width: '100%',
                                    padding: '14px',
                                    fontFamily: '"Fira Code", "Courier New", monospace',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    resize: 'none',
                                    border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
                                    borderTop: 'none',
                                    borderRight: 'none',
                                    borderLeft: 'none',
                                    borderRadius: '0',
                                    background: isDark ? '#1a1a1a' : '#fff',
                                    color: isDark ? '#e0e0e0' : '#212529',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    overflowY: 'auto',
                                }}
                                placeholder={'# Drivers Handbook\n\nStart writing your handbook here…\n\n## Section 1\n\nYour content…'}
                            />
                        </div>

                        {/* Phone Preview Panel */}
                        <div style={{
                            flex: '0 0 456px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '14px 20px 14px 16px',
                            background: isDark ? '#252525' : '#dde1e7',
                            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
                            borderTop: 'none',
                            borderRight: 'none',
                            borderBottom: 'none',
                            borderRadius: '0',
                            overflowY: 'auto',
                        }}>
                            <p style={{ fontSize: '11px', color: isDark ? '#888' : '#666', margin: '0 0 12px', textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: '600' }}>
                                📱 Mobile Preview
                            </p>

                            {/* Phone frame */}
                            <div style={{
                                width: '384px',
                                background: '#1c1c1e',
                                borderRadius: '50px',
                                padding: '17px 10px',
                                boxShadow: isDark
                                    ? '0 0 0 2px #777, 0 0 0 5px #3a3a3a, 0 24px 64px rgba(0,0,0,0.9)'
                                    : '0 0 0 3px #1c1c1e, 0 0 0 5px #444, 0 24px 48px rgba(0,0,0,0.45)',
                                flexShrink: 0,
                            }}>
                                {/* Side buttons (decorative) */}
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '-13px', top: '72px', width: '5px', height: '34px', background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
                                    <div style={{ position: 'absolute', left: '-13px', top: '115px', width: '5px', height: '53px', background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
                                    <div style={{ position: 'absolute', left: '-13px', top: '178px', width: '5px', height: '53px', background: '#3a3a3a', borderRadius: '2px 0 0 2px' }} />
                                    <div style={{ position: 'absolute', right: '-13px', top: '120px', width: '5px', height: '72px', background: '#3a3a3a', borderRadius: '0 2px 2px 0' }} />
                                </div>

                                {/* Dynamic island */}
                                <div style={{
                                    width: '115px', height: '31px',
                                    background: '#000',
                                    borderRadius: '17px',
                                    margin: '0 auto 7px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                                }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#1a1a1a' }} />
                                    <div style={{ width: '36px', height: '12px', borderRadius: '7px', background: '#1a1a1a' }} />
                                </div>

                                {/* Status bar */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 19px 6px', fontSize: '11px', color: '#ccc', fontFamily: 'sans-serif', fontWeight: '600' }}>
                                    <span>9:41</span>
                                    <span style={{ letterSpacing: '1px' }}>▲ WiFi 🔋</span>
                                </div>

                                {/* Screen — grows with content; panel scrolls to reveal it */}
                                <div style={{
                                    background: phoneBg,
                                    borderRadius: '34px',
                                    overflow: 'hidden',
                                    height: '700px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid #111',
                                }}>
                                    {/* App bar — sticky at top */}
                                    <div style={{
                                        background: '#fff',
                                        borderBottom: '1px solid #e0e0e0',
                                        padding: '12px 17px 10px',
                                        flexShrink: 0,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                    }}>
                                        <div style={{ fontSize: '17px', fontWeight: '700', color: phoneText }}>Drivers Handbook</div>
                                        <div style={{ fontSize: '11px', color: '#8e8e93' }}>Company Policy &amp; Guidance</div>
                                    </div>

                                    {/* Content — full height, no inner scroll */}
                                    <scrollView
                                        style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            fontSize: '14px',
                                            padding: '0 16px',
                                            color: phoneText,
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                            lineHeight: '1.5',
                                        }}
                                        dangerouslySetInnerHTML={{ __html: handbookDraft ? parseMarkdown(handbookDraft) : '<p style="color:#8e8e93;font-style:italic;font-size:14px;">Start typing to see a preview…</p>' }}
                                    />

                                    {/* Bottom home indicator */}
                                    <div style={{ padding: '8px 0 6px', display: 'flex', justifyContent: 'center', flexShrink: 0, background: phoneBg }}>
                                        <div style={{ width: '132px', height: '5px', borderRadius: '3px', background: '#8e8e93' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DevPage;

// ===========================================================================
// Training Tab Component
// ===========================================================================
const STATUS_ICON = { passed: '🟢', valid: '🟢', renewal_required: '🟡', not_started: '⚫', expired: '🔴', not_required: '—' };
const STATUS_LABEL = { passed: 'Passed', valid: 'Passed', renewal_required: 'Renewal Required', not_started: 'Not Started', expired: 'Expired', not_required: 'Not Required' };
const EXPIRY_LABEL = { 6: '6 months', 12: 'Annual', 24: '2 years', 36: '3 years', 60: '5 years' };

const TrainingTab = ({ theme, showAlert, isDev, canEdit }) => {
    const isDark = theme === 'dark';
    const card = { border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, borderRadius: 8, padding: '20px', marginBottom: 16, backgroundColor: isDark ? '#1e1e1e' : '#fff' };

    // Sub-tab: modules | compliance | roles
    const [subTab, setSubTab] = useState('modules');

    // Modules
    const [modules, setModules] = useState([]);
    const [modulesLoading, setModulesLoading] = useState(false);
    const [expandedModule, setExpandedModule] = useState(null);
    const [moduleQuestions, setModuleQuestions] = useState({});
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [showModuleForm, setShowModuleForm] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [moduleForm, setModuleForm] = useState({ title: '', description: '', category_id: '', pass_mark: 80, duration_minutes: '' });
    const [moduleSaving, setModuleSaving] = useState(false);
    const [videoUploading, setVideoUploading] = useState({});  // keyed by module id
    const videoInputRef = useRef(null);
    const [videoUploadTarget, setVideoUploadTarget] = useState(null);
    const [videoUrlInputs, setVideoUrlInputs] = useState({});   // keyed by module id
    const [videoUrlSaving, setVideoUrlSaving] = useState({});   // keyed by module id
    const [videoMode, setVideoMode] = useState({});             // keyed by module id: 'video-upload'|'video-url'|'doc-upload'|'doc-url'
    const [docUploading, setDocUploading] = useState({});       // keyed by module id
    const [docUrlInputs, setDocUrlInputs] = useState({});       // keyed by module id
    const [docUrlSaving, setDocUrlSaving] = useState({});       // keyed by module id

    // Categories
    const [categories, setCategories] = useState([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showCategoryManager, setShowCategoryManager] = useState(false);

    // Questions
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [questionForm, setQuestionForm] = useState({ question: '', answers: [{ answer: '', is_correct: false }, { answer: '', is_correct: false }] });
    const [questionSaving, setQuestionSaving] = useState(false);

    // Compliance
    const [compliance, setCompliance] = useState(null);
    const [complianceLoading, setComplianceLoading] = useState(false);
    const [historyUser, setHistoryUser] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Roles
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [roleRequirementsOpen, setRoleRequirementsOpen] = useState(null);

    // User assignments
    const [assignableUsers, setAssignableUsers] = useState([]);
    const [assignableUsersLoading, setAssignableUsersLoading] = useState(false);
    const [expandedAssignUser, setExpandedAssignUser] = useState(null);

    // ── Data fetching ─────────────────────────────────────────────
    const loadModules = useCallback(async () => {
        setModulesLoading(true);
        const res = await getTrainingModules();
        setModulesLoading(false);
        if (res.modules) setModules(res.modules);
        else showAlert({ type: 'danger', message: res.error || 'Failed to load modules' });
    }, [showAlert]);

    const loadCategories = useCallback(async () => {
        const res = await getTrainingCategories();
        if (res.categories) setCategories(res.categories);
    }, []);

    const loadQuestionsForModule = useCallback(async (moduleId) => {
        setQuestionsLoading(true);
        const res = await getModuleQuestions(moduleId);
        setQuestionsLoading(false);
        if (res.questions) setModuleQuestions(prev => ({ ...prev, [moduleId]: res.questions }));
    }, []);

    const loadCompliance = useCallback(async () => {
        setComplianceLoading(true);
        const res = await getTrainingCompliance();
        setComplianceLoading(false);
        if (res.modules) setCompliance(res);
        else showAlert({ type: 'danger', message: res.error || 'Failed to load compliance data' });
    }, [showAlert]);

    const loadRoles = useCallback(async () => {
        setRolesLoading(true);
        const res = await getTrainingRoles();
        setRolesLoading(false);
        if (res.roles) setRoles(res.roles);
    }, []);

    const loadAssignableUsers = useCallback(async () => {
        setAssignableUsersLoading(true);
        const res = await getAssignableUsers();
        setAssignableUsersLoading(false);
        if (res.users) setAssignableUsers(res.users);
    }, []);

    const loadUserHistory = useCallback(async (userId, userName) => {
        setHistoryUser(userName);
        setHistoryLoading(true);
        const res = await getUserTrainingHistory(userId);
        setHistoryLoading(false);
        if (res.records) setHistoryData(res);
    }, []);

    useEffect(() => {
        if (subTab === 'modules') { loadModules(); loadCategories(); }
        if (subTab === 'compliance') loadCompliance();
        if (subTab === 'roles') { loadRoles(); loadModules(); loadCategories(); loadAssignableUsers(); }
    }, [subTab, loadModules, loadCompliance, loadRoles, loadCategories, loadAssignableUsers]);

    // ── Module form ───────────────────────────────────────────────
    const openCreateModule = () => { setEditingModule(null); setModuleForm({ title: '', description: '', category_id: '', pass_mark: 80, duration_minutes: '', expiry_months: '' }); setShowModuleForm(true); };
    const openEditModule = (m) => { setEditingModule(m); setModuleForm({ title: m.title, description: m.description || '', category_id: m.category_id || '', pass_mark: m.pass_mark, duration_minutes: m.duration_minutes || '', expiry_months: m.expiry_months || '', video_url: m.video_url || '' }); setShowModuleForm(true); };

    const handleSaveModule = async () => {
        if (!moduleForm.title.trim()) return showAlert({ type: 'warning', message: 'Title is required' });
        setModuleSaving(true);
        const res = editingModule
            ? await updateTrainingModule(editingModule.id, moduleForm)
            : await createTrainingModule(moduleForm);
        setModuleSaving(false);
        if (res.success || res.module) { setShowModuleForm(false); loadModules(); showAlert({ type: 'success', message: editingModule ? 'Module updated' : 'Module created' }); }
        else showAlert({ type: 'danger', message: res.error || 'Save failed' });
    };

    const handleVideoUpload = async (moduleId, file) => {
        const allowed = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
        if (!allowed.includes(file.type)) return showAlert({ type: 'warning', message: 'Only MP4, MOV, WebM or AVI files are allowed' });
        if (file.size > 500 * 1024 * 1024) return showAlert({ type: 'warning', message: 'File must be under 500 MB' });
        setVideoUploading(p => ({ ...p, [moduleId]: true }));
        const res = await uploadTrainingVideo(moduleId, file);
        setVideoUploading(p => ({ ...p, [moduleId]: false }));
        if (res.path) { loadModules(); showAlert({ type: 'success', message: 'Video uploaded successfully' }); }
        else showAlert({ type: 'danger', message: res.error || 'Upload failed' });
    };

    const handleDocUpload = async (moduleId, file) => {
        if (!file.name.toLowerCase().endsWith('.pdf')) return showAlert({ type: 'warning', message: 'Only PDF files are allowed' });
        if (file.size > 50 * 1024 * 1024) return showAlert({ type: 'warning', message: 'File must be under 50 MB' });
        setDocUploading(p => ({ ...p, [moduleId]: true }));
        const res = await uploadTrainingDocument(moduleId, file);
        setDocUploading(p => ({ ...p, [moduleId]: false }));
        if (res.path) { loadModules(); showAlert({ type: 'success', message: 'Document uploaded successfully' }); }
        else showAlert({ type: 'danger', message: res.error || 'Upload failed' });
    };

    const handleDeleteModule = async (id) => {
        if (!window.confirm('Deactivate this module? Employees will no longer be assigned to it.')) return;
        const res = await deleteTrainingModule(id);
        if (res.success || res.message) { loadModules(); showAlert({ type: 'success', message: 'Module deactivated' }); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleBumpVersion = async (id, currentVersion) => {
        if (!window.confirm(`Bump to version ${currentVersion + 1}? All current completions will be marked "Renewal Required".`)) return;
        const res = await bumpModuleVersion(id);
        if (res.success || res.version) { loadModules(); showAlert({ type: 'success', message: res.message || 'Version bumped' }); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const toggleModuleExpand = (moduleId) => {
        const m = modules.find(x => x.id === moduleId);
        if (expandedModule === moduleId) { setExpandedModule(null); return; }
        setExpandedModule(moduleId);
        if (!moduleQuestions[moduleId]) loadQuestionsForModule(moduleId);
        setShowQuestionForm(false);
        setEditingQuestion(null);
        // Initialise content panel state for this module
        const defaultMode = m?.video_url ? 'video-url' : m?.video_path ? 'video-upload' : m?.document_url ? 'doc-url' : m?.document_path ? 'doc-upload' : 'video-upload';
        setVideoMode(p => ({ ...p, [moduleId]: defaultMode }));
        setVideoUrlInputs(p => ({ ...p, [moduleId]: m?.video_url || '' }));
        setDocUrlInputs(p => ({ ...p, [moduleId]: m?.document_url || '' }));
    };

    const handleSaveVideoUrl = async (moduleId) => {
        const url = (videoUrlInputs[moduleId] || '').trim();
        setVideoUrlSaving(p => ({ ...p, [moduleId]: true }));
        const res = await updateTrainingModule(moduleId, { video_url: url });
        setVideoUrlSaving(p => ({ ...p, [moduleId]: false }));
        if (res.success || res.module) { loadModules(); showAlert({ type: 'success', message: url ? 'Video URL saved' : 'Video URL cleared' }); }
        else showAlert({ type: 'danger', message: res.error || 'Save failed' });
    };

    const handleSaveDocUrl = async (moduleId) => {
        const url = (docUrlInputs[moduleId] || '').trim();
        setDocUrlSaving(p => ({ ...p, [moduleId]: true }));
        const res = await updateTrainingModule(moduleId, { document_url: url });
        setDocUrlSaving(p => ({ ...p, [moduleId]: false }));
        if (res.success || res.module) { loadModules(); showAlert({ type: 'success', message: url ? 'Document URL saved' : 'Document URL cleared' }); }
        else showAlert({ type: 'danger', message: res.error || 'Save failed' });
    };

    // ── Question form ─────────────────────────────────────────────
    const openAddQuestion = () => { setEditingQuestion(null); setQuestionForm({ question: '', answers: [{ answer: '', is_correct: false }, { answer: '', is_correct: false }, { answer: '', is_correct: false }, { answer: '', is_correct: false }] }); setShowQuestionForm(true); };
    const openEditQuestion = (q) => { setEditingQuestion(q); setQuestionForm({ question: q.question, answers: q.answers.map(a => ({ ...a })) }); setShowQuestionForm(true); };

    const handleSaveQuestion = async () => {
        if (!questionForm.question.trim()) return showAlert({ type: 'warning', message: 'Question text is required' });
        const validAnswers = questionForm.answers.filter(a => a.answer.trim());
        if (validAnswers.length < 2) return showAlert({ type: 'warning', message: 'At least 2 answers are required' });
        if (!validAnswers.some(a => a.is_correct)) return showAlert({ type: 'warning', message: 'Mark at least one answer as correct' });
        setQuestionSaving(true);
        const payload = { question: questionForm.question.trim(), answers: validAnswers };
        const res = editingQuestion
            ? await updateQuestion(editingQuestion.id, payload)
            : await createQuestion(expandedModule, payload);
        setQuestionSaving(false);
        if (res.success || res.question_id || res.message) {
            setShowQuestionForm(false); setEditingQuestion(null);
            loadQuestionsForModule(expandedModule);
            showAlert({ type: 'success', message: editingQuestion ? 'Question updated' : 'Question added' });
        } else showAlert({ type: 'danger', message: res.error || 'Save failed' });
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Delete this question?')) return;
        const res = await deleteQuestion(questionId);
        if (res.success || res.message) loadQuestionsForModule(expandedModule);
        else showAlert({ type: 'danger', message: res.error });
    };

    const setAnswerField = (idx, field, val) => {
        setQuestionForm(prev => {
            const answers = prev.answers.map((a, i) => {
                if (i !== idx) return field === 'is_correct' ? { ...a, is_correct: false } : a;
                return { ...a, [field]: val };
            });
            return { ...prev, answers };
        });
    };

    // ── Roles ─────────────────────────────────────────────────────
    const handleCreateRole = async () => {
        if (!newRoleName.trim()) return;
        const res = await createTrainingRole(newRoleName.trim());
        if (res.role) { setNewRoleName(''); loadRoles(); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;
        const res = await createTrainingCategory(newCategoryName.trim());
        if (res.category) { setNewCategoryName(''); loadCategories(); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('Delete this category? Modules using it will become uncategorised.')) return;
        const res = await deleteTrainingCategory(id);
        if (res.success || res.message) { loadCategories(); loadModules(); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleDeleteRole = async (roleId) => {
        if (!window.confirm('Delete this training role?')) return;
        const res = await deleteTrainingRole(roleId);
        if (res.success || res.message) loadRoles();
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleSetRoleRequirements = async (roleId, moduleIds) => {
        const res = await setRoleRequirements(roleId, moduleIds);
        if (res.success || res.message) { loadRoles(); showAlert({ type: 'success', message: 'Requirements saved' }); }
        else showAlert({ type: 'danger', message: res.error });
    };

    const handleSetUserRoles = async (userId, roleIds) => {
        const res = await setUserTrainingRoles(userId, roleIds);
        if (res.success || res.message) {
            setAssignableUsers(prev => prev.map(u => u.id === userId ? { ...u, role_ids: roleIds } : u));
        } else showAlert({ type: 'danger', message: res.error });
    };

    // ── Compliance stats ──────────────────────────────────────────
    const complianceStats = compliance ? (() => {
        let total = 0, passed = 0, renewalRequired = 0, notStarted = 0;
        (compliance.users || []).forEach(u => {
            (compliance.modules || []).forEach(m => {
                const s = u.records[m.id]?.status || 'not_started';
                if (s === 'not_required') return;  // exclude from totals
                total++;
                if (s === 'passed' || s === 'valid') passed++;
                else if (s === 'renewal_required' || s === 'expired') renewalRequired++;
                else notStarted++;
            });
        });
        return { total, passed, renewalRequired, notStarted, pct: total > 0 ? Math.round((passed / total) * 100) : 0 };
    })() : null;

    // ── Render ────────────────────────────────────────────────────
    const subTabStyle = (key) => ({
        padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14,
        background: subTab === key ? '#1997eb' : (isDark ? '#333' : '#f0f0f0'),
        color: subTab === key ? '#fff' : (isDark ? '#ccc' : '#444'),
        marginRight: 8,
    });

    return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <h3>Training</h3>
                <p style={{ color: '#888', fontStyle: 'italic' }}>Create training modules, manage questions, and monitor employee compliance</p>
            </div>

            {/* Sub-tabs */}
            <div style={{ marginBottom: 24, display: 'flex', gap: 4 }}>
                <button style={subTabStyle('modules')} onClick={() => setSubTab('modules')}>📚 Modules</button>
                <button style={subTabStyle('compliance')} onClick={() => setSubTab('compliance')}>✅ Compliance</button>
                <button style={subTabStyle('roles')} onClick={() => setSubTab('roles')}>👥 Training Roles</button>
            </div>

            {/* ── MODULES ── */}
            {subTab === 'modules' && (
                <div>
                    {canEdit && (
                        <div style={{ marginBottom: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Button variant="primary" size="sm" onClick={openCreateModule}>+ Create Module</Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setShowCategoryManager(p => !p)}>
                                {showCategoryManager ? 'Hide' : '⚙ Manage Categories'}
                            </Button>
                        </div>
                    )}

                    {/* Category manager */}
                    {showCategoryManager && canEdit && (
                        <div style={{ ...card, border: '1px dashed #aaa', marginBottom: 24, padding: '16px 20px' }}>
                            <strong style={{ fontSize: 14 }}>Categories</strong>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' }}>
                                {categories.map(c => (
                                    <span key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: isDark ? '#333' : '#e8f0fe', borderRadius: 20, padding: '4px 12px', fontSize: 13 }}>
                                        {c.name}
                                        <button onClick={() => handleDeleteCategory(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                                    </span>
                                ))}
                                {categories.length === 0 && <span style={{ color: '#888', fontSize: 13 }}>No categories yet.</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Form.Control size="sm" style={{ maxWidth: 200 }} placeholder="New category name" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateCategory()} />
                                <Button variant="outline-success" size="sm" onClick={handleCreateCategory}>Add</Button>
                            </div>
                        </div>
                    )}

                    {/* Module create/edit form */}
                    {showModuleForm && (
                        <div style={{ ...card, border: '2px solid #1997eb', marginBottom: 24 }}>
                            <h5 style={{ marginBottom: 16 }}>{editingModule ? 'Edit Module' : 'New Training Module'}</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <Form.Label>Title *</Form.Label>
                                    <Form.Control value={moduleForm.title} onChange={e => setModuleForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Manual Handling" />
                                </div>
                                <div>
                                    <Form.Label>Category</Form.Label>
                                    <Form.Select value={moduleForm.category_id} onChange={e => setModuleForm(p => ({ ...p, category_id: e.target.value }))}>
                                        <option value="">— None —</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </Form.Select>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control as="textarea" rows={2} value={moduleForm.description} onChange={e => setModuleForm(p => ({ ...p, description: e.target.value }))} />
                                </div>
                                <div>
                                    <Form.Label>Pass Mark (%)</Form.Label>
                                    <Form.Control type="number" min={1} max={100} value={moduleForm.pass_mark} onChange={e => setModuleForm(p => ({ ...p, pass_mark: parseInt(e.target.value) || 80 }))} />
                                </div>
                                <div>
                                    <Form.Label>Duration (minutes)</Form.Label>
                                    <Form.Control type="number" min={1} value={moduleForm.duration_minutes} onChange={e => setModuleForm(p => ({ ...p, duration_minutes: e.target.value }))} placeholder="Optional" />
                                </div>
                                <div>
                                    <Form.Label>Renewal Required</Form.Label>
                                    <Form.Select value={moduleForm.expiry_months} onChange={e => setModuleForm(p => ({ ...p, expiry_months: e.target.value }))}>
                                        <option value="">No expiry (one-time)</option>
                                        <option value="6">Every 6 months</option>
                                        <option value="12">Annually (12 months)</option>
                                        <option value="24">Every 2 years</option>
                                        <option value="36">Every 3 years</option>
                                        <option value="60">Every 5 years</option>
                                    </Form.Select>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                <Button variant="success" size="sm" onClick={handleSaveModule} disabled={moduleSaving}>{moduleSaving ? 'Saving…' : (editingModule ? 'Save Changes' : 'Create Module')}</Button>
                                <Button variant="outline-secondary" size="sm" onClick={() => setShowModuleForm(false)}>Cancel</Button>
                            </div>
                        </div>
                    )}

                    {modulesLoading ? <p style={{ color: '#888' }}>Loading modules…</p> : modules.length === 0 ? (
                        <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>No modules yet. Create your first training module above.</p>
                    ) : modules.map(m => (
                        <div key={m.id} style={{ ...card, opacity: m.is_active ? 1 : 0.55 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                        <strong style={{ fontSize: 16 }}>{m.title}</strong>
                                        {m.category_name && <span style={{ fontSize: 11, background: isDark ? '#333' : '#e8f0fe', color: '#1997eb', borderRadius: 4, padding: '2px 8px' }}>{m.category_name}</span>}
                                        <span style={{ fontSize: 11, background: isDark ? '#333' : '#f0f0f0', borderRadius: 4, padding: '2px 8px' }}>v{m.version}</span>
                                        {!m.is_active && <span style={{ fontSize: 11, background: '#ff4d4f22', color: '#ff4d4f', borderRadius: 4, padding: '2px 8px' }}>Inactive</span>}
                                    </div>
                                    {m.description && <p style={{ fontSize: 13, color: '#888', margin: '0 0 6px' }}>{m.description}</p>}
                                    <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 16 }}>
                                        <span>Pass mark: <strong>{m.pass_mark}%</strong></span>
                                        {m.duration_minutes && <span>Duration: <strong>{m.duration_minutes} min</strong></span>}
                                        <span>Questions: <strong>{m.question_count}</strong></span>
                                        <span>Renewal: <strong>{m.expiry_months ? (EXPIRY_LABEL[m.expiry_months] || `${m.expiry_months} months`) : 'None'}</strong></span>
                                        <span>{m.video_url || m.video_path ? '🎬 Video set' : m.document_url || m.document_path ? '📄 Document set' : '⚠️ No content'}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 16 }}>
                                    <Button variant="outline-primary" size="sm" onClick={() => toggleModuleExpand(m.id)}>
                                        {expandedModule === m.id ? 'Close' : 'Questions'}
                                    </Button>
                                    {canEdit && <>
                                        <Button variant="outline-secondary" size="sm" onClick={() => openEditModule(m)}>Edit</Button>
                                        <Button variant="outline-warning" size="sm" onClick={() => handleBumpVersion(m.id, m.version)} title="Bump version — marks all completions as renewal required">v+</Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteModule(m.id)}>Deactivate</Button>
                                    </>}
                                </div>
                            </div>

                            {/* Questions panel */}
                            {expandedModule === m.id && (
                                <div style={{ marginTop: 20, borderTop: `1px solid ${isDark ? '#444' : '#eee'}`, paddingTop: 16 }}>

                                    {/* Content panel */}
                                    {canEdit && (() => {
                                        const mode = videoMode[m.id] || 'video-upload';
                                        const MODES = [
                                            { key: 'video-upload', label: '⬆ Video' },
                                            { key: 'video-url',    label: '🔗 Video URL' },
                                            { key: 'doc-upload',   label: '📄 PDF Upload' },
                                            { key: 'doc-url',      label: '🔗 PDF URL' },
                                        ];
                                        return (
                                            <div style={{ background: isDark ? '#1a1a1a' : '#f8f9fa', border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                                    <strong style={{ fontSize: 13 }}>📎 Module Content</strong>
                                                    <div style={{ display: 'flex', gap: 3 }}>
                                                        {MODES.map(({ key, label }) => (
                                                            <button key={key} onClick={() => setVideoMode(p => ({ ...p, [m.id]: key }))} style={{ padding: '3px 9px', fontSize: 11, borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer', background: mode === key ? '#1997eb' : (isDark ? '#333' : '#fff'), color: mode === key ? '#fff' : (isDark ? '#ccc' : '#444'), fontWeight: 600 }}>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {mode === 'video-upload' && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                        <div style={{ fontSize: 12, color: '#888' }}>{m.video_path && !m.video_url ? `Uploaded: ${m.video_path.split('/').pop()}` : 'MP4, MOV, WebM or AVI — max 500 MB'}</div>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            {videoUploading[m.id] && <span style={{ fontSize: 12, color: '#1997eb' }}>Uploading…</span>}
                                                            <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" style={{ display: 'none' }} id={`video-input-${m.id}`} onChange={e => { if (e.target.files[0]) handleVideoUpload(m.id, e.target.files[0]); e.target.value = ''; }} />
                                                            <Button variant={m.video_path && !m.video_url ? 'outline-warning' : 'outline-primary'} size="sm" disabled={!!videoUploading[m.id]} onClick={() => document.getElementById(`video-input-${m.id}`).click()}>
                                                                {m.video_path && !m.video_url ? 'Replace Video' : 'Upload Video'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {mode === 'video-url' && (
                                                    <div>
                                                        <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Paste a direct video URL (DVSA, YouTube, Vimeo, or any hosted mp4)</div>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <Form.Control size="sm" placeholder="https://…" value={videoUrlInputs[m.id] || ''} onChange={e => setVideoUrlInputs(p => ({ ...p, [m.id]: e.target.value }))} />
                                                            {(videoUrlInputs[m.id] || m.video_url) && <Button variant="outline-secondary" size="sm" onClick={() => window.open(videoUrlInputs[m.id] || m.video_url, '_blank')} title="Test this URL">Test ↗</Button>}
                                                            <Button variant="success" size="sm" disabled={!!videoUrlSaving[m.id]} onClick={() => handleSaveVideoUrl(m.id)}>{videoUrlSaving[m.id] ? 'Saving…' : 'Save'}</Button>
                                                            {(videoUrlInputs[m.id] || m.video_url) && <Button variant="outline-danger" size="sm" onClick={() => { setVideoUrlInputs(p => ({ ...p, [m.id]: '' })); updateTrainingModule(m.id, { video_url: '' }).then(loadModules); }}>✕</Button>}
                                                        </div>
                                                        {m.video_url && <div style={{ fontSize: 11, color: '#52c41a', marginTop: 6 }}>Current: <a href={m.video_url} target="_blank" rel="noreferrer" style={{ color: '#52c41a' }}>{m.video_url}</a></div>}
                                                    </div>
                                                )}

                                                {mode === 'doc-upload' && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                        <div style={{ fontSize: 12, color: '#888' }}>{m.document_path && !m.document_url ? `Uploaded: ${m.document_path.split('/').pop()}` : 'PDF only — max 50 MB'}</div>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            {docUploading[m.id] && <span style={{ fontSize: 12, color: '#1997eb' }}>Uploading…</span>}
                                                            <input type="file" accept="application/pdf" style={{ display: 'none' }} id={`doc-input-${m.id}`} onChange={e => { if (e.target.files[0]) handleDocUpload(m.id, e.target.files[0]); e.target.value = ''; }} />
                                                            <Button variant={m.document_path && !m.document_url ? 'outline-warning' : 'outline-primary'} size="sm" disabled={!!docUploading[m.id]} onClick={() => document.getElementById(`doc-input-${m.id}`).click()}>
                                                                {m.document_path && !m.document_url ? 'Replace PDF' : 'Upload PDF'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {mode === 'doc-url' && (
                                                    <div>
                                                        <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Paste a PDF URL (Gov.uk, DVSA, or any hosted PDF)</div>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            <Form.Control size="sm" placeholder="https://…" value={docUrlInputs[m.id] || ''} onChange={e => setDocUrlInputs(p => ({ ...p, [m.id]: e.target.value }))} />
                                                            {(docUrlInputs[m.id] || m.document_url) && <Button variant="outline-secondary" size="sm" onClick={() => window.open(docUrlInputs[m.id] || m.document_url, '_blank')} title="Test this URL">Test ↗</Button>}
                                                            <Button variant="success" size="sm" disabled={!!docUrlSaving[m.id]} onClick={() => handleSaveDocUrl(m.id)}>{docUrlSaving[m.id] ? 'Saving…' : 'Save'}</Button>
                                                            {(docUrlInputs[m.id] || m.document_url) && <Button variant="outline-danger" size="sm" onClick={() => { setDocUrlInputs(p => ({ ...p, [m.id]: '' })); updateTrainingModule(m.id, { document_url: '' }).then(loadModules); }}>✕</Button>}
                                                        </div>
                                                        {m.document_url && <div style={{ fontSize: 11, color: '#52c41a', marginTop: 6 }}>Current: <a href={m.document_url} target="_blank" rel="noreferrer" style={{ color: '#52c41a' }}>{m.document_url}</a></div>}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {questionsLoading ? <p style={{ color: '#888', fontSize: 13 }}>Loading questions…</p> : (
                                        <>
                                            {(moduleQuestions[m.id] || []).length === 0 && !showQuestionForm && (
                                                <p style={{ color: '#888', fontSize: 13 }}>No questions yet.</p>
                                            )}
                                            {(moduleQuestions[m.id] || []).map((q, qi) => (
                                                <div key={q.id} style={{ background: isDark ? '#252525' : '#f9f9f9', borderRadius: 6, padding: 12, marginBottom: 10 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Q{qi + 1}. {q.question}</div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                {(q.answers || []).map(a => (
                                                                    <div key={a.id} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ color: a.is_correct ? '#52c41a' : '#888', fontSize: 12 }}>{a.is_correct ? '✓' : '○'}</span>
                                                                        <span style={{ color: a.is_correct ? (isDark ? '#7fff7f' : '#237804') : undefined }}>{a.answer}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {canEdit && (
                                                            <div style={{ display: 'flex', gap: 6, marginLeft: 12 }}>
                                                                <Button variant="outline-secondary" size="sm" onClick={() => openEditQuestion(q)}>Edit</Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteQuestion(q.id)}>Delete</Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Question form */}
                                            {showQuestionForm && (
                                                <div style={{ background: isDark ? '#1a1a2e' : '#f0f8ff', borderRadius: 8, padding: 16, marginTop: 8, border: '1px solid #1997eb' }}>
                                                    <h6 style={{ marginBottom: 12 }}>{editingQuestion ? 'Edit Question' : 'Add Question'}</h6>
                                                    <Form.Control as="textarea" rows={2} placeholder="Question text" value={questionForm.question} onChange={e => setQuestionForm(p => ({ ...p, question: e.target.value }))} style={{ marginBottom: 12 }} />
                                                    <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>Answers (tick the correct one):</div>
                                                    {questionForm.answers.map((a, idx) => (
                                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                                            <Form.Check type="radio" name="correct_answer" checked={a.is_correct} onChange={() => setAnswerField(idx, 'is_correct', true)} title="Mark as correct" />
                                                            <Form.Control size="sm" placeholder={`Answer ${idx + 1}`} value={a.answer} onChange={e => setAnswerField(idx, 'answer', e.target.value)} />
                                                        </div>
                                                    ))}
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                                        <Button variant="success" size="sm" onClick={handleSaveQuestion} disabled={questionSaving}>{questionSaving ? 'Saving…' : (editingQuestion ? 'Save' : 'Add Question')}</Button>
                                                        <Button variant="outline-secondary" size="sm" onClick={() => { setShowQuestionForm(false); setEditingQuestion(null); }}>Cancel</Button>
                                                    </div>
                                                </div>
                                            )}

                                            {canEdit && !showQuestionForm && (
                                                <Button variant="outline-success" size="sm" style={{ marginTop: 8 }} onClick={openAddQuestion}>+ Add Question</Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ── COMPLIANCE ── */}
            {subTab === 'compliance' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h5 style={{ margin: 0 }}>Training Compliance Matrix</h5>
                        <Button variant="outline-secondary" size="sm" onClick={loadCompliance}>Refresh</Button>
                    </div>

                    {complianceLoading ? <p style={{ color: '#888' }}>Loading compliance data…</p> : !compliance ? null : (
                        <>
                            {/* Summary stats */}
                            {complianceStats && (
                                <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                                    {[
                                        { label: 'Overall', value: `${complianceStats.pct}%`, color: complianceStats.pct >= 80 ? '#52c41a' : complianceStats.pct >= 50 ? '#faad14' : '#ff4d4f' },
                                        { label: '🟢 Passed', value: complianceStats.passed, color: '#52c41a' },
                                        { label: '🟡 Renewal Due', value: complianceStats.renewalRequired, color: '#faad14' },
                                        { label: '⚫ Not Started', value: complianceStats.notStarted, color: '#888' },
                                    ].map(s => (
                                        <div key={s.label} style={{ ...card, padding: '12px 20px', minWidth: 120, textAlign: 'center', margin: 0 }}>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                                            <div style={{ fontSize: 12, color: '#888' }}>{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* User history modal */}
                            {historyUser && (
                                <div style={{ ...card, border: '2px solid #1997eb', marginBottom: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <h6 style={{ margin: 0 }}>Training History — {historyUser}</h6>
                                        <Button variant="outline-secondary" size="sm" onClick={() => { setHistoryUser(null); setHistoryData(null); }}>Close</Button>
                                    </div>
                                    {historyLoading ? <p style={{ color: '#888' }}>Loading…</p> : historyData && (
                                        <>
                                            <strong style={{ fontSize: 13 }}>Current Records</strong>
                                            {(historyData.records || []).length === 0 ? <p style={{ color: '#888', fontSize: 13 }}>No completions yet.</p> : (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, marginTop: 8 }}>
                                                    <thead><tr style={{ fontSize: 12, color: '#888', borderBottom: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Module</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Score</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Version</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Completed</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Status</th>
                                                    </tr></thead>
                                                    <tbody>{historyData.records.map(r => (
                                                        <tr key={r.id} style={{ fontSize: 13, borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}>
                                                            <td style={{ padding: '6px 8px' }}>{r.training_modules?.title || r.module_id}</td>
                                                            <td style={{ padding: '6px 8px' }}>{r.score}%</td>
                                                            <td style={{ padding: '6px 8px' }}>v{r.module_version}</td>
                                                            <td style={{ padding: '6px 8px' }}>{r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-GB') : '—'}</td>
                                                            <td style={{ padding: '6px 8px' }}>{STATUS_ICON[r.status]} {STATUS_LABEL[r.status] || r.status}</td>
                                                        </tr>
                                                    ))}</tbody>
                                                </table>
                                            )}
                                            <strong style={{ fontSize: 13 }}>Attempt History</strong>
                                            {(historyData.attempts || []).length === 0 ? <p style={{ color: '#888', fontSize: 13 }}>No attempts yet.</p> : (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                                                    <thead><tr style={{ fontSize: 12, color: '#888', borderBottom: `1px solid ${isDark ? '#444' : '#eee'}` }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Module</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Attempt</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Score</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Result</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Date</th>
                                                    </tr></thead>
                                                    <tbody>{historyData.attempts.map(a => (
                                                        <tr key={a.id} style={{ fontSize: 13, borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}>
                                                            <td style={{ padding: '6px 8px' }}>{a.training_modules?.title || a.module_id}</td>
                                                            <td style={{ padding: '6px 8px' }}>#{a.attempt_number}</td>
                                                            <td style={{ padding: '6px 8px' }}>{a.score !== null ? `${a.score}%` : '—'}</td>
                                                            <td style={{ padding: '6px 8px' }}>{a.passed ? '✅ Passed' : '❌ Failed'}</td>
                                                            <td style={{ padding: '6px 8px' }}>{a.completed_at ? new Date(a.completed_at).toLocaleDateString('en-GB') : '—'}</td>
                                                        </tr>
                                                    ))}</tbody>
                                                </table>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Matrix table */}
                            {(compliance.modules || []).length === 0 ? (
                                <p style={{ color: '#888', textAlign: 'center' }}>No active modules to display.</p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `5px solid ${isDark ? '#444' : '#ddd'}`, whiteSpace: 'nowrap', minWidth: 160 }}>Employee</th>
                                                {compliance.modules.map(m => (
                                                    <th key={m.id} style={{ padding: '8px 10px', textAlign: 'center', borderBottom: `5px solid ${isDark ? '#444' : '#ddd'}`, whiteSpace: 'normal', fontSize: 12, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }} title={m.title}>
                                                        {m.title}
                                                        <div style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>v{m.version}</div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {compliance.users.map(u => (
                                                <tr key={u.id} style={{ borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}>
                                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                                        <button onClick={() => loadUserHistory(u.id, u.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1997eb', fontWeight: 600, padding: 0, fontSize: 13 }}>
                                                            {u.name}
                                                        </button>
                                                        <div style={{ fontSize: 11, color: '#888' }}>{u.group}{!u.has_roles && <span style={{ color: '#faad14', marginLeft: 6 }}>⚠ no role</span>}</div>
                                                    </td>
                                                    {compliance.modules.map(m => {
                                                        const rec = u.records[m.id] || { status: 'not_started' };
                                                        const icon = STATUS_ICON[rec.status] || '⚫';
                                                        const label = rec.status === 'passed' ? `${rec.score}% — v${rec.module_version}` : STATUS_LABEL[rec.status] || rec.status;
                                                        if (rec.status === 'not_required') {
                                                            return <td key={m.id} style={{ padding: '8px 10px', textAlign: 'center', color: isDark ? '#444' : '#ccc', fontSize: 16 }}>—</td>;
                                                        }
                                                        return (
                                                            <td key={m.id} style={{ padding: '8px 10px', textAlign: 'center', verticalAlign: 'top' }} title={`${label}${rec.expiry_date ? ` · Expires ${new Date(rec.expiry_date).toLocaleDateString('en-GB')}` : ''}`}>
                                                                <span style={{ fontSize: 18 }}>{icon}</span>
                                                                {rec.status === 'passed' && <div style={{ fontSize: 10, color: '#888' }}>{rec.score}%{rec.expiry_date ? <><br />{new Date(rec.expiry_date).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}</> : ''}</div>}
                                                                {rec.status === 'expired' && <div style={{ fontSize: 10, color: '#ff4d4f' }}>Expired</div>}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── ROLES ── */}
            {subTab === 'roles' && (
                <div>
                    <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>
                        Training roles determine which modules are automatically required for each employee. Assign modules to a role, then assign employees to roles.
                    </p>

                    {canEdit && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                            <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="New role name (e.g. Driver)" value={newRoleName} onChange={e => setNewRoleName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateRole()} />
                            <Button variant="outline-success" size="sm" onClick={handleCreateRole}>+ Add Role</Button>
                        </div>
                    )}

                    {rolesLoading ? <p style={{ color: '#888' }}>Loading roles…</p> : roles.length === 0 ? (
                        <p style={{ color: '#888' }}>No training roles defined yet.</p>
                    ) : roles.map(role => {
                        const reqIds = (role.role_training_requirements || []).map(r => r.module_id);
                        const isOpen = roleRequirementsOpen === role.id;
                        return (
                            <div key={role.id} style={card}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <strong>{role.name}</strong>
                                        <span style={{ fontSize: 12, color: '#888', marginLeft: 12 }}>{reqIds.length} module{reqIds.length !== 1 ? 's' : ''} required</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {canEdit && <Button variant="outline-primary" size="sm" onClick={() => setRoleRequirementsOpen(isOpen ? null : role.id)}>
                                            {isOpen ? 'Done' : 'Set Modules'}
                                        </Button>}
                                        {canEdit && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRole(role.id)}>Delete</Button>}
                                    </div>
                                </div>

                                {isOpen && (
                                    <div style={{ marginTop: 16, borderTop: `1px solid ${isDark ? '#444' : '#eee'}`, paddingTop: 16 }}>
                                        <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Select which modules are required for the <strong>{role.name}</strong> role:</p>
                                        {modules.filter(m => m.is_active).map(m => {
                                            const checked = reqIds.includes(m.id);
                                            const newIds = checked ? reqIds.filter(id => id !== m.id) : [...reqIds, m.id];
                                            return (
                                                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                    <Form.Check type="checkbox" id={`role-${role.id}-mod-${m.id}`} checked={checked} onChange={() => handleSetRoleRequirements(role.id, newIds)} label={<span style={{ fontSize: 13 }}>{m.title} {m.category_name ? <span style={{ color: '#888', fontSize: 11 }}>({m.category_name})</span> : null}</span>} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* User Assignments */}
                    <div style={{ marginTop: 32 }}>
                        <h5 style={{ marginBottom: 4 }}>👤 User Assignments</h5>
                        <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Assign training roles to individual employees. Only active Drivers, Admins and Warehouse staff are shown.</p>

                        {assignableUsersLoading ? <p style={{ color: '#888' }}>Loading users…</p> : assignableUsers.length === 0 ? (
                            <p style={{ color: '#888' }}>No active users found.</p>
                        ) : assignableUsers.map(u => {
                            const isOpen = expandedAssignUser === u.id;
                            const assignedRoles = roles.filter(r => u.role_ids.includes(r.id));
                            return (
                                <div key={u.id} style={{ ...card, marginBottom: 8 }}>
                                    <div onClick={() => setExpandedAssignUser(isOpen ? null : u.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: canEdit ? 'pointer' : 'default' }}>
                                        <div>
                                            <span style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</span>
                                            <span style={{ fontSize: 11, background: isDark ? '#333' : '#f0f0f0', borderRadius: 4, padding: '2px 7px', marginLeft: 10 }}>{u.group}</span>
                                            {assignedRoles.length === 0
                                                ? <span style={{ fontSize: 12, color: '#faad14', marginLeft: 12 }}>⚠ No training role assigned</span>
                                                : assignedRoles.map(r => <span key={r.id} style={{ fontSize: 11, background: isDark ? '#1a3a1a' : '#f0fff0', color: '#52c41a', border: '1px solid #52c41a44', borderRadius: 4, padding: '2px 7px', marginLeft: 6 }}>{r.name}</span>)
                                            }
                                        </div>
                                        {canEdit && <span style={{ fontSize: 12, color: '#888' }}>{isOpen ? 'Close ▲' : 'Edit ▼'}</span>}
                                    </div>

                                    {isOpen && canEdit && (
                                        <div style={{ marginTop: 14, borderTop: `1px solid ${isDark ? '#444' : '#eee'}`, paddingTop: 14 }}>
                                            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Tick the training roles that apply to <strong>{u.name}</strong>:</p>
                                            {roles.map(r => {
                                                const checked = u.role_ids.includes(r.id);
                                                const newIds = checked ? u.role_ids.filter(id => id !== r.id) : [...u.role_ids, r.id];
                                                const reqCount = (r.role_training_requirements || []).length;
                                                return (
                                                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                        <Form.Check type="checkbox" id={`user-${u.id}-role-${r.id}`} checked={checked} onChange={() => handleSetUserRoles(u.id, newIds)} label={<span style={{ fontSize: 13 }}>{r.name} <span style={{ color: '#888', fontSize: 11 }}>({reqCount} module{reqCount !== 1 ? 's' : ''})</span></span>} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
